import { useState, useEffect, useCallback, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

// ── SUPABASE CLIENT ───────────────────────────────────────────────────────────
const SUPABASE_URL = "https://phbcavffcgixrwjciclx.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBoYmNhdmZmY2dpeHJ3amNpY2x4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzUzOTg2NDUsImV4cCI6MjA5MDk3NDY0NX0.JqS5V2o7i54Xb-SIJZg929iZ01KbVfwdZmCXPwXuIyc";
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ── SUPABASE DATA HELPERS ─────────────────────────────────────────────────────
// Map DB snake_case row → app camelCase object
function dbToMed(row) {
  return {
    id: row.id,
    name: row.name,
    dose: row.dose || "",
    color: row.color || "#2dd4bf",
    category: row.category || "",
    refillCount: row.refill_count ?? 30,
    refillTotal: row.refill_total ?? 30,
    missedRule: row.missed_rule || "take_if_window",
    schedType: row.sched_type || "fixed",
    fixedTimes: row.fixed_times || ["08:00"],
    timesPerDay: row.times_per_day ?? 1,
    timesStartHour: row.times_start_hour ?? 8,
    timesGapHours: row.times_gap_hours || null,
    intervalHours: row.interval_hours ?? 8,
    intervalFirstTime: row.interval_first_time || "08:00",
    intervalMaxPerDay: row.interval_max_per_day ?? 3,
    prnMaxPerDay: row.prn_max_per_day ?? 4,
    prnMinGapHours: row.prn_min_gap_hours ?? 4,
    cyclicOnDays: row.cyclic_on_days ?? 21,
    cyclicOffDays: row.cyclic_off_days ?? 7,
    cyclicTime: row.cyclic_time || "08:00",
    cyclicStart: row.cyclic_start || null,
    epicImported: row.epic_imported || false,
    epicResourceId: row.epic_resource_id || null,
    pharmacy: row.pharmacy || "",
    rxNumber: row.rx_number || "",
  };
}

// Map app camelCase object → DB snake_case for upsert
function medToDb(med, userId) {
  return {
    id: typeof med.id === "string" ? med.id : undefined,
    user_id: userId,
    name: med.name,
    dose: med.dose || "",
    color: med.color || "#2dd4bf",
    category: med.category || "",
    refill_count: med.refillCount ?? 30,
    refill_total: med.refillTotal ?? 30,
    missed_rule: med.missedRule || "take_if_window",
    sched_type: med.schedType || "fixed",
    fixed_times: med.fixedTimes || ["08:00"],
    times_per_day: med.timesPerDay ?? 1,
    times_start_hour: med.timesStartHour ?? 8,
    times_gap_hours: med.timesGapHours || null,
    interval_hours: med.intervalHours ?? 8,
    interval_first_time: med.intervalFirstTime || "08:00",
    interval_max_per_day: med.intervalMaxPerDay ?? 3,
    prn_max_per_day: med.prnMaxPerDay ?? 4,
    prn_min_gap_hours: med.prnMinGapHours ?? 4,
    cyclic_on_days: med.cyclicOnDays ?? 21,
    cyclic_off_days: med.cyclicOffDays ?? 7,
    cyclic_time: med.cyclicTime || "08:00",
    cyclic_start: med.cyclicStart || null,
    epic_imported: med.epicImported || false,
    epic_resource_id: med.epicResourceId || null,
    pharmacy: med.pharmacy || null,
    rx_number: med.rxNumber || null,
    is_active: true,
  };
}

const GOOGLE_FONT = `@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');`;

const styles = `
  ${GOOGLE_FONT}
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg:#0d1117; --surface:#161b22; --surface2:#1c2333; --border:#30363d;
    --teal:#2dd4bf; --teal-dim:rgba(45,212,191,0.10); --teal-glow:rgba(45,212,191,0.28);
    --coral:#fb7185; --amber:#fbbf24; --green:#4ade80; --blue:#60a5fa; --purple:#c084fc;
    --text:#e6edf3; --text-muted:#8b949e; --text-dim:#484f58;
    --radius:14px; --radius-sm:8px;
  }
  html,body{background:var(--bg);color:var(--text);font-family:'DM Sans',sans-serif;}
  .app{min-height:100vh;display:flex;flex-direction:column;}
  .topnav{display:flex;align-items:center;justify-content:space-between;padding:14px 24px;border-bottom:1px solid var(--border);background:var(--surface);position:sticky;top:0;z-index:200;}
  .logo{font-family:'DM Serif Display',serif;font-size:21px;color:var(--teal);letter-spacing:-.5px;}
  .logo-sub{font-family:'DM Sans',sans-serif;font-size:12px;color:var(--text-muted);font-weight:400;margin-left:8px;}
  .nav-actions{display:flex;gap:8px;align-items:center;}
  .avatar{width:34px;height:34px;border-radius:50%;background:var(--teal-dim);border:2px solid var(--teal);display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:var(--teal);cursor:pointer;}
  .icon-btn{background:var(--surface2);border:1px solid var(--border);color:var(--text-muted);border-radius:8px;padding:7px 10px;cursor:pointer;font-size:15px;position:relative;}
  .notif-dot{position:absolute;top:5px;right:5px;width:7px;height:7px;background:var(--coral);border-radius:50%;}
  .main-layout{display:flex;flex:1;}
  .sidebar{width:216px;background:var(--surface);border-right:1px solid var(--border);padding:16px 10px;display:flex;flex-direction:column;gap:2px;position:sticky;top:57px;height:calc(100vh - 57px);overflow-y:auto;}
  .nav-item{display:flex;align-items:center;gap:10px;padding:9px 12px;border-radius:var(--radius-sm);cursor:pointer;font-size:13.5px;color:var(--text-muted);transition:all .14s;border:1px solid transparent;font-weight:500;}
  .nav-item:hover{background:var(--surface2);color:var(--text);}
  .nav-item.active{background:var(--teal-dim);color:var(--teal);border-color:rgba(45,212,191,.18);}
  .nav-icon{font-size:16px;width:20px;text-align:center;}
  .nav-section{font-size:9.5px;text-transform:uppercase;letter-spacing:1.6px;color:var(--text-dim);padding:12px 12px 3px;}
  .content{flex:1;padding:26px 28px;overflow-y:auto;}
  .card{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:18px 20px;margin-bottom:14px;}
  .card-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:14px;}
  .card-title{font-family:'DM Serif Display',serif;font-size:17px;}
  .card-sub{font-size:12px;color:var(--text-muted);margin-top:2px;}
  .stat-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(148px,1fr));gap:10px;margin-bottom:16px;}
  .stat-card{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:14px 16px;position:relative;overflow:hidden;}
  .stat-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;}
  .stat-card.teal::before{background:var(--teal)}.stat-card.coral::before{background:var(--coral)}.stat-card.amber::before{background:var(--amber)}.stat-card.green::before{background:var(--green)}
  .stat-num{font-family:'DM Serif Display',serif;font-size:30px;line-height:1;}
  .stat-label{font-size:11px;color:var(--text-muted);margin-top:3px;}
  .stat-icon{position:absolute;top:14px;right:14px;font-size:20px;opacity:.35;}
  .btn{display:inline-flex;align-items:center;gap:6px;padding:8px 15px;border-radius:var(--radius-sm);font-size:13px;font-weight:500;cursor:pointer;border:none;transition:all .14s;font-family:'DM Sans',sans-serif;}
  .btn-primary{background:var(--teal);color:#0d1117;}
  .btn-primary:hover{background:#5eead4;transform:translateY(-1px);box-shadow:0 4px 14px var(--teal-glow);}
  .btn-primary:disabled{opacity:.45;cursor:not-allowed;transform:none;box-shadow:none;}
  .btn-secondary{background:var(--surface2);color:var(--text);border:1px solid var(--border);}
  .btn-secondary:hover{border-color:var(--teal);color:var(--teal);}
  .btn-danger{background:rgba(251,113,133,.12);color:var(--coral);border:1px solid rgba(251,113,133,.28);}
  .btn-danger:hover{background:rgba(251,113,133,.22);}
  .btn-warning{background:rgba(251,191,36,.12);color:var(--amber);border:1px solid rgba(251,191,36,.28);}
  .btn-epic{background:linear-gradient(135deg,#c084fc,#818cf8);color:#fff;border:none;}
  .btn-epic:hover{opacity:.88;transform:translateY(-1px);box-shadow:0 4px 14px rgba(192,132,252,.35);}
  .btn-sm{padding:5px 11px;font-size:12px;}
  .btn-xs{padding:3px 8px;font-size:11px;}
  .stype-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:4px;}
  .stype-card{border:1.5px solid var(--border);border-radius:var(--radius-sm);padding:11px;cursor:pointer;transition:all .14s;}
  .stype-card:hover{border-color:var(--teal);background:var(--teal-dim);}
  .stype-card.selected{border-color:var(--teal);background:var(--teal-dim);}
  .stype-icon{font-size:18px;margin-bottom:5px;}
  .stype-title{font-size:12.5px;font-weight:700;margin-bottom:2px;}
  .stype-desc{font-size:11px;color:var(--text-muted);line-height:1.5;}
  .dose-row{display:flex;align-items:center;gap:12px;padding:11px 14px;background:var(--surface2);border:1px solid var(--border);border-radius:var(--radius-sm);margin-bottom:7px;transition:all .14s;}
  .dose-row.clickable{cursor:pointer;}
  .dose-row.clickable:hover{border-color:var(--teal);}
  .dose-row.taken{opacity:.45;}
  .dose-row.missed-row{border-color:rgba(251,113,133,.4);background:rgba(251,113,133,.05);}
  .dose-row.shifted-row{border-color:rgba(251,191,36,.4);background:rgba(251,191,36,.05);}
  .dose-time-col{min-width:62px;flex-shrink:0;}
  .dose-time{font-size:13px;font-weight:700;color:var(--teal);}
  .dose-time.t-missed{color:var(--coral);}
  .dose-time.t-shifted{color:var(--amber);}
  .dose-time-sub{font-size:10px;color:var(--text-dim);margin-top:1px;}
  .dose-name{flex:1;}
  .dose-med-name{font-size:14px;font-weight:600;}
  .dose-med-sub{font-size:11px;color:var(--text-muted);margin-top:2px;}
  .dose-actions{display:flex;gap:5px;flex-shrink:0;align-items:center;}
  .check-circle{width:26px;height:26px;border-radius:50%;border:2px solid var(--border);display:flex;align-items:center;justify-content:center;font-size:12px;flex-shrink:0;transition:all .14s;cursor:pointer;}
  .check-circle:hover{border-color:var(--green);}
  .check-circle.done{background:var(--green);border-color:var(--green);color:#0d1117;}
  .check-circle.t-missed{border-color:var(--coral);color:var(--coral);}
  .reschedule-banner{background:rgba(251,191,36,.1);border:1px solid rgba(251,191,36,.3);border-radius:var(--radius-sm);padding:10px 14px;margin-bottom:10px;font-size:12.5px;line-height:1.65;}
  .med-list{display:flex;flex-direction:column;gap:9px;}
  .med-card{background:var(--surface2);border:1px solid var(--border);border-radius:var(--radius);padding:14px 16px;display:flex;align-items:flex-start;gap:14px;transition:border-color .14s;}
  .med-card:hover{border-color:var(--teal);}
  .med-card.epic-imported{border-color:rgba(192,132,252,.35);background:rgba(192,132,252,.04);}
  .med-dot{width:12px;height:12px;border-radius:50%;flex-shrink:0;margin-top:4px;}
  .med-info{flex:1;}
  .med-name{font-weight:700;font-size:15px;}
  .med-meta{font-size:12px;color:var(--text-muted);margin-top:3px;}
  .med-sched{font-size:11.5px;color:var(--text-muted);margin-top:4px;}
  .med-tags{display:flex;gap:5px;margin-top:7px;flex-wrap:wrap;}
  .tag{padding:2px 8px;border-radius:20px;font-size:11px;font-weight:500;}
  .tag-teal{background:var(--teal-dim);color:var(--teal);}
  .tag-amber{background:rgba(251,191,36,.12);color:var(--amber);}
  .tag-coral{background:rgba(251,113,133,.12);color:var(--coral);}
  .tag-blue{background:rgba(96,165,250,.12);color:var(--blue);}
  .tag-green{background:rgba(74,222,128,.12);color:var(--green);}
  .tag-purple{background:rgba(192,132,252,.12);color:var(--purple);}
  .form-group{margin-bottom:13px;}
  .form-label{font-size:11.5px;font-weight:600;color:var(--text-muted);margin-bottom:5px;display:block;letter-spacing:.3px;}
  .form-input{width:100%;background:var(--surface2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:9px 12px;color:var(--text);font-size:13.5px;font-family:'DM Sans',sans-serif;outline:none;transition:border-color .14s;}
  .form-input:focus{border-color:var(--teal);}
  .form-row{display:grid;grid-template-columns:1fr 1fr;gap:11px;}
  .form-hint{font-size:11px;color:var(--text-dim);margin-top:4px;line-height:1.5;}
  .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.72);z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px;}
  .modal{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:22px 24px;width:100%;max-width:520px;max-height:90vh;overflow-y:auto;}
  .modal-title{font-family:'DM Serif Display',serif;font-size:19px;margin-bottom:16px;}
  .modal-section{font-size:10px;text-transform:uppercase;letter-spacing:1.4px;color:var(--text-dim);margin:16px 0 8px;padding-top:4px;border-top:1px solid var(--border);}
  .modal-footer{display:flex;justify-content:flex-end;gap:8px;margin-top:18px;padding-top:14px;border-top:1px solid var(--border);}
  .missed-options{display:flex;flex-direction:column;gap:8px;}
  .missed-option{border:1.5px solid var(--border);border-radius:var(--radius-sm);padding:12px 14px;cursor:pointer;transition:all .14s;}
  .missed-option:hover{border-color:var(--teal);background:var(--teal-dim);}
  .missed-option.selected{border-color:var(--teal);background:var(--teal-dim);}
  .missed-option-title{font-size:13px;font-weight:600;margin-bottom:3px;}
  .missed-option-desc{font-size:11.5px;color:var(--text-muted);line-height:1.5;}
  .interaction-alert{background:rgba(251,191,36,.07);border:1px solid rgba(251,191,36,.28);border-radius:var(--radius);padding:13px 15px;margin-bottom:10px;display:flex;gap:11px;}
  .interaction-icon{font-size:19px;flex-shrink:0;}
  .interaction-text{font-size:13px;line-height:1.6;}
  .interaction-drugs{font-weight:700;color:var(--amber);margin-bottom:3px;}
  .symptom-entry{background:var(--surface2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:11px 13px;margin-bottom:7px;display:flex;justify-content:space-between;align-items:flex-start;}
  .severity-bar{display:flex;gap:3px;margin-top:5px;}
  .refill-card{background:var(--surface2);border:1px solid var(--border);border-radius:var(--radius);padding:15px;margin-bottom:9px;}
  .refill-bar-bg{background:var(--border);border-radius:4px;height:6px;margin:9px 0 5px;overflow:hidden;}
  .refill-bar-fill{height:100%;border-radius:4px;transition:width .4s;}
  .caregiver-card{background:var(--surface2);border:1px solid var(--border);border-radius:var(--radius);padding:13px 15px;margin-bottom:9px;display:flex;align-items:center;gap:11px;}
  .ai-chat{display:flex;flex-direction:column;gap:9px;max-height:370px;overflow-y:auto;padding-bottom:4px;}
  .ai-bubble{padding:11px 14px;border-radius:var(--radius);font-size:13.5px;line-height:1.65;max-width:87%;}
  .ai-bubble.assistant{background:var(--teal-dim);border:1px solid rgba(45,212,191,.18);align-self:flex-start;}
  .ai-bubble.user{background:var(--surface2);border:1px solid var(--border);align-self:flex-end;}
  .ai-bubble.loading{color:var(--text-muted);font-style:italic;}
  .ai-input-row{display:flex;gap:8px;margin-top:11px;}
  .ai-input{flex:1;background:var(--surface2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:9px 13px;color:var(--text);font-size:13.5px;font-family:'DM Sans',sans-serif;outline:none;transition:border-color .14s;}
  .ai-input:focus{border-color:var(--teal);}
  .adh-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:4px;}
  .adh-cell{aspect-ratio:1;border-radius:3px;display:flex;align-items:center;justify-content:center;font-size:9px;}
  .adh-full{background:var(--teal);color:#0d1117;font-weight:700;}
  .adh-partial{background:var(--amber);color:#0d1117;}
  .adh-missed{background:rgba(251,113,133,.3);color:var(--coral);}
  .adh-empty{background:var(--surface2);}
  .badge{display:inline-flex;align-items:center;justify-content:center;min-width:17px;height:17px;border-radius:50%;font-size:10px;font-weight:700;background:var(--coral);color:white;padding:0 3px;margin-left:3px;}
  .toast{position:fixed;bottom:22px;right:22px;z-index:9999;background:var(--surface);border:1px solid var(--teal);border-radius:var(--radius);padding:11px 17px;font-size:13px;display:flex;align-items:center;gap:8px;box-shadow:0 8px 28px rgba(0,0,0,.45);animation:slideUp .28s ease;}
  @keyframes slideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
  .page-header{margin-bottom:22px;}
  .page-title{font-family:'DM Serif Display',serif;font-size:25px;}
  .page-desc{font-size:13px;color:var(--text-muted);margin-top:3px;}
  .empty-state{text-align:center;padding:38px;color:var(--text-muted);}
  .empty-icon{font-size:34px;margin-bottom:9px;}
  .empty-text{font-size:13.5px;}
  .color-swatches{display:flex;gap:7px;flex-wrap:wrap;}
  .swatch{width:26px;height:26px;border-radius:50%;cursor:pointer;border:2px solid transparent;transition:all .14s;}
  .swatch.selected{border-color:white;transform:scale(1.2);}
  .info-box{background:var(--surface2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:11px 13px;font-size:12px;color:var(--text-muted);line-height:1.65;margin-top:8px;}

  /* ── EPIC FHIR IMPORT STYLES ── */
  .epic-banner{background:linear-gradient(135deg,rgba(192,132,252,.12),rgba(129,140,248,.08));border:1px solid rgba(192,132,252,.28);border-radius:var(--radius);padding:18px 20px;margin-bottom:16px;position:relative;overflow:hidden;}
  .epic-banner::before{content:'';position:absolute;top:-30px;right:-30px;width:100px;height:100px;background:radial-gradient(circle,rgba(192,132,252,.18),transparent 70%);border-radius:50%;}
  .epic-banner-title{font-family:'DM Serif Display',serif;font-size:16px;color:var(--purple);margin-bottom:4px;display:flex;align-items:center;gap:8px;}
  .epic-banner-desc{font-size:12.5px;color:var(--text-muted);line-height:1.6;margin-bottom:14px;}
  .epic-status-row{display:flex;align-items:center;gap:8px;font-size:12px;color:var(--text-muted);margin-bottom:12px;}
  .epic-status-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
  .epic-status-dot.connected{background:var(--green);box-shadow:0 0 6px rgba(74,222,128,.5);}
  .epic-status-dot.disconnected{background:var(--text-dim);}
  .epic-status-dot.loading{background:var(--amber);animation:pulse 1s infinite;}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
  .fhir-import-list{display:flex;flex-direction:column;gap:8px;margin-top:12px;}
  .fhir-med-row{background:var(--surface2);border:1px solid rgba(192,132,252,.2);border-radius:var(--radius-sm);padding:12px 14px;display:flex;align-items:center;gap:12px;transition:all .14s;}
  .fhir-med-row:hover{border-color:var(--purple);}
  .fhir-med-row.selected{border-color:var(--purple);background:rgba(192,132,252,.08);}
  .fhir-check{width:20px;height:20px;border-radius:4px;border:2px solid var(--border);display:flex;align-items:center;justify-content:center;font-size:11px;cursor:pointer;flex-shrink:0;transition:all .14s;}
  .fhir-check.checked{background:var(--purple);border-color:var(--purple);color:white;}
  .fhir-med-info{flex:1;}
  .fhir-med-name{font-size:13.5px;font-weight:600;}
  .fhir-med-detail{font-size:11px;color:var(--text-muted);margin-top:2px;}
  .fhir-source-badge{font-size:10px;padding:2px 7px;border-radius:20px;background:rgba(192,132,252,.15);color:var(--purple);border:1px solid rgba(192,132,252,.25);}
  .epic-step{display:flex;align-items:flex-start;gap:12px;padding:12px 0;border-bottom:1px solid var(--border);}
  .epic-step:last-child{border-bottom:none;}
  .epic-step-num{width:24px;height:24px;border-radius:50%;background:rgba(192,132,252,.15);color:var(--purple);font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px;}
  .epic-step-text{font-size:13px;color:var(--text-muted);line-height:1.55;}
  .epic-step-title{font-size:13px;font-weight:600;color:var(--text);margin-bottom:2px;}
  .spinner{display:inline-block;width:16px;height:16px;border:2px solid rgba(192,132,252,.3);border-top-color:var(--purple);border-radius:50%;animation:spin .7s linear infinite;}
  @keyframes spin{to{transform:rotate(360deg)}}

  /* ── ONBOARDING ── */
  .onboard{min-height:100vh;background:var(--bg);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:32px 24px;text-align:center;}
  .onboard-logo{font-family:'DM Serif Display',serif;font-size:32px;color:var(--teal);letter-spacing:-.5px;margin-bottom:6px;}
  .onboard-step-dots{display:flex;gap:8px;margin-bottom:36px;}
  .onboard-dot{width:8px;height:8px;border-radius:50%;background:var(--border);transition:all .2s;}
  .onboard-dot.active{background:var(--teal);width:24px;border-radius:4px;}
  .onboard-icon{font-size:56px;margin-bottom:20px;line-height:1;}
  .onboard-title{font-family:'DM Serif Display',serif;font-size:26px;margin-bottom:12px;line-height:1.25;}
  .onboard-desc{font-size:15px;color:var(--text-muted);line-height:1.7;max-width:340px;margin:0 auto 32px;}
  .onboard-features{display:flex;flex-direction:column;gap:12px;margin-bottom:32px;width:100%;max-width:340px;}
  .onboard-feature{display:flex;align-items:center;gap:13px;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:13px 16px;text-align:left;}
  .onboard-feature-icon{font-size:22px;flex-shrink:0;}
  .onboard-feature-text{font-size:13.5px;line-height:1.45;}
  .onboard-feature-label{font-weight:600;color:var(--text);}
  .onboard-feature-sub{font-size:12px;color:var(--text-muted);margin-top:2px;}
  .onboard-actions{display:flex;flex-direction:column;gap:10px;width:100%;max-width:340px;}
  .onboard-skip{font-size:12px;color:var(--text-dim);cursor:pointer;margin-top:8px;padding:4px;}
  .onboard-skip:hover{color:var(--text-muted);}
  /* ── BOTTOM TAB BAR (mobile) ── */
  .bottom-nav{display:none;position:fixed;bottom:0;left:0;right:0;background:var(--surface);border-top:1px solid var(--border);z-index:200;padding-bottom:env(safe-area-inset-bottom,0px);}
  .bottom-nav-inner{display:flex;align-items:stretch;height:56px;}
  .tab-item{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;cursor:pointer;color:var(--text-muted);font-size:10px;font-weight:500;padding:6px 2px;border:none;background:none;position:relative;transition:color .14s;}
  .tab-item.active{color:var(--teal);}
  .tab-item-icon{font-size:18px;line-height:1;}
  .tab-badge{position:absolute;top:4px;right:calc(50% - 14px);min-width:15px;height:15px;border-radius:8px;background:var(--coral);color:#fff;font-size:9px;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0 3px;}
  .tab-more-menu{position:fixed;bottom:58px;left:0;right:0;background:var(--surface);border-top:1px solid var(--border);padding:12px 16px;display:flex;flex-direction:column;gap:4px;z-index:300;animation:slideUp .18s ease;}
  .tab-more-item{display:flex;align-items:center;gap:12px;padding:11px 14px;border-radius:var(--radius-sm);font-size:14px;color:var(--text);cursor:pointer;}
  .tab-more-item:hover,.tab-more-item.active{background:var(--teal-dim);color:var(--teal);}
  .tab-more-backdrop{position:fixed;inset:0;z-index:290;}
  /* ── NEXT-UP BANNER ── */
  .next-up-banner{background:linear-gradient(135deg,rgba(45,212,191,.12),rgba(45,212,191,.06));border:1px solid rgba(45,212,191,.28);border-radius:var(--radius);padding:14px 18px;margin-bottom:14px;display:flex;align-items:center;gap:14px;}
  .next-up-dot{width:44px;height:44px;border-radius:50%;background:var(--teal-dim);border:2px solid var(--teal);display:flex;align-items:center;justify-content:center;font-size:20px;flex-shrink:0;}
  .next-up-info{flex:1;}
  .next-up-label{font-size:11px;font-weight:600;color:var(--teal);text-transform:uppercase;letter-spacing:.8px;margin-bottom:3px;}
  .next-up-name{font-size:16px;font-weight:700;color:var(--text);}
  .next-up-time{font-size:12px;color:var(--text-muted);margin-top:2px;}
  /* ── ADD MED STEPS ── */
  .step-indicator{display:flex;align-items:center;gap:6px;margin-bottom:18px;}
  .step-dot{width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;flex-shrink:0;transition:all .18s;}
  .step-dot.done{background:var(--teal);color:#0d1117;}
  .step-dot.active{background:var(--teal-dim);color:var(--teal);border:2px solid var(--teal);}
  .step-dot.pending{background:var(--surface2);color:var(--text-dim);border:1px solid var(--border);}
  .step-line{flex:1;height:1px;background:var(--border);}
  .step-line.done{background:var(--teal);}
  .advanced-toggle{display:flex;align-items:center;gap:8px;padding:10px 13px;border-radius:var(--radius-sm);border:1px solid var(--border);background:var(--surface2);cursor:pointer;font-size:13px;color:var(--text-muted);margin-top:8px;transition:all .14s;}
  .advanced-toggle:hover{border-color:var(--teal);color:var(--teal);}
  .advanced-chevron{transition:transform .18s;}
  .advanced-chevron.open{transform:rotate(180deg);}
  @media(max-width:768px){
    .sidebar{display:none;}
    .bottom-nav{display:block;}
    .content{padding:14px;padding-bottom:72px;}
    .form-row{grid-template-columns:1fr;}
    .stype-grid{grid-template-columns:1fr;}
    .stat-grid{grid-template-columns:1fr 1fr;}
    .next-up-banner{flex-direction:column;text-align:center;}
  }
`;

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const COLORS = ["#2dd4bf","#fb7185","#fbbf24","#60a5fa","#4ade80","#c084fc","#f97316","#e879f9"];
function getTodayStr() { return new Date().toISOString().split("T")[0]; }
const todayStr = getTodayStr(); // kept for backward-compat with non-dashboard uses

// ── EPIC FHIR CONFIG ──────────────────────────────────────────────────────────
const EPIC_CONFIG = {
  clientId: "34097e36-afc0-43a6-92a8-9789c70c04d4",
  fhirBaseUrl: "https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4",
  authUrl: "https://fhir.epic.com/interconnect-fhir-oauth/oauth2/authorize",
  tokenUrl: "https://fhir.epic.com/interconnect-fhir-oauth/oauth2/token",
  redirectUri: window.location.origin + window.location.pathname,
  scopes: "openid fhirUser patient/MedicationRequest.read patient/Patient.read patient/Condition.read patient/AllergyIntolerance.read offline_access",
};

// ── PKCE HELPERS ──────────────────────────────────────────────────────────────
function generateCodeVerifier() {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return btoa(String.fromCharCode(...arr)).replace(/\+/g,"-").replace(/\//g,"_").replace(/=/g,"");
}

async function generateCodeChallenge(verifier) {
  const enc = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", enc);
  return btoa(String.fromCharCode(...new Uint8Array(hash))).replace(/\+/g,"-").replace(/\//g,"_").replace(/=/g,"");
}

function generateState() {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return btoa(String.fromCharCode(...arr)).replace(/[^a-zA-Z0-9]/g,"").slice(0,16);
}

// ── EPIC AUTH FLOW ────────────────────────────────────────────────────────────
async function launchEpicAuth() {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateState();
  sessionStorage.setItem("epic_verifier", verifier);
  sessionStorage.setItem("epic_state", state);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: EPIC_CONFIG.clientId,
    redirect_uri: EPIC_CONFIG.redirectUri,
    scope: EPIC_CONFIG.scopes,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
    aud: EPIC_CONFIG.fhirBaseUrl,
  });

  window.location.href = `${EPIC_CONFIG.authUrl}?${params.toString()}`;
}

async function exchangeCodeForToken(code) {
  const verifier = sessionStorage.getItem("epic_verifier");
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: EPIC_CONFIG.redirectUri,
    client_id: EPIC_CONFIG.clientId,
    code_verifier: verifier,
  });

  const res = await fetch(EPIC_CONFIG.tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) throw new Error(`Token exchange failed: ${res.status}`);
  return res.json();
}

async function fetchMedicationRequests(accessToken, patientId) {
  const url = `${EPIC_CONFIG.fhirBaseUrl}/MedicationRequest?patient=${patientId}&status=active&_count=50`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/fhir+json" },
  });
  if (!res.ok) throw new Error(`FHIR fetch failed: ${res.status}`);
  return res.json();
}

// ── PARSE FHIR MedicationRequest → Veriva med format ─────────────────────────
function parseFhirMedication(entry) {
  const r = entry.resource;
  const name = r.medicationCodeableConcept?.text
    || r.medicationCodeableConcept?.coding?.[0]?.display
    || r.medicationReference?.display
    || "Unknown Medication";

  const dosageInstr = r.dosageInstruction?.[0];
  const dose = dosageInstr?.doseAndRate?.[0]?.doseQuantity
    ? `${dosageInstr.doseAndRate[0].doseQuantity.value}${dosageInstr.doseAndRate[0].doseQuantity.unit || ""}`
    : dosageInstr?.text || "";

  const freq = dosageInstr?.timing?.repeat;
  let schedType = "fixed";
  let fixedTimes = ["08:00"];
  let timesPerDay = 1;
  let intervalHours = 8;

  if (freq) {
    const period = freq.period;
    const periodUnit = freq.periodUnit;
    const freqCount = freq.frequency || 1;

    if (periodUnit === "d" && period === 1) {
      if (freqCount === 1) { schedType = "fixed"; fixedTimes = ["08:00"]; }
      else if (freqCount === 2) { schedType = "fixed"; fixedTimes = ["08:00","20:00"]; }
      else if (freqCount === 3) { schedType = "fixed"; fixedTimes = ["08:00","14:00","20:00"]; }
      else { schedType = "times"; timesPerDay = freqCount; }
    } else if (periodUnit === "h") {
      schedType = "interval"; intervalHours = period || 8;
    } else {
      schedType = "times"; timesPerDay = freqCount;
    }
  }

  const category = r.category?.[0]?.text || r.category?.[0]?.coding?.[0]?.display || "Imported";
  const color = COLORS[Math.floor(Math.random() * COLORS.length)];

  return {
    id: Date.now() + Math.random(),
    name: name.split(" ").map(w=>w.charAt(0).toUpperCase()+w.slice(1).toLowerCase()).join(" "),
    dose,
    color,
    category,
    refillCount: 30,
    refillTotal: 30,
    missedRule: "take_if_window",
    schedType,
    fixedTimes,
    timesPerDay,
    timesStartHour: 8,
    intervalHours,
    intervalFirstTime: "08:00",
    intervalMaxPerDay: Math.floor(24 / (intervalHours || 8)),
    prnMaxPerDay: 4,
    prnMinGapHours: 4,
    epicImported: true,
    epicResourceId: r.id,
  };
}

const SCHED_TYPES = [
  { id:"fixed",    icon:"🕐", title:"Fixed Times",     desc:"Set exact clock times — e.g. 8am, 2pm, 9pm" },
  { id:"times",    icon:"📅", title:"Times Per Day",   desc:"Auto-space N doses evenly across waking hours" },
  { id:"interval", icon:"⏱️", title:"Every N Hours",   desc:"Strict gap from last actual dose — shifts when late" },
  { id:"prn",      icon:"💡", title:"As Needed (PRN)", desc:"On-demand with max daily limit & min gap" },
  { id:"cyclic",   icon:"🔄", title:"Cyclic / On-Off", desc:"X days on, Y days off — auto-restarts" },
];

// ── LIVE DRUG INTERACTION ENGINE ──────────────────────────────────────────────
const _rxcuiCache = {};
const _interactionCache = {}; // Note: cache resets on page reload

async function getRxCUI(drugName) {
  const key = drugName.toLowerCase().trim();
  if (_rxcuiCache[key] !== undefined) return _rxcuiCache[key];
  try {
    const r = await fetch(`https://rxnav.nlm.nih.gov/REST/rxcui.json?name=${encodeURIComponent(key)}&search=1`);
    const d = await r.json();
    const raw = d?.idGroup?.rxnormId?.[0] || null;
    const rxcui = raw ? raw.toString().trim().split(/\s+/)[0] : null;
    _rxcuiCache[key] = rxcui;
    return rxcui;
  } catch { _rxcuiCache[key] = null; return null; }
}

// ── KNOWN DRUG INTERACTION TABLE ─────────────────────────────────────────────
const KNOWN_INTERACTIONS = [
  // Opioids + CNS depressants (high risk)
  { drugs:["oxycodone","alprazolam"],     severity:"high",     note:"Combining oxycodone and alprazolam (Xanax) can cause severe respiratory depression, profound sedation, coma, or death. Avoid concurrent use." },
  { drugs:["oxycodone","diazepam"],       severity:"high",     note:"Oxycodone and diazepam together significantly increase the risk of respiratory depression and sedation. Avoid combination." },
  { drugs:["oxycodone","clonazepam"],     severity:"high",     note:"Concurrent use of oxycodone and clonazepam can cause life-threatening respiratory depression. Avoid unless no alternatives exist." },
  { drugs:["oxycodone","gabapentin"],     severity:"moderate", note:"Oxycodone and gabapentin together can increase CNS depression, sedation, and risk of respiratory problems. Monitor closely." },
  { drugs:["oxycodone","pregabalin"],     severity:"moderate", note:"Combining oxycodone with pregabalin (Lyrica) may enhance CNS and respiratory depression. Use with caution and monitor breathing." },
  { drugs:["oxycodone","varenicline"],    severity:"mild",     note:"Varenicline (Chantix) may affect opioid efficacy. Discuss with your doctor if using both." },
  { drugs:["oxycodone","zolpidem"],       severity:"high",     note:"Oxycodone and zolpidem together can cause dangerous CNS and respiratory depression. Avoid concurrent use." },
  { drugs:["oxycodone","carisoprodol"],   severity:"high",     note:"Combining oxycodone with carisoprodol significantly increases sedation and respiratory depression risk." },
  { drugs:["hydrocodone","alprazolam"],   severity:"high",     note:"Hydrocodone and alprazolam together can cause life-threatening respiratory depression. Avoid combination." },
  { drugs:["hydrocodone","gabapentin"],   severity:"moderate", note:"Hydrocodone and gabapentin may increase CNS depression and sedation when used together. Monitor closely." },
  // Benzodiazepines + other CNS depressants
  { drugs:["alprazolam","gabapentin"],    severity:"moderate", note:"Alprazolam and gabapentin together can enhance CNS depression and sedation. Avoid alcohol and other depressants." },
  { drugs:["alprazolam","pregabalin"],    severity:"moderate", note:"Combining alprazolam with pregabalin (Lyrica) increases risk of sedation and CNS depression." },
  { drugs:["alprazolam","zolpidem"],      severity:"high",     note:"Alprazolam and zolpidem together greatly increase the risk of extreme sedation and respiratory depression." },
  { drugs:["diazepam","gabapentin"],      severity:"moderate", note:"Diazepam and gabapentin together increase CNS depression. Use with caution." },
  // SSRIs/SNRIs interactions
  { drugs:["sertraline","tramadol"],      severity:"high",     note:"Sertraline and tramadol together significantly increase the risk of serotonin syndrome, a potentially life-threatening condition." },
  { drugs:["fluoxetine","tramadol"],      severity:"high",     note:"Fluoxetine and tramadol increase risk of serotonin syndrome and seizures. Avoid combination." },
  { drugs:["sertraline","linezolid"],     severity:"high",     note:"Sertraline and linezolid combination can cause serotonin syndrome. Contraindicated — do not use together." },
  { drugs:["fluoxetine","maoi"],          severity:"high",     note:"Fluoxetine with MAOIs is contraindicated and can cause fatal serotonin syndrome. Do not combine." },
  { drugs:["sertraline","maoi"],          severity:"high",     note:"SSRIs and MAOIs together can cause life-threatening serotonin syndrome. Do not use within 14 days of each other." },
  // Blood thinners
  { drugs:["warfarin","aspirin"],         severity:"high",     note:"Warfarin and aspirin together significantly increase bleeding risk. Avoid unless specifically directed by your doctor." },
  { drugs:["warfarin","ibuprofen"],       severity:"high",     note:"Ibuprofen can increase warfarin's blood-thinning effect and cause serious bleeding. Avoid combination." },
  { drugs:["warfarin","naproxen"],        severity:"high",     note:"Naproxen increases warfarin's anticoagulant effect and bleeding risk. Avoid concurrent use." },
  { drugs:["warfarin","fluconazole"],     severity:"high",     note:"Fluconazole significantly increases warfarin levels, raising bleeding risk. Monitor INR closely." },
  { drugs:["warfarin","amiodarone"],      severity:"high",     note:"Amiodarone greatly increases warfarin effect and bleeding risk. Dose adjustment and close monitoring required." },
  { drugs:["clopidogrel","aspirin"],      severity:"moderate", note:"Clopidogrel and aspirin together increase bleeding risk. This combination is sometimes intentional — confirm with your doctor." },
  // Statins
  { drugs:["atorvastatin","clarithromycin"], severity:"high",  note:"Clarithromycin can greatly increase atorvastatin levels, raising the risk of muscle damage (rhabdomyolysis). Avoid combination." },
  { drugs:["simvastatin","amlodipine"],   severity:"moderate", note:"Amlodipine increases simvastatin blood levels. Limit simvastatin dose and watch for muscle pain." },
  { drugs:["simvastatin","clarithromycin"],severity:"high",    note:"Clarithromycin significantly raises simvastatin levels, causing dangerous muscle breakdown risk. Avoid." },
  // Blood pressure
  { drugs:["lisinopril","potassium"],     severity:"moderate", note:"Lisinopril and potassium supplements together can cause dangerously high potassium levels (hyperkalemia). Monitor levels." },
  { drugs:["lisinopril","spironolactone"],severity:"moderate", note:"Combining lisinopril with spironolactone increases risk of hyperkalemia. Monitor potassium levels regularly." },
  { drugs:["metoprolol","verapamil"],     severity:"high",     note:"Metoprolol and verapamil together can cause severe bradycardia and heart block. Avoid concurrent use." },
  { drugs:["metoprolol","diltiazem"],     severity:"moderate", note:"Metoprolol and diltiazem together may cause slow heart rate and low blood pressure. Monitor closely." },
  // Diabetes
  { drugs:["metformin","alcohol"],        severity:"high",     note:"Alcohol and metformin increase the risk of lactic acidosis, a rare but serious condition. Limit alcohol intake." },
  { drugs:["metformin","iodinated contrast"], severity:"high", note:"Metformin should be stopped before contrast dye procedures due to risk of kidney damage and lactic acidosis." },
  { drugs:["glipizide","fluconazole"],    severity:"high",     note:"Fluconazole can significantly increase glipizide levels, causing dangerous low blood sugar. Monitor closely." },
  // Antibiotics
  { drugs:["ciprofloxacin","tizanidine"],severity:"high",      note:"Ciprofloxacin greatly increases tizanidine blood levels, causing severe low blood pressure and sedation. Contraindicated." },
  { drugs:["metronidazole","alcohol"],    severity:"high",     note:"Alcohol and metronidazole cause a severe reaction including nausea, vomiting, and flushing. Avoid all alcohol." },
  { drugs:["doxycycline","antacids"],     severity:"moderate", note:"Antacids reduce doxycycline absorption significantly. Take doxycycline 2 hours before or 6 hours after antacids." },
  // Psych meds
  { drugs:["lithium","ibuprofen"],        severity:"high",     note:"Ibuprofen increases lithium levels and can cause lithium toxicity. Use acetaminophen instead." },
  { drugs:["lithium","naproxen"],         severity:"high",     note:"NSAIDs like naproxen can raise lithium to toxic levels. Avoid and use acetaminophen if pain relief needed." },
  { drugs:["quetiapine","ketoconazole"],  severity:"high",     note:"Ketoconazole greatly increases quetiapine levels, raising the risk of serious cardiac effects." },
  { drugs:["haloperidol","lithium"],      severity:"moderate", note:"Haloperidol and lithium together may increase risk of neurotoxicity. Monitor for confusion or movement problems." },
  // Smoking cessation
  { drugs:["varenicline","alcohol"],      severity:"moderate", note:"Varenicline (Chantix) may increase alcohol effects and lower seizure threshold. Reduce alcohol intake." },
  { drugs:["bupropion","maoi"],           severity:"high",     note:"Bupropion with MAOIs is contraindicated — can cause hypertensive crisis. Do not use within 14 days of each other." },
  { drugs:["bupropion","tramadol"],       severity:"high",     note:"Bupropion and tramadol together significantly increase seizure risk. Avoid combination." },
  // Thyroid
  { drugs:["levothyroxine","calcium"],    severity:"moderate", note:"Calcium supplements reduce levothyroxine absorption. Take levothyroxine at least 4 hours apart from calcium." },
  { drugs:["levothyroxine","antacids"],   severity:"moderate", note:"Antacids interfere with levothyroxine absorption. Separate doses by at least 4 hours." },
  { drugs:["levothyroxine","warfarin"],   severity:"moderate", note:"Levothyroxine can enhance warfarin's blood-thinning effect. Monitor INR when starting or adjusting thyroid medication." },
];

// Check a drug name against an interaction entry (handles brand/generic synonyms)
const DRUG_SYNONYMS = {
  xanax:"alprazolam", lyrica:"pregabalin", chantix:"varenicline",
  neurontin:"gabapentin", vicodin:"hydrocodone", norco:"hydrocodone",
  valium:"diazepam", klonopin:"clonazepam", ambien:"zolpidem",
  soma:"carisoprodol", zoloft:"sertraline", prozac:"fluoxetine",
  coumadin:"warfarin", plavix:"clopidogrel", lipitor:"atorvastatin",
  zocor:"simvastatin", prinivil:"lisinopril", toprol:"metoprolol",
  glucophage:"metformin", abilify:"aripiprazole", seroquel:"quetiapine",
  wellbutrin:"bupropion", synthroid:"levothyroxine",
};

function resolveGeneric(name) {
  const lower = name.toLowerCase().trim();
  return DRUG_SYNONYMS[lower] || lower;
}

function checkKnownInteractions(meds) {
  const results = [];
  const seen = new Set();
  const resolved = meds.map(m => ({ med: m, generic: resolveGeneric(m.name) }));
  for (const entry of KNOWN_INTERACTIONS) {
    const [drugA, drugB] = entry.drugs;
    const matchA = resolved.find(r => r.generic === drugA || r.med.name.toLowerCase() === drugA);
    const matchB = resolved.find(r => r.generic === drugB || r.med.name.toLowerCase() === drugB);
    if (!matchA || !matchB || matchA === matchB) continue;
    const pairKey = [matchA.med.name, matchB.med.name].map(n => n.toLowerCase()).sort().join("|");
    if (seen.has(pairKey)) continue;
    seen.add(pairKey);
    // Replace generic names in note with the brand names the user actually entered
    let note = entry.note;
    const nameA = matchA.med.name;
    const nameB = matchB.med.name;
    const genericA = matchA.generic;
    const genericB = matchB.generic;
    if (nameA.toLowerCase() !== genericA) note = note.replace(new RegExp(genericA, "gi"), nameA);
    if (nameB.toLowerCase() !== genericB) note = note.replace(new RegExp(genericB, "gi"), nameB);
    results.push({
      drugs: nameA + " + " + nameB,
      severity: entry.severity,
      note,
      source: "known",
    });
  }
  return results;
}

async function fetchLiveInteractions(meds) {
  if (meds.length < 2) return [];
  const cacheKey = meds.map(m => m.name.toLowerCase()).sort().join("|");
  if (_interactionCache[cacheKey] !== undefined) return _interactionCache[cacheKey];

  // Step 1: Check hardcoded known interactions table (instant, reliable)
  const knownResults = checkKnownInteractions(meds);
  const knownPairs = new Set(knownResults.map(r => r.drugs.toLowerCase()));
  const results = [...knownResults];
  const seen = new Set(results.map(r => [r.drugs.split(" + ")[0], r.drugs.split(" + ")[1]].map(n=>n.toLowerCase()).sort().join("|")));

  // Step 2: OpenFDA label scan for additional interactions not in table
  const fdaFound = [];
  for (const med of meds) {
    try {
      let label = null;
      const nameEnc = encodeURIComponent('"' + med.name + '"');
      for (const field of ["generic_name", "brand_name", "substance_name"]) {
        try {
          const r = await fetch(`https://api.fda.gov/drug/label.json?search=openfda.${field}:${nameEnc}&limit=1`);
          if (!r.ok) continue;
          const d = await r.json();
          if (d.results?.[0]) { label = d.results[0]; break; }
        } catch { continue; }
      }
      if (!label) continue;
      const interactionText = [label.drug_interactions, label.warnings, label.warnings_and_cautions, label.boxed_warning]
        .filter(Boolean).flat().join(" ").toLowerCase();
      for (const other of meds) {
        if (other.id === med.id) continue;
        const otherGeneric = resolveGeneric(other.name);
        const otherName = other.name.toLowerCase();
        const found = interactionText.includes(otherName) || interactionText.includes(otherGeneric);
        if (!found) continue;
        const pairKey = [med.name, other.name].map(n => n.toLowerCase()).sort().join("|");
        if (seen.has(pairKey)) continue;
        seen.add(pairKey);
        const searchName = interactionText.includes(otherName) ? otherName : otherGeneric;
        const idx = interactionText.indexOf(searchName);
        const snippet = interactionText.slice(Math.max(0, idx-200), idx+400);
        const severity =
          /contraindicated|avoid combination|do not use|life.threatening|fatal/.test(snippet) ? "high" :
          /serious|severe|caution|monitor|may increase|may decrease|respiratory depression|sedation/.test(snippet) ? "moderate" : "mild";
        fdaFound.push({ drugs: med.name + " + " + other.name, severity, snippet, pairKey });
      }
    } catch(e) { console.warn("FDA scan failed for " + med.name, e.message); }
  }

  // Step 3: For FDA-found interactions, use Claude to generate plain English note
  if (fdaFound.length > 0) {
    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: "You are a clinical pharmacist writing patient-friendly drug interaction summaries. Given FDA label text about a drug interaction, write ONE clear sentence (max 25 words) explaining the risk in plain English a patient can understand. No jargon, no section references. Just the key risk and what to do.",
          messages: fdaFound.map(f => ({
            role: "user",
            content: `Drug pair: ${f.drugs}\nFDA label excerpt: ${f.snippet.slice(0,300)}\nWrite one plain English sentence about this interaction.`
          })).slice(0,1), // one message with the first interaction for simplicity
        }),
      });
      const data = await response.json();
      const aiNote = data.content?.[0]?.text?.trim();
      if (aiNote && fdaFound[0]) {
        results.push({ drugs: fdaFound[0].drugs, severity: fdaFound[0].severity, note: aiNote });
        // For remaining FDA pairs without AI note, use a clean generic
        for (const f of fdaFound.slice(1)) {
          results.push({ drugs: f.drugs, severity: f.severity, note: `Taking ${f.drugs.replace(" + ", " with ")} may cause an interaction. Consult your doctor or pharmacist.` });
        }
      }
    } catch(e) {
      // If Claude API fails, push FDA results with generic notes
      console.warn("Claude API note generation failed:", e.message);
      for (const f of fdaFound) {
        results.push({ drugs: f.drugs, severity: f.severity, note: `Taking ${f.drugs.replace(" + ", " with ")} may cause an interaction. Consult your doctor or pharmacist.` });
      }
    }
  }

  const sevOrder = {high:0,moderate:1,mild:2};
  results.sort((a,b) => (sevOrder[a.severity]??2) - (sevOrder[b.severity]??2));
  _interactionCache[cacheKey] = results;
  console.log("Interactions found:", results.length, "(known:", knownResults.length, "FDA:", fdaFound.length, ")");
  return results;
}


const INITIAL_MEDS = [
  { id:1, name:"Lisinopril", dose:"10mg", color:"#2dd4bf", category:"Blood Pressure", refillCount:18, refillTotal:30, missedRule:"skip", schedType:"fixed", fixedTimes:["08:00","21:00"] },
  { id:2, name:"Metformin", dose:"500mg", color:"#60a5fa", category:"Diabetes", refillCount:8, refillTotal:60, missedRule:"take_if_window", schedType:"interval", intervalHours:8, intervalFirstTime:"08:00", intervalMaxPerDay:3 },
  { id:3, name:"Atorvastatin", dose:"20mg", color:"#fb7185", category:"Cholesterol", refillCount:25, refillTotal:30, missedRule:"skip", schedType:"times", timesPerDay:1, timesStartHour:21 },
  { id:4, name:"Aspirin", dose:"81mg", color:"#fbbf24", category:"Heart Health", refillCount:3, refillTotal:30, missedRule:"take_if_window", schedType:"times", timesPerDay:1, timesStartHour:8 },
];
const INITIAL_SYMPTOMS = [
  { id:1, text:"Slight dizziness after morning dose", severity:2, date:todayStr, med:"Lisinopril" },
  { id:2, text:"Mild nausea — settled after eating", severity:1, date:todayStr, med:"Metformin" },
];
const CAREGIVERS_INIT = [
  { id:1, name:"Sarah Johnson", role:"Primary Caregiver", initials:"SJ", color:"#2dd4bf", status:"Active", access:"Full" },
  { id:2, name:"Dr. Michael Chen", role:"Physician", initials:"MC", color:"#60a5fa", status:"Active", access:"Read-only" },
];

// ── SCHEDULING ENGINE ─────────────────────────────────────────────────────────
const hhmm = (d) => d.toTimeString().slice(0,5); // internal 24hr "HH:MM"
const fmt12 = (d) => {
  if (!d) return "";
  const h = d.getHours(), m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2,"0")} ${ampm}`;
};
const fmtHHMM12 = (s) => { // convert "HH:MM" string to 12hr display
  if (!s) return "";
  const [h,m] = s.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2,"0")} ${ampm}`;
};
const addHours = (d, h) => new Date(d.getTime() + h * 3600000);
const addMins = (d, m) => new Date(d.getTime() + m * 60000);

function parseHHMM(str, ref) {
  const [h,m] = str.split(":").map(Number);
  const d = new Date(ref); d.setHours(h,m,0,0); return d;
}

function generateTodayDoses(med, doseHistory, now) {
  const doses = [];
  const today = now.toISOString().split("T")[0];
  const takenToday = (doseHistory[med.id]||[]).filter(e=>e.takenAt&&e.takenAt.startsWith(today));

  if (med.schedType === "fixed") {
    (med.fixedTimes||["08:00"]).forEach((t,i) => {
      doses.push({ doseKey:`${med.id}-fixed-${i}-${today}`, medId:med.id, plannedTime:parseHHMM(t,now), originalTime:t, type:"fixed" });
    });
  } else if (med.schedType === "times") {
    const n = med.timesPerDay||1;
    // Guard: treat 0 (midnight) as missing — default to 8am
    const startH = (med.timesStartHour != null && med.timesStartHour > 0) ? med.timesStartHour : 8;
    // Gap: use user-defined gap if set; otherwise default to 24/n (even 24hr split)
    // e.g. 2x/day = 12h apart, 3x/day = 8h apart, 4x/day = 6h apart
    const gapHours = med.timesGapHours || (n > 1 ? Math.round(24/n) : 0);
    for (let i=0; i<n; i++) {
      const hrs = startH + i*gapHours;
      const t = new Date(now); t.setHours(Math.floor(hrs % 24), Math.round((hrs%1)*60), 0, 0);
      doses.push({ doseKey:`${med.id}-times-${i}-${today}`, medId:med.id, plannedTime:t, originalTime:hhmm(t), type:"times" });
    }
  } else if (med.schedType === "interval") {
    const allTaken = (doseHistory[med.id]||[]).filter(e=>e.takenAt).sort((a,b)=>new Date(b.takenAt)-new Date(a.takenAt));
    const lastTaken = allTaken[0];
    const intervalH = med.intervalHours||8;
    const maxPerDay = med.intervalMaxPerDay||Math.floor(24/intervalH);
    const dayStart = new Date(now); dayStart.setHours(0,0,0,0);
    const dayEnd = new Date(now); dayEnd.setHours(23,59,0,0);
    let anchor;
    if (lastTaken) {
      const lt = new Date(lastTaken.takenAt);
      anchor = lt >= dayStart ? lt : parseHHMM(med.intervalFirstTime||"08:00", now);
    } else {
      anchor = parseHHMM(med.intervalFirstTime||"08:00", now);
    }
    for (let i=0; i<maxPerDay; i++) {
      const t = i===0 ? new Date(anchor) : addHours(doses[i-1].plannedTime, intervalH);
      if (t < dayStart || t > dayEnd) continue;
      doses.push({ doseKey:`${med.id}-interval-${i}-${today}`, medId:med.id, plannedTime:t, originalTime:hhmm(t), type:"interval" });
    }
  } else if (med.schedType === "prn") {
    doses.push({ doseKey:`${med.id}-prn-0`, medId:med.id, plannedTime:null, originalTime:null, type:"prn",
      maxPerDay:med.prnMaxPerDay||4, minGapH:med.prnMinGapHours||4, takenCount:takenToday.length });
  } else if (med.schedType === "cyclic") {
    const onDays = med.cyclicOnDays||21;
    const offDays = med.cyclicOffDays||7;
    const start = med.cyclicStart ? new Date(med.cyclicStart) : new Date(now.getFullYear(), now.getMonth(), 1);
    const daysSince = Math.floor((now - start)/86400000);
    const pos = daysSince % (onDays+offDays);
    const isOn = pos < onDays;
    const daysLeft = isOn ? (onDays-pos) : (onDays+offDays-pos);
    if (isOn) {
      const t = parseHHMM(med.cyclicTime||"08:00", now);
      doses.push({ doseKey:`${med.id}-cyclic-0-${today}`, medId:med.id, plannedTime:t, originalTime:hhmm(t), type:"cyclic", isOn, daysLeft });
    } else {
      doses.push({ doseKey:`${med.id}-cyclic-off-${today}`, medId:med.id, plannedTime:null, originalTime:null, type:"cyclic", isOn, daysLeft });
    }
  }
  return doses;
}

function getMissedAdvice(med, plannedTime, now) {
  const minsLate = Math.round((now - plannedTime)/60000);
  const hoursLate = minsLate/60;
  if (med.schedType==="interval") {
    const half = (med.intervalHours||8)/2;
    if (hoursLate < half) return { action:"take_now", label:"Take now — shift next dose", desc:`${minsLate} min late. Take it now and the next dose shifts ${med.intervalHours}h from this moment.`, shifts:true };
    return { action:"skip", label:"Skip this dose", desc:`${Math.round(hoursLate)}h late — over half the interval. Safer to skip and take next dose on the original interval.`, shifts:false };
  }
  if (med.missedRule==="take_if_window") {
    if (hoursLate<=4) return { action:"take_now", label:"Take now", desc:`${minsLate} min late — within the 4h window. Take now and continue your normal schedule.`, shifts:false };
    return { action:"skip", label:"Skip this dose", desc:`${Math.round(hoursLate)}h late — past the safe 4h window. Skip and take your next dose normally.`, shifts:false };
  }
  return { action:"skip", label:"Skip this dose", desc:"Your prescription says to skip missed doses. Resume your next scheduled dose.", shifts:false };
}

// Synchronous fallback — returns cached results or empty (components use fetchLiveInteractions)
function getInteractions(meds) {
  if (meds.length < 2) return [];
  const cacheKey = meds.map(m => m.name.toLowerCase()).sort().join("|");
  return _interactionCache[cacheKey] || [];
}

function schedLabel(med) {
  if (med.schedType==="fixed") return `Fixed: ${(med.fixedTimes||[]).join(", ")}`;
  if (med.schedType==="times") {
  const h = med.timesStartHour||8;
  const ampm = h>=12?"PM":"AM"; const h12 = h%12||12;
  const gapStr = med.timesGapHours ? ` every ${med.timesGapHours}h` : "";
  return `${med.timesPerDay}x daily from ${h12}:00 ${ampm}${gapStr}`;
}
  if (med.schedType==="interval") return `Every ${med.intervalHours}h from last dose`;
  if (med.schedType==="prn") return `As needed — max ${med.prnMaxPerDay}/day, ≥${med.prnMinGapHours}h apart`;
  if (med.schedType==="cyclic") return `${med.cyclicOnDays}d on / ${med.cyclicOffDays}d off`;
  return "";
}

function schedTagColor(type) {
  return { fixed:"tag-teal", times:"tag-blue", interval:"tag-purple", prn:"tag-amber", cyclic:"tag-coral" }[type]||"tag-teal";
}

// ── AI ────────────────────────────────────────────────────────────────────────
async function askClaude(messages, medCtx) {
  // Calls Netlify serverless function — keeps API key off the client
  const res = await fetch("/.netlify/functions/claude", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001",
      max_tokens: 1000,
      system: `You are Veriva's caring AI health companion. You have access to the user's complete medication profile below. Always reference this data directly when answering — never say you don't have access to it.

MEDICATION DATA:
${medCtx || "No medications added yet."}

Answer questions about refills, adherence, side effects, and scheduling using the data above. Be warm and concise (under 150 words). Never diagnose or prescribe. Always suggest consulting their doctor for medical decisions.`,
      messages,
    }),
  });
  if (!res.ok) {
    console.error("Claude proxy error:", res.status);
    return "Connection issue — please try again.";
  }
  const d = await res.json();
  return d.content?.[0]?.text || "Connection issue — please try again.";
}

// ── SUB COMPONENTS ────────────────────────────────────────────────────────────
function SeverityDots({ level }) {
  return <div className="severity-bar">{[1,2,3,4,5].map(i=><div key={i} style={{width:8,height:8,borderRadius:2,background:i<=level?(level<=2?"var(--green)":level<=3?"var(--amber)":"var(--coral)"):"var(--border)"}}/>)}</div>;
}

function AdherenceGrid({ meds, doseHistory }) {
  // Build last 28 days — newest on right
  const days = Array.from({length:28}, (_,i) => {
    const d = new Date();
    d.setHours(0,0,0,0);
    d.setDate(d.getDate() - (27 - i));
    return d;
  });

  // For each day compute scheduled vs taken
  const cells = days.map(day => {
    const dayStr = day.toISOString().split("T")[0];
    const isToday = dayStr === todayStr;
    const isFuture = day > new Date();
    if (isFuture) return { status: "future", date: day };

    // Count scheduled doses for that day (use day-of-week to match schedule)
    let scheduled = 0;
    let taken = 0;
    let skipped = 0;

    meds.forEach(med => {
      // Estimate scheduled doses for the day
      let doses = 0;
      if (med.schedType === "fixed") doses = (med.fixedTimes||["08:00"]).length;
      else if (med.schedType === "times") doses = med.timesPerDay || 1;
      else if (med.schedType === "interval") doses = med.intervalMaxPerDay || 3;
      else if (med.schedType === "prn") doses = 0; // PRN not counted in adherence
      else if (med.schedType === "cyclic") {
        // Check if this day was an "on" day
        const start = med.cyclicStart ? new Date(med.cyclicStart) : new Date(day.getFullYear(), day.getMonth(), 1);
        const daysSince = Math.floor((day - start) / 86400000);
        const cycleLen = (med.cyclicOnDays||21) + (med.cyclicOffDays||7);
        const posInCycle = ((daysSince % cycleLen) + cycleLen) % cycleLen;
        doses = posInCycle < (med.cyclicOnDays||21) ? 1 : 0;
      }
      scheduled += doses;

      // Count actual taken/skipped entries for this day
      const entries = (doseHistory[med.id]||[]).filter(e => e.date === dayStr || (e.takenAt && e.takenAt.startsWith(dayStr)));
      taken += entries.filter(e => e.takenAt && !e.skipped).length;
      skipped += entries.filter(e => e.skipped).length;
    });

    if (scheduled === 0) return { status: isToday ? "today" : "none", date: day };
    const pct = taken / scheduled;
    let status;
    if (pct >= 1) status = "full";
    else if (pct > 0) status = "partial";
    else if (skipped > 0) status = "skipped";
    else if (isToday) status = "today"; // today with no data yet
    else status = "missed";
    return { status, date: day, pct: Math.round(pct*100), taken, scheduled };
  });

  // 28-day adherence headline
  const tracked = cells.filter(c => !["future","none","today"].includes(c.status));
  const fullDays = cells.filter(c => c.status === "full").length;
  const weekPct = tracked.length > 0 ? Math.round((cells.filter(c=>c.status==="full").length / Math.max(tracked.length,1))*100) : null;

  const statusColor = { full:"var(--teal)", partial:"var(--amber)", missed:"rgba(251,113,133,.5)", skipped:"var(--text-dim)", today:"var(--border)", future:"transparent", none:"var(--border)" };
  const statusLabel = { full:"✓", partial:"½", missed:"✗", skipped:"–", today:"·", future:"", none:"" };

  return (
    <div>
      {weekPct !== null && (
        <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:14}}>
          <span style={{fontFamily:"'DM Serif Display',serif",fontSize:28,color:weekPct>=80?"var(--teal)":weekPct>=60?"var(--amber)":"var(--coral)"}}>{weekPct}%</span>
          <span style={{fontSize:13,color:"var(--text-muted)"}}>overall adherence · {fullDays} perfect days</span>
        </div>
      )}
      {weekPct === null && (
        <div style={{fontSize:13,color:"var(--text-muted)",marginBottom:14}}>Start logging doses to see your adherence history.</div>
      )}
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:4}}>
        {["S","M","T","W","T","F","S"].map((l,i)=><div key={i} style={{fontSize:10,color:"var(--text-dim)",textAlign:"center"}}>{l}</div>)}
      </div>
      <div className="adh-grid">
        {cells.map((c,i)=>(
          <div key={i} title={c.date?.toLocaleDateString("en-US",{month:"short",day:"numeric"}) + (c.scheduled ? ` · ${c.taken}/${c.scheduled} taken` : "")}
            className="adh-cell" style={{background:statusColor[c.status],border:c.status==="today"?"2px solid var(--teal)":undefined,opacity:c.status==="future"?0.15:1}}>
            <span style={{fontSize:9,color:c.status==="full"?"#0d1117":"var(--text-muted)"}}>{statusLabel[c.status]}</span>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:12,marginTop:9,flexWrap:"wrap"}}>
        {[["var(--teal)","Taken"],["var(--amber)","Partial"],["rgba(251,113,133,.5)","Missed"],["var(--text-dim)","Skipped"]].map(([c,l])=>(
          <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"var(--text-muted)"}}>
            <div style={{width:10,height:10,borderRadius:2,background:c}}/>{l}
          </div>))}
      </div>
    </div>
  );
}

function RefillBar({ med }) {
  const pct = Math.round((med.refillCount/med.refillTotal)*100);
  const color = pct<=20?"var(--coral)":pct<=40?"var(--amber)":"var(--green)";
  const daily = med.schedType==="fixed"?(med.fixedTimes||["08:00"]).length:med.schedType==="times"?(med.timesPerDay||1):med.schedType==="interval"?(med.intervalMaxPerDay||3):1;
  const days = Math.floor(med.refillCount/daily);
  return (
    <div className="refill-card">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontWeight:700,fontSize:14}}>{med.name} <span style={{fontWeight:400,fontSize:12,color:"var(--text-muted)"}}>{med.dose}</span></span>
        <span style={{fontSize:12,color:pct<=20?"var(--coral)":"var(--text-muted)"}}>{med.refillCount} pills {pct<=20&&"⚠️"}</span>
      </div>
      <div className="refill-bar-bg"><div className="refill-bar-fill" style={{width:`${pct}%`,background:color}}/></div>
      <div style={{display:"flex",justifyContent:"space-between"}}>
        <span style={{fontSize:11,color:"var(--text-muted)"}}>{pct}% remaining</span>
        <span style={{fontSize:11,color:"var(--text-muted)"}}>~{days} days supply</span>
      </div>
    </div>
  );
}

// ── EPIC FHIR IMPORT PANEL ────────────────────────────────────────────────────
function EpicImportPanel({ meds, setMeds, showToast, epicSession, setEpicSession }) {
  const [fhirMeds, setFhirMeds] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [status, setStatus] = useState("idle"); // idle | loading | loaded | error
  const [errorMsg, setErrorMsg] = useState("");

  const isConnected = !!epicSession?.accessToken;

  const fetchMeds = async (token, patientId) => {
    setStatus("loading");
    try {
      const bundle = await fetchMedicationRequests(token, patientId);
      const entries = bundle.entry || [];
      const parsed = entries
        .filter(e => e.resource?.resourceType === "MedicationRequest")
        .map(parseFhirMedication);
      setFhirMeds(parsed);
      setSelected(new Set(parsed.map(m => m.epicResourceId)));
      setStatus("loaded");
    } catch (e) {
      setErrorMsg(e.message);
      setStatus("error");
    }
  };

  useEffect(() => {
    if (isConnected && fhirMeds.length === 0 && status === "idle") {
      fetchMeds(epicSession.accessToken, epicSession.patientId);
    }
  }, [isConnected]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const importSelected = () => {
    const toImport = fhirMeds.filter(m => selected.has(m.epicResourceId));
    const existingNames = new Set(meds.map(m => m.name.toLowerCase()));
    const newMeds = toImport.filter(m => !existingNames.has(m.name.toLowerCase()));
    if (newMeds.length === 0) { showToast("⚠️ All selected medications already exist"); return; }
    setMeds(prev => [...prev, ...newMeds]);
    showToast(`✅ Imported ${newMeds.length} medication${newMeds.length>1?"s":""} from Epic`);
    setFhirMeds([]);
    setSelected(new Set());
    setStatus("idle");
  };

  const disconnect = () => {
    setEpicSession(null);
    setFhirMeds([]);
    setSelected(new Set());
    setStatus("idle");
    sessionStorage.removeItem("epic_verifier");
    sessionStorage.removeItem("epic_state");
    showToast("🔌 Disconnected from Epic");
  };

  return (
    <div className="epic-banner">
      <div className="epic-banner-title">
        <span>⚕️</span> Import from Epic Health Record
      </div>
      <div className="epic-banner-desc">
        Securely connect your Epic MyChart account to automatically import your active prescriptions. Uses OAuth 2.0 + PKCE — Veriva™ never stores your Epic credentials.
      </div>

      {/* Status row */}
      <div className="epic-status-row">
        <div className={`epic-status-dot ${isConnected ? "connected" : status === "loading" ? "loading" : "disconnected"}`}/>
        <span>
          {isConnected
            ? `Connected · Patient ID: ${epicSession.patientId?.slice(0,12)}…`
            : status === "loading" ? "Fetching medications…"
            : "Not connected"}
        </span>
        {isConnected && (
          <button className="btn btn-danger btn-xs" style={{marginLeft:"auto"}} onClick={disconnect}>Disconnect</button>
        )}
      </div>

      {/* Not connected */}
      {!isConnected && status !== "loading" && (
        <div>
          <div style={{display:"flex",flexDirection:"column",gap:0,marginBottom:14}}>
            {[
              ["1","Click 'Connect Epic' below"],
              ["2","Log in with your MyChart credentials"],
              ["3","Authorize Veriva™ to read your prescriptions"],
              ["4","Your medications import automatically"],
            ].map(([n,t])=>(
              <div key={n} className="epic-step">
                <div className="epic-step-num">{n}</div>
                <div className="epic-step-text"><div className="epic-step-title">{t}</div></div>
              </div>
            ))}
          </div>
          <button className="btn btn-epic" onClick={launchEpicAuth}>
            🏥 Connect Epic MyChart
          </button>
        </div>
      )}

      {/* Loading */}
      {status === "loading" && (
        <div style={{display:"flex",alignItems:"center",gap:10,color:"var(--text-muted)",fontSize:13,padding:"8px 0"}}>
          <div className="spinner"/> Fetching your prescriptions from Epic…
        </div>
      )}

      {/* Error */}
      {status === "error" && (
        <div style={{background:"rgba(251,113,133,.08)",border:"1px solid rgba(251,113,133,.25)",borderRadius:"var(--radius-sm)",padding:"11px 13px",fontSize:12.5,color:"var(--coral)",marginTop:8}}>
          ⚠️ {errorMsg}
          <button className="btn btn-secondary btn-sm" style={{marginLeft:12}} onClick={()=>fetchMeds(epicSession.accessToken,epicSession.patientId)}>Retry</button>
        </div>
      )}

      {/* Loaded — show medication list */}
      {status === "loaded" && fhirMeds.length > 0 && (
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:12.5,color:"var(--text-muted)"}}>{fhirMeds.length} active prescription{fhirMeds.length>1?"s":""} found</span>
            <div style={{display:"flex",gap:6}}>
              <button className="btn btn-secondary btn-xs" onClick={()=>setSelected(new Set(fhirMeds.map(m=>m.epicResourceId)))}>All</button>
              <button className="btn btn-secondary btn-xs" onClick={()=>setSelected(new Set())}>None</button>
            </div>
          </div>
          <div className="fhir-import-list">
            {fhirMeds.map(m=>(
              <div key={m.epicResourceId} className={`fhir-med-row ${selected.has(m.epicResourceId)?"selected":""}`} role="checkbox" aria-checked={selected.has(m.epicResourceId)} tabIndex={0} onClick={()=>toggleSelect(m.epicResourceId)} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&toggleSelect(m.epicResourceId)}>
                <div className={`fhir-check ${selected.has(m.epicResourceId)?"checked":""}`}>{selected.has(m.epicResourceId)?"✓":""}</div>
                <div className="fhir-med-info">
                  <div className="fhir-med-name">{m.name} {m.dose && <span style={{fontWeight:400,fontSize:12,color:"var(--text-muted)"}}>{m.dose}</span>}</div>
                  <div className="fhir-med-detail">{schedLabel(m)} · {m.category}</div>
                </div>
                <span className="fhir-source-badge">Epic</span>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:8,marginTop:12}}>
            <button className="btn btn-epic" disabled={selected.size===0} onClick={importSelected}>
              Import {selected.size} Medication{selected.size!==1?"s":""}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={()=>{setFhirMeds([]);setStatus("idle");}}>Cancel</button>
          </div>
        </div>
      )}

      {status === "loaded" && fhirMeds.length === 0 && (
        <div style={{fontSize:13,color:"var(--text-muted)",padding:"8px 0"}}>
          No active prescriptions found in your Epic record.
        </div>
      )}
    </div>
  );
}

// ── SCHEDULE FORM ─────────────────────────────────────────────────────────────
function ScheduleForm({ form, setForm }) {
  const t = form.schedType;
  return (
    <div>
      <div className="modal-section">Scheduling Type</div>
      <div className="stype-grid">
        {SCHED_TYPES.map(s=>(
          <div key={s.id} className={`stype-card ${t===s.id?"selected":""}`} role="radio" aria-checked={t===s.id} tabIndex={0} onClick={()=>setForm(f=>({...f,schedType:s.id}))} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&setForm(f=>({...f,schedType:s.id}))}>
            <div className="stype-icon">{s.icon}</div>
            <div className="stype-title">{s.title}</div>
            <div className="stype-desc">{s.desc}</div>
          </div>
        ))}
      </div>
      <div className="modal-section">Schedule Settings</div>
      {t==="fixed"&&(
        <div className="form-group">
          <label className="form-label">Dose Times</label>
          {(form.fixedTimes||["08:00"]).map((time,i)=>(
            <div key={i} style={{display:"flex",gap:8,marginBottom:6}}>
              <input className="form-input" type="time" value={time} onChange={e=>{const a=[...(form.fixedTimes||["08:00"])];a[i]=e.target.value;setForm(f=>({...f,fixedTimes:a}));}}/>
              {i>0&&<button className="btn btn-danger btn-sm" onClick={()=>setForm(f=>({...f,fixedTimes:f.fixedTimes.filter((_,j)=>j!==i)}))}>×</button>}
            </div>
          ))}
          {(form.fixedTimes||[]).length<4&&<button className="btn btn-secondary btn-sm" onClick={()=>setForm(f=>({...f,fixedTimes:[...(f.fixedTimes||["08:00"]),"12:00"]}))}>+ Add Time</button>}
        </div>
      )}
      {t==="times"&&(
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Times Per Day</label>
            <select className="form-input" value={form.timesPerDay||1} onChange={e=>setForm(f=>({...f,timesPerDay:parseInt(e.target.value)}))}>
              {[1,2,3,4,5,6].map(n=><option key={n} value={n}>{n}x per day</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">First Dose Time</label>
            <select className="form-input" value={form.timesStartHour ?? 8} onChange={e=>setForm(f=>({...f,timesStartHour:parseInt(e.target.value)}))}>
              {Array.from({length:18},(_,i)=>i+5).map(h=><option key={h} value={h}>{h<12?`${h}:00 AM`:h===12?`12:00 PM`:`${h-12}:00 PM`}</option>)}
            </select>
          </div>
          {(form.timesPerDay||1) > 1 && (
            <div className="form-group">
              <label className="form-label">Hours between doses</label>
              <select className="form-input"
                value={form.timesGapHours || Math.round(24/(form.timesPerDay||2))}
                onChange={e=>setForm(f=>({...f,timesGapHours:parseFloat(e.target.value)}))}>
                {[1,2,3,4,5,6,7,8,10,12,16,24].map(h=>(
                  <option key={h} value={h}>{h} hour{h>1?"s":""} apart</option>
                ))}
              </select>
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>
                {(() => {
                  const start = form.timesStartHour||8;
                  const gap = form.timesGapHours || Math.round(24/(form.timesPerDay||2));
                  const n = form.timesPerDay||2;
                  return Array.from({length:n},(_,i)=>{
                    const h = (start + i*gap) % 24;
                    const ampm = h>=12?"PM":"AM"; const h12 = h%12||12;
                    return `${h12}:00 ${ampm}`;
                  }).join(" · ");
                })()}
              </div>
            </div>
          )}
        </div>
      )}
      {t==="interval"&&(
        <div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Interval (hours)</label>
              <select className="form-input" value={form.intervalHours||8} onChange={e=>setForm(f=>({...f,intervalHours:parseInt(e.target.value)}))}>
                {[2,3,4,6,8,12,24].map(h=><option key={h} value={h}>Every {h}h</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">First Dose Time</label>
              <input className="form-input" type="time" value={form.intervalFirstTime||"08:00"} onChange={e=>setForm(f=>({...f,intervalFirstTime:e.target.value}))}/>
            </div>
          </div>
          <div className="info-box">⏱️ Taking a dose late automatically pushes all subsequent doses today to maintain the {form.intervalHours||8}h gap.</div>
        </div>
      )}
      {t==="prn"&&(
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Max Doses Per Day</label>
            <select className="form-input" value={form.prnMaxPerDay||4} onChange={e=>setForm(f=>({...f,prnMaxPerDay:parseInt(e.target.value)}))}>
              {[1,2,3,4,5,6,8].map(n=><option key={n} value={n}>{n} per day</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Min Hours Between Doses</label>
            <select className="form-input" value={form.prnMinGapHours||4} onChange={e=>setForm(f=>({...f,prnMinGapHours:parseInt(e.target.value)}))}>
              {[1,2,3,4,6,8].map(h=><option key={h} value={h}>{h}h minimum</option>)}
            </select>
          </div>
        </div>
      )}
      {t==="cyclic"&&(
        <div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Days ON</label>
              <input className="form-input" type="number" min={1} max={90} value={form.cyclicOnDays||21} onChange={e=>setForm(f=>({...f,cyclicOnDays:parseInt(e.target.value)||21}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">Days OFF</label>
              <input className="form-input" type="number" min={1} max={90} value={form.cyclicOffDays||7} onChange={e=>setForm(f=>({...f,cyclicOffDays:parseInt(e.target.value)||7}))}/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Dose Time</label>
              <input className="form-input" type="time" value={form.cyclicTime||"08:00"} onChange={e=>setForm(f=>({...f,cyclicTime:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">Cycle Start Date</label>
              <input className="form-input" type="date" value={form.cyclicStart||todayStr} onChange={e=>setForm(f=>({...f,cyclicStart:e.target.value}))}/>
            </div>
          </div>
        </div>
      )}
      {t!=="interval"&&t!=="prn"&&t!=="cyclic"&&(
        <div>
          <div className="modal-section">Missed Dose Rule</div>
          <div className="form-group">
            <select className="form-input" value={form.missedRule||"take_if_window"} onChange={e=>setForm(f=>({...f,missedRule:e.target.value}))}>
              <option value="take_if_window">Take ASAP if within 4 hours, otherwise skip</option>
              <option value="skip">Always skip — resume next scheduled dose</option>
              <option value="always_take">Always take when remembered</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

// ── MISSED DOSE MODAL ─────────────────────────────────────────────────────────
function MissedDoseModal({ dose, med, onResolve, onClose }) {
  const now = new Date();
  const advice = getMissedAdvice(med, dose.plannedTime, now);
  const [chosen, setChosen] = useState("take_now");
  const opts = [
    { id:"taken_on_schedule", title:"✅ Taken as prescribed", desc:`Log as taken at the scheduled time (${dose.originalTime}). Use this if you took it on time but forgot to log it.` },
    { id:"take_now", title:"🕐 Take / log it now", desc: advice.shifts ? `Records time as ${fmt12(now)}. Next dose shifts ${med.intervalHours}h from now.` : `Records time as ${fmt12(now)}. Schedule continues normally.` },
    { id:"skip", title:"⏭️ Skip this dose", desc:"Mark as missed. Your next scheduled dose is unaffected." },
    { id:"take_later", title:"🔔 Remind me in 30 min", desc:"Snooze — you'll get a reminder shortly." },
  ];
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Missed dose options">
      <div className="modal">
        <div className="modal-title">⏰ Missed Dose — {med.name} {med.dose}</div>
        <div style={{fontSize:13,color:"var(--text-muted)",marginBottom:12,lineHeight:1.65}}>
          Scheduled: <strong style={{color:"var(--text)"}}>{dose.originalTime}</strong> &nbsp;·&nbsp;
          Now: <strong style={{color:"var(--text)"}}>{hhmm(now)}</strong> &nbsp;·&nbsp;
          Late by: <strong style={{color:"var(--coral)"}}>{Math.round((now-dose.plannedTime)/60000)} min</strong>
        </div>
        <div className="reschedule-banner"><strong style={{color:"var(--amber)"}}>Recommendation: </strong>{advice.desc}</div>
        <div className="modal-section">What would you like to do?</div>
        <div className="missed-options">
          {opts.map(o=>(
            <div key={o.id} className={`missed-option ${chosen===o.id?"selected":""}`} role="radio" aria-checked={chosen===o.id} tabIndex={0} onClick={()=>setChosen(o.id)} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&setChosen(o.id)}>
              <div className="missed-option-title">{o.title}</div>
              <div className="missed-option-desc">{o.desc}</div>
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={()=>onResolve(chosen, dose, med)}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
function Dashboard({ meds, setMeds, doseHistory, setDoseHistory, showToast, setView, interactions, interactionsLoading, user }) {
  const now = new Date();
  const [missedModal, setMissedModal] = useState(null);
  const [shifts, setShifts] = useState({});
  const [tileModal, setTileModal] = useState(null);
  const [logModal, setLogModal] = useState(null); // dose to confirm taking

  const allDoses = meds.flatMap(med => generateTodayDoses(med, doseHistory, now).map(d=>({...d,med}))).sort((a,b)=>(a.plannedTime||new Date(9e15))-(b.plannedTime||new Date(9e15)));

  const dynamicToday = now.toISOString().split("T")[0];
  const takenSet = new Set((doseHistory ? Object.values(doseHistory).flat() : []).filter(e=>e.takenAt&&(e.takenAt.startsWith(dynamicToday)||e.date===dynamicToday)).map(e=>e.doseKey));
  const skippedSet = new Set((doseHistory ? Object.values(doseHistory).flat() : []).filter(e=>e.skipped&&e.date===dynamicToday).map(e=>e.doseKey));

  const scheduled = allDoses.filter(d=>d.plannedTime&&d.type!=="cyclic");
  const takenCount = scheduled.filter(d=>takenSet.has(d.doseKey)).length;
  const pct = scheduled.length>0 ? Math.round((takenCount/scheduled.length)*100) : 100;

  const isMissed = d => d.plannedTime && !takenSet.has(d.doseKey) && !skippedSet.has(d.doseKey) && now > addMins(d.plannedTime,15);
  const displayTime = d => shifts[d.doseKey] ? fmt12(shifts[d.doseKey]) : (d.plannedTime ? fmt12(d.plannedTime) : null);

  const markTaken = (dose) => {
    // PRN doses use a unique timestamped key so multiple takes per day are each logged
    const doseKey = dose.type === "prn"
      ? `${dose.medId}-prn-${Date.now()}`
      : dose.doseKey;
    setDoseHistory(prev=>({...prev,[dose.medId]:[...(prev[dose.medId]||[]),{doseKey,takenAt:new Date().toISOString(),date:getTodayStr()}]}));
    // Decrement refill count by 1 pill per dose taken
    setMeds(prev => prev.map(m => {
      if (m.id !== dose.medId) return m;
      const newCount = Math.max(0, (m.refillCount ?? 0) - 1);
      return {...m, refillCount: newCount};
    }));
    if (dose.type==="interval") showToast(`💊 ${dose.med.name} taken — next dose in ${dose.med.intervalHours}h`);
    else showToast(`💊 ${dose.med.name} marked taken!`);
  };

  const handleClick = (dose) => {
    // PRN doses can be taken multiple times — check both daily max AND min gap
    if (dose.type === "prn") {
      if (dose.takenCount >= dose.maxPerDay) return; // already at daily max
      // Enforce minimum gap between PRN doses
      const med = meds.find(m=>m.id===dose.medId);
      const minGapMs = ((med?.prnMinGapHours||4)*3600000);
      const prnTakes = (doseHistory[dose.medId]||[])
        .filter(e=>e.takenAt&&e.doseKey.includes("-prn-"))
        .map(e=>new Date(e.takenAt))
        .sort((a,b)=>b-a);
      const lastTake = prnTakes[0];
      if (lastTake && (now-lastTake) < minGapMs) {
        const minsLeft = Math.ceil((minGapMs-(now-lastTake))/60000);
        showToast(`⏳ ${med?.name||"PRN"}: ${minsLeft} min until next dose allowed`);
        return;
      }
      markTaken(dose);
      return;
    }
    if (takenSet.has(dose.doseKey)||skippedSet.has(dose.doseKey)) return;
    if (isMissed(dose)) setMissedModal({dose, med:dose.med});
    else setLogModal({dose, med:dose.med});
  };

  const handleResolve = (action, dose, med) => {
    setMissedModal(null);
    if (action==="taken_on_schedule") {
      const scheduledTime = dose.plannedTime ? dose.plannedTime.toISOString() : new Date().toISOString();
      setDoseHistory(prev=>({...prev,[dose.medId]:[...(prev[dose.medId]||[]),{doseKey:dose.doseKey,takenAt:scheduledTime,date:getTodayStr()}]}));
      // Decrement refill
      setMeds(prev=>prev.map(m=>m.id!==dose.medId?m:{...m,refillCount:Math.max(0,(m.refillCount??0)-1)}));
      showToast(`✅ ${med.name} logged as taken on schedule`);
    } else if (action==="take_now") {
      markTaken(dose);
      if (med.schedType==="interval") {
        const nextDose = allDoses.find(d=>d.medId===med.id&&d.doseKey!==dose.doseKey&&!takenSet.has(d.doseKey)&&d.plannedTime);
        if (nextDose) {
          const newTime = addHours(new Date(), med.intervalHours||8);
          setShifts(prev=>({...prev,[nextDose.doseKey]:newTime}));
          showToast(`⏱️ Next ${med.name} rescheduled → ${fmt12(newTime)}`);
        }
      }
    } else if (action==="skip") {
      setDoseHistory(prev=>({...prev,[dose.medId]:[...(prev[dose.medId]||[]),{doseKey:dose.doseKey,skipped:true,date:getTodayStr()}]}));
      showToast(`⏭️ ${med.name} dose skipped`);
    } else if (action==="take_later") {
      // Remind in 30 min — use browser setTimeout as best-effort web reminder
      showToast(`🔔 Reminder set for ${med.name} in 30 min`);
      setTimeout(() => {
        showToast(`⏰ Time to take ${med.name}!`);
      }, 30 * 60 * 1000);
    }
  };

  const today = now.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"});
  const greetHour = now.getHours();
  const greeting = greetHour < 12 ? "Good morning" : greetHour < 17 ? "Good afternoon" : "Good evening";

  // Find next upcoming dose for banner
  const nextUpDose = allDoses.find(d => {
    if (takenSet.has(d.doseKey) || skippedSet.has(d.doseKey)) return false;
    if (d.type === "prn" || (d.type === "cyclic" && !d.isOn)) return false;
    const t = shifts[d.doseKey] || d.plannedTime;
    return t && t > now;
  });
  const nextUpMed = nextUpDose ? meds.find(m => m.id === nextUpDose.medId) : null;
  const nextUpTime = nextUpDose ? (shifts[nextUpDose.doseKey] || nextUpDose.plannedTime) : null;
  const minsUntil = nextUpTime ? Math.round((nextUpTime - now) / 60000) : null;
  const allDone = scheduled.length > 0 && takenCount === scheduled.length;

  // ── Streak computation ───────────────────────────────────────────────────────
  const calcStreak = () => {
    let streak = 0;
    for (let i = 1; i <= 60; i++) {
      const d = new Date(); d.setHours(0,0,0,0); d.setDate(d.getDate() - i);
      const ds = d.toISOString().split("T")[0];
      let sched = 0, taken = 0;
      meds.forEach(m => {
        let doses = 0;
        if (m.schedType==="fixed") doses = (m.fixedTimes||["08:00"]).length;
        else if (m.schedType==="times") doses = m.timesPerDay||1;
        else if (m.schedType==="interval") doses = m.intervalMaxPerDay||3;
        else if (m.schedType==="cyclic") {
          const start = m.cyclicStart ? new Date(m.cyclicStart) : new Date();
          const daysSince = Math.floor((d - start)/86400000);
          const cycleLen = (m.cyclicOnDays||21)+(m.cyclicOffDays||7);
          const pos = ((daysSince%cycleLen)+cycleLen)%cycleLen;
          doses = pos < (m.cyclicOnDays||21) ? 1 : 0;
        }
        sched += doses;
        const entries = (doseHistory[m.id]||[]).filter(e=>e.date===ds||(e.takenAt&&e.takenAt.startsWith(ds)));
        taken += entries.filter(e=>e.takenAt&&!e.skipped).length;
      });
      if (sched === 0) continue;
      if (taken >= sched) streak++;
      else break;
    }
    return streak;
  };
  const streak = calcStreak();

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{greeting}, {user?.user_metadata?.full_name?.split(" ")[0] || user?.email?.split("@")[0] || "there"} 👋</h1>
        <div className="page-desc">{today}</div>
      </div>

      {/* ── TODAY'S SCHEDULE — PRIMARY, always first ── */}
      <div className="card" style={{marginBottom:14}}>
        <div className="card-header">
          <div>
            <div className="card-title">Today's Schedule</div>
            <div className="card-sub">Tap a dose to log it · Late doses show options</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            {streak>0&&<span style={{fontSize:12,fontWeight:600,color:"var(--amber)"}}>🔥 {streak}d streak</span>}
            <span style={{fontSize:14,fontWeight:700,color:pct===100?"var(--green)":pct>=80?"var(--teal)":"var(--amber)"}}>{takenCount}/{scheduled.length}</span>
          </div>
        </div>

        {/* Progress bar */}
        {scheduled.length > 0 && (
          <div style={{height:4,background:"var(--border)",borderRadius:2,marginBottom:12,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${pct}%`,background:pct===100?"var(--green)":pct>=80?"var(--teal)":"var(--amber)",borderRadius:2,transition:"width .4s ease"}}/>
          </div>
        )}

        {/* All done celebration strip */}
        {allDone && scheduled.length > 0 && (
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"rgba(74,222,128,.08)",border:"1px solid rgba(74,222,128,.25)",borderRadius:"var(--radius-sm)",marginBottom:12}}>
            <span style={{fontSize:20}}>🎉</span>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--green)"}}>All done for today!</div>
              <div style={{fontSize:12,color:"var(--text-muted)"}}>Every dose logged{streak>1?` · ${streak}-day streak — keep it up`:""}</div>
            </div>
          </div>
        )}

        {/* Next-up strip — compact, inside the schedule card */}
        {!allDone && nextUpMed && minsUntil !== null && (
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",background:"var(--teal-dim)",border:"1px solid rgba(45,212,191,.2)",borderRadius:"var(--radius-sm)",marginBottom:12}}>
            <span style={{fontSize:16}}>💊</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:11,fontWeight:600,color:"var(--teal)",textTransform:"uppercase",letterSpacing:.5}}>Next up</div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--text)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                {nextUpMed.name} <span style={{fontWeight:400,color:"var(--text-muted)"}}>{nextUpMed.dose}</span>
              </div>
            </div>
            <div style={{fontSize:13,fontWeight:600,color:"var(--teal)",flexShrink:0}}>
              {minsUntil <= 0 ? "Now" : minsUntil < 60 ? `in ${minsUntil}m` : fmt12(nextUpTime)}
            </div>
            {minsUntil <= 5 && (
              <button className="btn btn-primary btn-sm" onClick={()=>setLogModal({dose:nextUpDose, med:nextUpMed})}>Take</button>
            )}
          </div>
        )}

        {/* Empty state with trust signal */}
        {allDoses.length===0&&(
          <div className="empty-state">
            <div className="empty-icon">💊</div>
            <div className="empty-text">No doses scheduled today</div>
            <div style={{fontSize:12,color:"var(--text-muted)",marginTop:6}}>Add a medication to start tracking your schedule</div>
            <div style={{fontSize:11,color:"var(--text-dim)",marginTop:10,display:"flex",alignItems:"center",gap:5,justifyContent:"center"}}>
              <span>🔒</span><span>Your medication data is private and encrypted</span>
            </div>
          </div>
        )}
        {allDoses.map(dose=>{
          const taken = takenSet.has(dose.doseKey);
          const skipped = skippedSet.has(dose.doseKey);
          const missed = isMissed(dose);
          const shifted = !!shifts[dose.doseKey];
          const dt = displayTime(dose);

          if (dose.type==="cyclic"&&!dose.isOn) return (
            <div key={dose.doseKey} className="dose-row" style={{opacity:.45}}>
              <div className="dose-time-col"><div className="dose-time" style={{color:"var(--text-dim)"}}>OFF</div></div>
              <div className="dose-name"><div className="dose-med-name">{dose.med.name}</div><div className="dose-med-sub">Rest day · {dose.daysLeft}d until next cycle</div></div>
              <span className="tag tag-coral">Cyclic off</span>
            </div>
          );

          if (dose.type==="prn") {
            // Compute live PRN count from doseHistory so it updates immediately after each take
            const prnTakenToday = (doseHistory[dose.medId]||[]).filter(e=>e.takenAt&&(e.takenAt.startsWith(dynamicToday)||e.date===dynamicToday)&&e.doseKey.includes("-prn-")).length;
            const prnAtMax = prnTakenToday >= dose.maxPerDay;
            return (
              <div key={dose.doseKey} className="dose-row clickable" role="button" tabIndex={prnAtMax?-1:0} aria-label={`Log ${dose.med?.name} dose`} aria-disabled={prnAtMax} onClick={()=>!prnAtMax&&handleClick(dose)} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&!prnAtMax&&handleClick(dose)}>
                <div className="dose-time-col"><div className="dose-time" style={{color:"var(--amber)"}}>PRN</div><div className="dose-time-sub">as needed</div></div>
                <div className="dose-name">
                  <div className="dose-med-name">{dose.med.name} <span style={{fontWeight:400,fontSize:12,color:"var(--text-muted)"}}>{dose.med.dose}</span></div>
                  <div className="dose-med-sub">{prnTakenToday}/{dose.maxPerDay} today · min {dose.minGapH}h between</div>
                </div>
                <button className="btn btn-primary btn-sm" disabled={prnAtMax} onClick={e=>{e.stopPropagation();handleClick({...dose,takenCount:prnTakenToday});}}>
                  {prnAtMax ? "Max reached" : "Take"}
                </button>
              </div>
            );
          }

          return (
            <div key={dose.doseKey} className={`dose-row clickable ${taken||skipped?"taken":""} ${missed&&!taken&&!skipped?"missed-row":""} ${shifted&&!taken?"shifted-row":""}`} role="button" tabIndex={0} aria-label={`${dose.med?.name} ${dose.med?.dose}`} onClick={()=>handleClick(dose)} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&handleClick(dose)}>
              <div className="dose-time-col">
                <div className={`dose-time ${missed&&!taken&&!skipped?"t-missed":""} ${shifted&&!taken?"t-shifted":""}`}>{dt}</div>
                {shifted&&!taken&&<div className="dose-time-sub">shifted ⏱️</div>}
                {missed&&!taken&&!skipped&&<div className="dose-time-sub" style={{color:"var(--coral)"}}>late</div>}
              </div>
              <div style={{width:10,height:10,borderRadius:"50%",background:dose.med.color,flexShrink:0}}/>
              <div className="dose-name">
                <div className="dose-med-name">{dose.med.name} <span style={{fontWeight:400,fontSize:12,color:"var(--text-muted)"}}>{dose.med.dose}</span></div>
                <div className="dose-med-sub">
                  {dose.type==="interval"&&`Every ${dose.med.intervalHours}h`}
                  {dose.type==="times"&&`${dose.med.timesPerDay}x daily`}
                  {dose.type==="fixed"&&"Fixed time"}
                  {dose.type==="cyclic"&&`Day ${dose.daysLeft} of on-cycle`}
                  {skipped&&" · Skipped"}
                </div>
              </div>
              <div className="dose-actions">
                {!taken&&!skipped&&missed&&<button className="btn btn-warning btn-xs" onClick={e=>{e.stopPropagation();setMissedModal({dose,med:dose.med});}}>Missed?</button>}
                <div role="img" aria-label={taken?"Taken":skipped?"Skipped":"Not yet taken"} className={`check-circle ${taken?"done":""} ${missed&&!taken&&!skipped?"t-missed":""}`}>{taken?"✓":skipped?"–":""}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── INTERACTION ALERTS — below schedule, only if present ── */}
      {interactions.length>0&&(
        <div className="card" style={{borderColor:"rgba(251,191,36,.3)"}}>
          <div className="card-header"><div><div className="card-title">⚠️ Interaction Alerts</div><div className="card-sub">Review with your doctor</div></div><span style={{fontSize:11,color:"var(--amber)",fontWeight:600}}>{interactions.length} alert{interactions.length!==1?"s":""}</span></div>
          {interactions.map((a,i)=>(
            <div key={i} className="interaction-alert">
              <div className="interaction-icon">{a.severity==="moderate"?"🟡":"🟠"}</div>
              <div className="interaction-text"><div className="interaction-drugs">{a.drugs}</div><div style={{color:"var(--text-muted)"}}>{a.note}</div></div>
            </div>
          ))}
          <div style={{fontSize:11,color:"var(--text-dim)",marginTop:8,padding:"7px 10px",background:"rgba(255,255,255,0.04)",borderRadius:6,lineHeight:1.5}}>
            ℹ️ <strong>Always consult your doctor or pharmacist</strong> before making any medication changes based on these alerts.
          </div>
        </div>
      )}

      {/* ── STATS — below the schedule ── */}
      <div className="stat-grid">
        <div className="stat-card teal" style={{cursor:"pointer"}} role="button" tabIndex={0} aria-label="Active medications" onClick={()=>setTileModal("meds")} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&setTileModal("meds")}>
          <div className="stat-icon">💊</div><div className="stat-num">{meds.length}</div><div className="stat-label">Active Medications</div>
        </div>
        <div className="stat-card green" style={{cursor:"pointer"}} role="button" tabIndex={0} aria-label="Today's adherence" onClick={()=>setTileModal("adherence")} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&setTileModal("adherence")}>
          <div className="stat-icon">✅</div><div className="stat-num">{pct}%</div><div className="stat-label">Today's Adherence</div>
        </div>
        <div className="stat-card amber" style={{cursor:"pointer"}} role="button" tabIndex={0} aria-label="Doses remaining today" onClick={()=>setTileModal("remaining")} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&setTileModal("remaining")}>
          <div className="stat-icon">⏰</div><div className="stat-num">{scheduled.length-takenCount}</div><div className="stat-label">Doses Remaining</div>
        </div>
        <div className="stat-card coral" style={{cursor:"pointer"}} role="button" tabIndex={0} aria-label="Interaction alerts" onClick={()=>setTileModal("interactions")} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&setTileModal("interactions")}>
          <div className="stat-icon">⚠️</div><div className="stat-num">{interactions.length}</div><div className="stat-label">Interactions</div>
        </div>
      </div>

      {/* ── 28-DAY ADHERENCE with streak narrative ── */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Adherence History</div>
          {streak>0&&<span style={{fontSize:12,fontWeight:600,color:"var(--amber)"}}>🔥 {streak}-day streak</span>}
        </div>
        {streak===0&&meds.length>0&&<div style={{fontSize:13,color:"var(--text-muted)",marginBottom:10}}>Log doses consistently to build your streak.</div>}
        {streak===1&&<div style={{fontSize:13,color:"var(--teal)",marginBottom:10}}>Great start — first day of a new streak! Keep it going.</div>}
        {streak>=7&&streak<14&&<div style={{fontSize:13,color:"var(--teal)",marginBottom:10}}>🔥 {streak}-day streak — one week of consistent adherence!</div>}
        {streak>=14&&streak<30&&<div style={{fontSize:13,color:"var(--amber)",marginBottom:10}}>⭐ {streak}-day streak — two weeks strong. Outstanding consistency.</div>}
        {streak>=30&&<div style={{fontSize:13,color:"var(--green)",marginBottom:10}}>🏆 {streak}-day streak — a full month of perfect adherence. Exceptional!</div>}
        <AdherenceGrid meds={meds} doseHistory={doseHistory}/>
      </div>

      {missedModal&&<MissedDoseModal dose={missedModal.dose} med={missedModal.med} onResolve={handleResolve} onClose={()=>setMissedModal(null)}/>}

      {/* ── LOG CONFIRMATION MODAL ── */}
      {logModal&&(
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Log dose" onClick={e=>e.target===e.currentTarget&&setLogModal(null)}>
          <div className="modal" style={{maxWidth:400}}>
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:18}}>
              <div style={{width:44,height:44,borderRadius:"50%",background:`${logModal.med.color}22`,border:`2px solid ${logModal.med.color}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>💊</div>
              <div>
                <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18}}>{logModal.med.name}</div>
                <div style={{fontSize:12,color:"var(--text-muted)",marginTop:2}}>{logModal.med.dose} · scheduled {displayTime(logModal.dose)}</div>
              </div>
            </div>

            {/* PRIMARY: Take now — big button, this is what 95% of users want */}
            <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"13px",fontSize:15,marginBottom:10}}
              onClick={()=>{markTaken(logModal.dose);setLogModal(null);}}>
              ✅ Take now — log at {fmt12(new Date())}
            </button>

            {/* SECONDARY: less common options */}
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div className="missed-option" onClick={()=>{
                const scheduledTime = logModal.dose.plannedTime ? logModal.dose.plannedTime.toISOString() : new Date().toISOString();
                setDoseHistory(prev=>({...prev,[logModal.dose.medId]:[...(prev[logModal.dose.medId]||[]),{doseKey:logModal.dose.doseKey,takenAt:scheduledTime,date:getTodayStr()}]}));
                setMeds(prev=>prev.map(m=>m.id!==logModal.dose.medId?m:{...m,refillCount:Math.max(0,(m.refillCount??0)-1)}));
                if (logModal.dose.med?.schedType==="interval") showToast(`💊 ${logModal.med.name} taken — next dose in ${logModal.med.intervalHours}h`);
                else showToast(`💊 ${logModal.med.name} marked taken!`);
                setLogModal(null);
              }}>
                <div className="missed-option-title">🕐 Log at scheduled time ({displayTime(logModal.dose)})</div>
                <div className="missed-option-desc">Use this only if you took it on time but forgot to log it immediately.</div>
              </div>
              <div className="missed-option" onClick={()=>{
                setDoseHistory(prev=>({...prev,[logModal.dose.medId]:[...(prev[logModal.dose.medId]||[]),{doseKey:logModal.dose.doseKey,skipped:true,date:getTodayStr()}]}));
                showToast(`⏭️ ${logModal.med.name} dose skipped`);
                setLogModal(null);
              }}>
                <div className="missed-option-title">⏭️ Skip this dose</div>
                <div className="missed-option-desc">Mark as skipped. Your next scheduled dose is unaffected.</div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setLogModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── TILE DETAIL MODALS ── */}
      {tileModal&&(
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Dashboard detail" onClick={e=>e.target===e.currentTarget&&setTileModal(null)}>
          <div className="modal">

            {/* Active Medications */}
            {tileModal==="meds"&&(
              <>
                <div className="modal-title">💊 Active Medications</div>
                {meds.length===0
                  ? <div className="empty-state"><div className="empty-text">No medications added yet.</div></div>
                  : meds.map(m=>(
                    <div key={m.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"1px solid var(--border)"}}>
                      <div style={{width:10,height:10,borderRadius:"50%",background:m.color,flexShrink:0}}/>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:600,fontSize:14}}>{m.name} <span style={{fontWeight:400,fontSize:12,color:"var(--text-muted)"}}>{m.dose}</span></div>
                        <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>{m.category} · {schedLabel(m)}</div>
                      </div>
                      <span className={`tag ${schedTagColor(m.schedType)}`}>{m.refillCount} pills</span>
                    </div>
                  ))
                }
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={()=>setTileModal(null)}>Close</button>
                  <button className="btn btn-primary" onClick={()=>{setTileModal(null);setView("medications");}}>Manage Meds →</button>
                </div>
              </>
            )}

            {/* Today's Adherence */}
            {tileModal==="adherence"&&(()=>{
              // PRN meds — pull actual take times from doseHistory
              const prnMeds = meds.filter(m=>m.schedType==="prn");
              const prnRows = prnMeds.map(m=>{
                const todayTakes = (doseHistory[m.id]||[])
                  .filter(e=>e.takenAt&&(e.takenAt.startsWith(dynamicToday)||e.date===dynamicToday)&&e.doseKey.includes("-prn-"))
                  .map(e=>new Date(e.takenAt))
                  .sort((a,b)=>a-b);
                const lastTake = todayTakes.length>0 ? todayTakes[todayTakes.length-1] : null;
                const minGapMs = (m.prnMinGapHours||4)*3600000;
                const nextAvailAt = lastTake ? new Date(lastTake.getTime()+minGapMs) : null;
                const minsUntilNext = nextAvailAt ? Math.ceil((nextAvailAt-now)/60000) : 0;
                const canTakeNow = !nextAvailAt || now >= nextAvailAt;
                return { med:m, todayTakes, lastTake, nextAvailAt, minsUntilNext, canTakeNow };
              });

              return (
              <>
                <div className="modal-title">✅ Today's Adherence — {pct}%</div>

                {/* ── PRN section ── */}
                {prnRows.length>0&&(
                  <div style={{marginBottom:18}}>
                    <div style={{fontSize:11,fontWeight:700,color:"var(--amber)",textTransform:"uppercase",letterSpacing:".6px",marginBottom:8}}>
                      PRN / As-Needed Doses
                    </div>
                    {prnRows.map(({med,todayTakes,lastTake,nextAvailAt,minsUntilNext,canTakeNow})=>(
                      <div key={med.id} style={{marginBottom:12,padding:"10px 12px",background:"var(--surface-raised)",borderRadius:"var(--radius-sm)",border:"1px solid var(--border)"}}>
                        {/* Med header */}
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                          <div>
                            <span style={{fontWeight:600,fontSize:13}}>{med.name}</span>
                            <span style={{fontSize:11,color:"var(--text-muted)",marginLeft:6}}>{med.dose}</span>
                          </div>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:11,color:"var(--text-muted)"}}>{todayTakes.length}/{med.prnMaxPerDay||4} today</span>
                            {canTakeNow
                              ? <span style={{fontSize:11,fontWeight:600,color:"var(--green)",background:"rgba(74,222,128,.1)",padding:"2px 7px",borderRadius:10}}>✓ Available now</span>
                              : <span style={{fontSize:11,fontWeight:600,color:"var(--amber)",background:"rgba(251,191,36,.1)",padding:"2px 7px",borderRadius:10}}>⏳ {minsUntilNext}m</span>
                            }
                          </div>
                        </div>
                        {/* Take timeline */}
                        {todayTakes.length===0
                          ? <div style={{fontSize:12,color:"var(--text-dim)",fontStyle:"italic"}}>No doses taken yet today</div>
                          : <div style={{display:"flex",flexDirection:"column",gap:4}}>
                              {todayTakes.map((t,i)=>{
                                const prevTake = i>0 ? todayTakes[i-1] : null;
                                const gapMins = prevTake ? Math.round((t-prevTake)/60000) : null;
                                const gapOk = gapMins===null || gapMins>=(med.prnMinGapHours||4)*60;
                                return (
                                  <div key={i} style={{display:"flex",alignItems:"center",gap:8}}>
                                    <div style={{width:6,height:6,borderRadius:"50%",background:"var(--teal)",flexShrink:0}}/>
                                    <span style={{fontSize:12,fontWeight:600,color:"var(--text)",minWidth:52}}>{fmt12(t)}</span>
                                    {gapMins!==null&&(
                                      <span style={{fontSize:11,color:gapOk?"var(--text-muted)":"var(--coral)",background:gapOk?"transparent":"rgba(251,113,133,.1)",padding:gapOk?"0":"1px 5px",borderRadius:6}}>
                                        {gapOk?`+${gapMins}m gap`:`⚠ only ${gapMins}m gap`}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                        }
                        {/* Next available */}
                        {nextAvailAt&&!canTakeNow&&(
                          <div style={{marginTop:6,fontSize:11,color:"var(--text-muted)"}}>
                            Next dose available at <strong style={{color:"var(--teal)"}}>{fmt12(nextAvailAt)}</strong>
                            {" · "}{med.prnMinGapHours||4}h minimum gap
                          </div>
                        )}
                        {todayTakes.length>=(med.prnMaxPerDay||4)&&(
                          <div style={{marginTop:6,fontSize:11,fontWeight:600,color:"var(--coral)"}}>
                            ⚠ Daily maximum reached ({med.prnMaxPerDay||4} doses)
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Scheduled doses ── */}
                {scheduled.length===0&&prnRows.length===0
                  ? <div className="empty-state"><div className="empty-text">No doses scheduled today.</div></div>
                  : scheduled.length>0&&<>
                    {[
                      {label:"✓ Taken", color:"var(--green)",  doses: scheduled.filter(d=>takenSet.has(d.doseKey))},
                      {label:"— Skipped", color:"var(--text-muted)", doses: scheduled.filter(d=>skippedSet.has(d.doseKey))},
                      {label:"⚠ Missed", color:"var(--coral)", doses: scheduled.filter(d=>isMissed(d)&&!takenSet.has(d.doseKey)&&!skippedSet.has(d.doseKey))},
                      {label:"⏰ Upcoming", color:"var(--teal)", doses: scheduled.filter(d=>!takenSet.has(d.doseKey)&&!skippedSet.has(d.doseKey)&&!isMissed(d))},
                    ].map(({label,color,doses})=>doses.length>0&&(
                      <div key={label} style={{marginBottom:14}}>
                        <div style={{fontSize:11,fontWeight:700,color,textTransform:"uppercase",letterSpacing:".6px",marginBottom:6}}>{label} ({doses.length})</div>
                        {doses.map(d=>{
                          const takenEntry = (doseHistory[d.medId]||[]).find(e=>e.doseKey===d.doseKey&&e.takenAt);
                          const actualTime = takenEntry?.takenAt ? fmt12(new Date(takenEntry.takenAt)) : null;
                          return (
                            <div key={d.doseKey} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:"1px solid var(--border)",fontSize:13}}>
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                <div style={{width:7,height:7,borderRadius:"50%",background:d.med.color,flexShrink:0}}/>
                                <span style={{fontWeight:500}}>{d.med.name} <span style={{fontWeight:400,color:"var(--text-muted)"}}>{d.med.dose}</span></span>
                              </div>
                              <div style={{textAlign:"right"}}>
                                <div style={{color:"var(--text-muted)"}}>{actualTime||displayTime(d)||"—"}</div>
                                {actualTime&&displayTime(d)&&actualTime!==displayTime(d)&&(
                                  <div style={{fontSize:10,color:"var(--text-dim)"}}>sched {displayTime(d)}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </>
                }
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={()=>setTileModal(null)}>Close</button>
                </div>
              </>
              );
            })()}

            {/* Doses Remaining */}
            {tileModal==="remaining"&&(
              <>
                <div className="modal-title">⏰ Doses Remaining Today</div>
                {(() => {
                  const remaining = scheduled.filter(d=>!takenSet.has(d.doseKey)&&!skippedSet.has(d.doseKey));
                  return remaining.length===0
                    ? <div className="empty-state"><div className="empty-icon">🎉</div><div className="empty-text">All doses taken for today!</div></div>
                    : remaining.map(d=>{
                        const missed = isMissed(d);
                        return (
                          <div key={d.doseKey} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"1px solid var(--border)"}}>
                            <div style={{width:10,height:10,borderRadius:"50%",background:d.med.color,flexShrink:0}}/>
                            <div style={{flex:1}}>
                              <div style={{fontWeight:600,fontSize:14}}>{d.med.name} <span style={{fontWeight:400,fontSize:12,color:"var(--text-muted)"}}>{d.med.dose}</span></div>
                              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>{d.med.category}</div>
                            </div>
                            <div style={{textAlign:"right"}}>
                              <div style={{fontSize:13,fontWeight:700,color:missed?"var(--coral)":"var(--teal)"}}>{displayTime(d)||"—"}</div>
                              {missed&&<div style={{fontSize:10,color:"var(--coral)"}}>late</div>}
                            </div>
                          </div>
                        );
                      });
                })()}
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={()=>setTileModal(null)}>Close</button>
                </div>
              </>
            )}

            {/* Interactions */}
            {tileModal==="interactions"&&(
              <>
                <div className="modal-title">⚠️ Drug Interactions</div>
                {interactions.length===0
                  ? <div style={{padding:"16px 0",textAlign:"center"}}>
                      <div style={{fontSize:28,marginBottom:8}}>🟢</div>
                      <div style={{fontSize:14,color:"var(--text-muted)"}}>No interactions detected across your medications.</div>
                    </div>
                  : interactions.map((a,i)=>(
                    <div key={i} style={{padding:"12px 0",borderBottom:"1px solid var(--border)"}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                        <span style={{fontSize:18}}>{a.severity==="moderate"?"🟡":"🟠"}</span>
                        <span style={{fontWeight:700,fontSize:13,color:a.severity==="moderate"?"var(--amber)":"var(--coral)"}}>{a.drugs}</span>
                      </div>
                      <div style={{fontSize:12,color:"var(--text-muted)",lineHeight:1.6}}>{a.note}</div>
                      <span className={`tag ${a.severity==="moderate"?"tag-amber":"tag-coral"}`} style={{marginTop:6,display:"inline-block"}}>{a.severity} severity</span>
                    </div>
                  ))
                }
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={()=>setTileModal(null)}>Close</button>
                  <button className="btn btn-primary" onClick={()=>{setTileModal(null);setView("interactions");}}>Full Details →</button>
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
}

// ── RXNORM + FDA HELPERS ──────────────────────────────────────────────────────
async function rxNormSuggest(query) {
  if (!query || query.length < 2) return [];
  try {
    const r = await fetch(`https://rxnav.nlm.nih.gov/REST/spellingsuggestions.json?name=${encodeURIComponent(query)}`);
    const d = await r.json();
    const raw = d.suggestionGroup?.suggestionList?.suggestion?.slice(0, 12) || [];
    // Format as Title Case: "ADDERALL XR" -> "Adderall XR"
    const formatted = raw.map(s => s.split(" ").map(w =>
      w.length <= 2 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    ).join(" "));

    return formatted.slice(0, 8);
  } catch { return []; }
}

async function rxNormDoseOptions(name) {
  if (!name) return [];
  try {
    // Step 1: get RxCUI for the drug name
    const r1 = await fetch(`https://rxnav.nlm.nih.gov/REST/rxcui.json?name=${encodeURIComponent(name)}&search=1`);
    const d1 = await r1.json();
    const rxcui = d1.idGroup?.rxnormId?.[0];
    if (!rxcui) return [];

    // Step 2: fetch SBD (branded) and SCD (generic clinical) concepts
    const [r2, r3] = await Promise.all([
      fetch(`https://rxnav.nlm.nih.gov/REST/rxcui/${rxcui}/related.json?tty=SBD`),
      fetch(`https://rxnav.nlm.nih.gov/REST/rxcui/${rxcui}/related.json?tty=SCD`),
    ]);
    const [d2, d3] = await Promise.all([r2.json(), r3.json()]);

    const sbdConcepts = d2.relatedGroup?.conceptGroup?.flatMap(g => g.conceptProperties || []) || [];
    const scdConcepts = d3.relatedGroup?.conceptGroup?.flatMap(g => g.conceptProperties || []) || [];
    const concepts = sbdConcepts.length > 0 ? sbdConcepts : scdConcepts;

    // Step 3: extract TOTAL per-tablet strength from concept names.
    // Simple drug:  "Lisinopril 10 MG Oral Tablet" -> 10 mg
    // Combo drug:   "Amphetamine Aspartate 1.25 MG / Amphetamine Sulfate 1.25 MG /
    //                Amphetamine Aspartate Monohydrate 1.25 MG / ..." -> sum = 5 mg (Adderall 5)
    const strengths = new Set();
    const UNIT_RE = /([\d.]+)\s*(MG|MCG|MEQ|MMOL|ML|IU|UNIT)\b/gi;

    concepts.forEach(p => {
      if (!p.name) return;
      const matches = [...p.name.matchAll(UNIT_RE)];
      if (matches.length === 0) return;

      // Find the dominant unit (most frequent across all matches in name)
      const unitCounts = {};
      matches.forEach(m => {
        const u = m[2].toUpperCase();
        unitCounts[u] = (unitCounts[u] || 0) + 1;
      });
      const dominantUnit = Object.entries(unitCounts).sort((a,b) => b[1]-a[1])[0][0];

      // Sum all components with dominant unit — gives total tablet strength
      const total = matches
        .filter(m => m[2].toUpperCase() === dominantUnit)
        .reduce((sum, m) => sum + parseFloat(m[1]), 0);

      // Round to avoid floating-point noise (4.999999 -> 5)
      const rounded = Math.round(total * 1000) / 1000;
      if (rounded >= 0.1) strengths.add(`${rounded} ${dominantUnit.toLowerCase()}`);
    });

    return [...strengths]
      .sort((a, b) => parseFloat(a) - parseFloat(b))
      .slice(0, 12);
  } catch { return []; }
}
async function fdaMissedDoseRule(name) {
  if (!name) return null;
  try {
    const r = await fetch(`https://api.fda.gov/drug/label.json?search=openfda.generic_name:"${encodeURIComponent(name)}"&limit=1`);
    const d = await r.json();
    const text = (d.results?.[0]?.missed_dose || d.results?.[0]?.dosage_and_administration || [])[0] || "";
    if (/skip/i.test(text)) return "skip";
    if (/as soon as|asap|remember/i.test(text)) return "take_if_window";
    return null;
  } catch { return null; }
}

async function rxNormCategory(name) {
  if (!name) return "";
  try {
    const r1 = await fetch(`https://rxnav.nlm.nih.gov/REST/rxcui.json?name=${encodeURIComponent(name)}&search=1`);
    const d1 = await r1.json();
    const rxcui = d1.idGroup?.rxnormId?.[0];
    if (!rxcui) return "";
    const r2 = await fetch(`https://rxnav.nlm.nih.gov/REST/rxclass/class/byRxcui.json?rxcui=${rxcui}&relaSource=ATC`);
    const d2 = await r2.json();
    return d2.rxclassDrugInfoList?.rxclassDrugInfo?.[0]?.rxclassMinConceptItem?.className || "";
  } catch { return ""; }
}

// ── MEDICATIONS ───────────────────────────────────────────────────────────────
function Medications({ meds, setMeds, showToast, epicSession, setEpicSession }) {
  const BLANK = { name:"", dose:"", color:COLORS[0], category:"", refillCount:30, refillTotal:30, schedType:"fixed", fixedTimes:["08:00"], timesPerDay:1, timesStartHour:8, timesGapHours:null, intervalHours:8, intervalFirstTime:"08:00", intervalMaxPerDay:3, prnMaxPerDay:4, prnMinGapHours:4, missedRule:"take_if_window", cyclicOnDays:21, cyclicOffDays:7, cyclicTime:"08:00", cyclicStart:todayStr, pharmacy:"", rxNumber:"" };
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [step, setStep] = useState(1);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [doseOptions, setDoseOptions] = useState([]);
  const [manualDose, setManualDose] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingDoses, setLoadingDoses] = useState(false);
  const [loadingCategory, setLoadingCategory] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [inputMode, setInputMode] = useState("type"); // "type" | "scan"
  const debounceRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Barcode scanner using ZXing ─────────────────────────────────────────────
  const videoRef = useRef(null);
  const zxingRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = () => {
    if (zxingRef.current) {
      try { zxingRef.current.reset(); } catch {}
      zxingRef.current = null;
    }
    // Stop any lingering video tracks
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
  };

  const startBarcodeScanner = async () => {
    setScanning(true);
    try {
      // Dynamically load ZXing from CDN — try multiple sources
      if (!window.ZXing) {
        const cdnUrls = [
          "https://cdn.jsdelivr.net/npm/@zxing/library@0.21.3/umd/index.min.js",
          "https://unpkg.com/@zxing/library@0.21.3/umd/index.min.js",
          "https://cdn.jsdelivr.net/npm/@zxing/library@0.20.0/umd/index.min.js",
        ];
        let loaded = false;
        for (const url of cdnUrls) {
          try {
            await new Promise((res, rej) => {
              const s = document.createElement("script");
              s.src = url;
              s.onload = () => { loaded = true; res(); };
              s.onerror = rej;
              s.timeout = 5000;
              document.head.appendChild(s);
            });
            if (loaded) break;
          } catch { continue; }
        }
        if (!loaded || !window.ZXing) throw new Error("ZXing library failed to load");
      }
      // Give React a tick to render the video element
      await new Promise(r => setTimeout(r, 150));
      const codeReader = new window.ZXing.BrowserMultiFormatReader();
      zxingRef.current = codeReader;

      // decodeFromVideoDevice handles camera start + continuous scanning
      // Pass null as deviceId to use default (rear) camera
      await codeReader.decodeFromVideoDevice(null, videoRef.current, async (result, err) => {
        if (!result) return;
        stopCamera();
        setScanning(false);

        const raw = result.getText();
        console.log("Barcode raw:", raw);
        console.log("Barcode format:", result.getBarcodeFormat?.());

        // Show raw string in toast so we can debug without DevTools
        showToast(`🔍 Raw: ${raw.slice(0,60)}${raw.length>60?"…":""}`);

        let rxNumber = null, ndcCode = null, quantity = null, pharmacyName = null;

        // ── Strategy 1: GS1 with parenthesized AIs e.g. (01)12345(10)RX123
        const gs1Rx  = raw.match(/\(10\)([^\(]+)/);
        const gs1Ndc = raw.match(/\(01\)(\d{13,14})/);
        const gs1Qty = raw.match(/\(30\)(\d+)/);
        if (gs1Rx)  rxNumber = gs1Rx[1].trim();
        if (gs1Ndc) ndcCode  = gs1Ndc[1].replace(/^0+/,"").slice(0,10);
        if (gs1Qty) quantity = parseInt(gs1Qty[1]);

        // ── Strategy 2: GS1 with raw FNC1 / ASCII group separator (no parens)
        // Fields prefixed by 2-digit AI codes in sequence
        if (!rxNumber && !ndcCode) {
          const clean = raw.replace(/[]/g, "|"); // replace control chars
          const ai01 = clean.match(/01(\d{14})/);
          const ai10 = clean.match(/10([^|]+)/);
          const ai30 = clean.match(/30(\d+)/);
          if (ai01) ndcCode  = ai01[1].slice(1,11); // digits 2-11 = NDC
          if (ai10) rxNumber = ai10[1].trim();
          if (ai30) quantity = parseInt(ai30[1]);
        }

        // ── Strategy 3: CVS proprietary format
        // CVS barcodes often: "CVS" + Rx# + NDC + qty separated by spaces or |
        if (!rxNumber && raw.toUpperCase().includes("CVS")) {
          pharmacyName = "CVS";
          const parts = raw.split(/[\s|,]+/);
          const rxPart = parts.find(p => /^\d{7,12}$/.test(p));
          if (rxPart) rxNumber = rxPart;
          const ndcPart = parts.find(p => /^\d{10,11}$/.test(p));
          if (ndcPart) ndcCode = ndcPart.slice(0,10);
        }

        // ── Strategy 4: plain numeric — just an Rx number
        if (!rxNumber && /^\d{7,12}$/.test(raw.trim())) {
          rxNumber = raw.trim();
        }

        // ── Strategy 5: delimited key=value or positional fields
        if (!rxNumber && raw.includes("RX")) {
          const rxMatch = raw.match(/RX[:\s#]*(\d{6,12})/i);
          if (rxMatch) rxNumber = rxMatch[1];
        }
        if (!ndcCode && raw.includes("NDC")) {
          const ndcMatch = raw.match(/NDC[:\s]*(\d[\d-]{9,12})/i);
          if (ndcMatch) ndcCode = ndcMatch[1].replace(/-/g,"").slice(0,10);
        }

        // Pharmacy from prefix
        const upperRaw = raw.toUpperCase();
        if (!pharmacyName) {
          if (upperRaw.startsWith("CVS") || upperRaw.includes("CVS")) pharmacyName = "CVS";
          else if (upperRaw.startsWith("WAG") || upperRaw.includes("WALGREENS")) pharmacyName = "Walgreens";
          else if (upperRaw.startsWith("WMT") || upperRaw.includes("WALMART")) pharmacyName = "Walmart";
          else if (upperRaw.includes("RITE AID") || upperRaw.includes("RITEAID")) pharmacyName = "RiteAid";
          else if (upperRaw.includes("COSTCO")) pharmacyName = "Costco";
        }

        console.log("Parsed → rxNumber:", rxNumber, "ndcCode:", ndcCode, "qty:", quantity, "pharmacy:", pharmacyName);

        // ── NDC → FDA drug lookup
        let drugName = null, drugStrength = null;
        if (ndcCode) {
          try {
            // Try multiple NDC formats: 5-4-1, 5-3-2, 4-4-2
            const formats = [
              `${ndcCode.slice(0,5)}-${ndcCode.slice(5,9)}-${ndcCode.slice(9)}`,
              `${ndcCode.slice(0,5)}-${ndcCode.slice(5,8)}-${ndcCode.slice(8)}`,
              ndcCode,
            ];
            for (const fmt of formats) {
              const fdaRes = await fetch(`https://api.fda.gov/drug/ndc.json?search=product_ndc:"${fmt}"&limit=1`);
              const fdaData = await fdaRes.json();
              const product = fdaData.results?.[0];
              if (product) {
                drugName = product.generic_name || product.brand_name;
                const strength = product.active_ingredients?.[0];
                if (strength) drugStrength = strength.strength;
                break;
              }
            }
          } catch (e) { console.warn("NDC lookup failed:", e); }
        }

        // Populate form
        const updates = {};
        if (drugName) updates.name = drugName.split(" ").map(w => w.charAt(0).toUpperCase()+w.slice(1).toLowerCase()).join(" ");
        if (drugStrength) updates.dose = drugStrength;
        if (rxNumber) updates.rxNumber = rxNumber;
        if (pharmacyName) updates.pharmacy = pharmacyName;
        if (quantity) { updates.refillCount = quantity; updates.refillTotal = quantity; }

        setForm(f => ({...f, ...updates}));

        // Auto-fetch RxNorm dose options and category if we got a name
        if (updates.name) {
          setLoadingDoses(true); setLoadingCategory(true);
          try {
            const [doses, cat, rule] = await Promise.all([
              rxNormDoseOptions(updates.name),
              rxNormCategory(updates.name),
              fdaMissedDoseRule(updates.name),
            ]);
            setDoseOptions(doses);
            setForm(f => ({
              ...f,
              dose: f.dose || doses[0] || "",
              category: cat || f.category,
              missedRule: rule || f.missedRule,
            }));
          } catch {}
          setLoadingDoses(false); setLoadingCategory(false);
        }

        setInputMode("type");
        const found = [updates.name, updates.dose, rxNumber && `Rx #${rxNumber}`].filter(Boolean).join(" · ");
        showToast(`✅ Barcode scanned${found ? ` — ${found}` : ""}`);
      });
    } catch (e) {
      console.error("Barcode scanner error:", e.name, e.message);
      stopCamera();
      setScanning(false);
      if (e.name === "NotAllowedError") {
        showToast("⚠️ Camera access denied — please allow camera in browser settings");
      } else if (e.message?.includes("ZXing")) {
        showToast("⚠️ Scanner library failed to load — check your connection and try again");
      } else if (e.name === "NotFoundError") {
        showToast("⚠️ No camera found on this device");
      } else if (e.name === "NotSupportedError") {
        showToast("⚠️ Camera not supported in this browser — try Chrome or Safari");
      } else {
        showToast("⚠️ Scanner error — please enter details manually");
      }
      setInputMode("type");
    }
  };

  // Clean up camera when modal closes
  useEffect(() => {
    if (!scanning) stopCamera();
  }, [scanning]);

  const openAdd = () => { setEditId(null); setForm(BLANK); setStep(1); setShowAdvanced(false); setManualDose(false); setNameSuggestions([]); setDoseOptions([]); setInputMode("type"); setShowModal(true); };
  const openEdit = (m) => { setEditId(m.id); setForm({...m}); setStep(1); setShowAdvanced(false); setManualDose(false); setNameSuggestions([]); setDoseOptions([]); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setStep(1); setShowAdvanced(false); setManualDose(false); setNameSuggestions([]); setDoseOptions([]); setInputMode("type"); setScanning(false); };

  const save = () => {
    if (!form.name || !form.dose) return;
    if (editId) {
      setMeds(p => p.map(m => m.id === editId ? {...form, id: editId} : m));
      showToast("✅ Medication updated");
    } else {
      setMeds(p => [...p, {...form, id: Date.now()}]);
      showToast("✅ Medication added");
    }
    closeModal();
  };

  const handleNameChange = (val) => {
    setForm(f => ({...f, name: val, dose: ""}));
    setDoseOptions([]);
    clearTimeout(debounceRef.current);
    if (val.length < 2) { setNameSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      const s = await rxNormSuggest(val);
      setNameSuggestions(s);
      setShowSuggestions(true);
    }, 320);
  };

  const selectSuggestion = async (name) => {
    setForm(f => ({...f, name, dose: ""}));
    setNameSuggestions([]); setShowSuggestions(false);
    setManualDose(false);
    setLoadingDoses(true); setLoadingCategory(true);
    const [doses, cat, rule] = await Promise.all([rxNormDoseOptions(name), rxNormCategory(name), fdaMissedDoseRule(name)]);
    setDoseOptions(doses);
    setForm(f => ({...f, name, dose: doses[0] || f.dose, category: cat || f.category, missedRule: rule || f.missedRule}));
    setLoadingDoses(false); setLoadingCategory(false);
  };

  const SIMPLE_SCHEDS = [
    { id:"fixed",  icon:"🕐", title:"Set times",     desc:"e.g. 8am and 9pm every day" },
    { id:"times",  icon:"📅", title:"Times per day", desc:"Auto-spaced across waking hours" },
    { id:"prn",    icon:"💡", title:"As needed",     desc:"Take when required, up to a daily max" },
  ];
  const ADVANCED_SCHEDS = [
    { id:"interval", icon:"⏱️", title:"Every N hours",   desc:"Strict gap from last actual dose" },
    { id:"cyclic",   icon:"🔄", title:"Cyclic / on-off", desc:"X days on, Y days off" },
  ];

  return (
    <div>
      <div className="page-header" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div><h1 className="page-title">My Medications</h1><div className="page-desc">{meds.length} active prescriptions</div></div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Medication</button>
      </div>

      <EpicImportPanel meds={meds} setMeds={setMeds} showToast={showToast} epicSession={epicSession} setEpicSession={setEpicSession}/>

      <div className="med-list">
        {meds.map(m=>(
          <div key={m.id} className={`med-card ${m.epicImported?"epic-imported":""}`} style={{cursor:"pointer"}} role="button" tabIndex={0} aria-label={`Edit ${m.name}`} onClick={()=>openEdit(m)} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&openEdit(m)}>
            <div className="med-dot" style={{background:m.color}}/>
            <div className="med-info">
              <div className="med-name">{m.name} <span style={{fontWeight:400,fontSize:13,color:"var(--text-muted)"}}>{m.dose}</span></div>
              <div className="med-sched">{schedLabel(m)}</div>
              <div className="med-tags">
                <span className={`tag ${schedTagColor(m.schedType)}`}>{SCHED_TYPES.find(s=>s.id===m.schedType)?.title}</span>
                <span className="tag tag-amber">{m.refillCount} pills</span>
                {m.epicImported&&<span className="tag tag-purple">Epic Import</span>}
                {m.missedRule==="skip"&&<span className="tag tag-coral">Skip if missed</span>}
                {m.missedRule==="take_if_window"&&<span className="tag tag-green">4h window</span>}
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6,flexShrink:0}}>
              <button className="btn btn-secondary btn-sm" onClick={e=>{e.stopPropagation();openEdit(m);}}>Edit</button>
              <button className="btn btn-danger btn-sm" onClick={e=>{e.stopPropagation();setMeds(p=>p.filter(x=>x.id!==m.id));showToast("🗑️ Removed");}}>Remove</button>
            </div>
          </div>
        ))}
        {meds.length===0&&(
          <div className="empty-state" style={{padding:"48px 24px"}}>
            <div className="empty-icon">💊</div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:"var(--text)",marginBottom:8}}>Add your first medication</div>
            <div className="empty-text" style={{maxWidth:280,margin:"0 auto 20px",lineHeight:1.6}}>Veriva™ tracks your schedule, checks for interactions, and reminds you when it's time to take each dose.</div>
            <button className="btn btn-primary" onClick={()=>setShowModal(true)}>+ Add a medication</button>
            <div style={{fontSize:11,color:"var(--text-dim)",marginTop:16}}>🔒 Your medication list is private and encrypted</div>
          </div>
        )}
      </div>

      {showModal&&(
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={editId?"Edit medication":"Add medication"} onClick={e=>e.target===e.currentTarget&&closeModal()}>
          <div className="modal">
            <div className="step-indicator">
              {["Basics","Schedule","Confirm"].map((label,i)=>{
                const n=i+1, state=n<step?"done":n===step?"active":"pending";
                return [i>0&&<div key={`l${i}`} className={`step-line ${n<=step?"done":""}`}/>, <div key={label} className={`step-dot ${state}`}>{n<step?"✓":n}</div>];
              })}
            </div>

            {step===1&&(
              <>
                <div className="modal-title">{editId?"Edit medication":"What medication?"}</div>

                {/* ── Input mode toggle — only show on new add, not edit ── */}
                {!editId && (
                  <div style={{display:"flex",gap:0,marginBottom:18,border:"1px solid var(--border)",borderRadius:"var(--radius-sm)",overflow:"hidden"}}>
                    {[["type","⌨️ Type manually"],["scan","▦ Scan barcode"]].map(([mode,label])=>(
                      <button key={mode} onClick={()=>setInputMode(mode)}
                        style={{flex:1,padding:"9px 0",fontSize:13,fontWeight:500,cursor:"pointer",
                          background:inputMode===mode?"var(--teal)":"transparent",
                          color:inputMode===mode?"#0d1117":"var(--text-muted)",
                          border:"none",fontFamily:"DM Sans,sans-serif",transition:"all .14s"}}>
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                {/* ── Scan mode ── */}
                {inputMode==="scan" && !editId && (
                  <div style={{marginBottom:16}}>
                    {!scanning ? (
                      <div style={{border:"2px dashed var(--border)",borderRadius:"var(--radius)",padding:"32px 20px",textAlign:"center",cursor:"pointer",transition:"border-color .14s"}}
                        onClick={startBarcodeScanner}
                        onMouseEnter={e=>e.currentTarget.style.borderColor="var(--teal)"}
                        onMouseLeave={e=>e.currentTarget.style.borderColor="var(--border)"}>
                        <div style={{fontSize:44,marginBottom:10}}>▦</div>
                        <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>Scan prescription barcode</div>
                        <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:16,lineHeight:1.6}}>
                          Point your camera at the <strong>Scan to Refill</strong> barcode<br/>
                          on your CVS, Walgreens, or other pharmacy bottle.<br/>
                          Drug name, dose, Rx number and quantity fill in automatically.
                        </div>
                        <button className="btn btn-primary" onClick={e=>{e.stopPropagation();startBarcodeScanner();}}>
                          📷 Open Scanner
                        </button>
                      </div>
                    ) : (
                      <div style={{position:"relative",borderRadius:"var(--radius)",overflow:"hidden",background:"#000"}}>
                        <video ref={videoRef} autoPlay playsInline muted
                          style={{width:"100%",maxHeight:260,objectFit:"cover",display:"block"}}/>
                        {/* Targeting overlay */}
                        <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
                          <div style={{width:220,height:100,border:"2px solid var(--teal)",borderRadius:8,boxShadow:"0 0 0 2000px rgba(0,0,0,0.45)"}}>
                            <div style={{position:"absolute",top:-1,left:-1,width:20,height:20,borderTop:"3px solid var(--teal)",borderLeft:"3px solid var(--teal)",borderRadius:"4px 0 0 0"}}/>
                            <div style={{position:"absolute",top:-1,right:-1,width:20,height:20,borderTop:"3px solid var(--teal)",borderRight:"3px solid var(--teal)",borderRadius:"0 4px 0 0"}}/>
                            <div style={{position:"absolute",bottom:-1,left:-1,width:20,height:20,borderBottom:"3px solid var(--teal)",borderLeft:"3px solid var(--teal)",borderRadius:"0 0 0 4px"}}/>
                            <div style={{position:"absolute",bottom:-1,right:-1,width:20,height:20,borderBottom:"3px solid var(--teal)",borderRight:"3px solid var(--teal)",borderRadius:"0 0 4px 0"}}/>
                          </div>
                        </div>
                        <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"10px",textAlign:"center",background:"linear-gradient(transparent,rgba(0,0,0,.7))"}}>
                          <div style={{fontSize:12,color:"#fff"}}>Align barcode within the frame</div>
                        </div>
                        <button className="btn btn-secondary btn-sm"
                          style={{position:"absolute",top:8,right:8}}
                          onClick={()=>{stopCamera();setScanning(false);setInputMode("type");}}>
                          Cancel
                        </button>
                      </div>
                    )}
                    <div style={{textAlign:"center",marginTop:12}}>
                      <span style={{fontSize:12,color:"var(--text-muted)",cursor:"pointer",textDecoration:"underline"}}
                        onClick={()=>{stopCamera();setScanning(false);setInputMode("type");}}>
                        Enter manually instead
                      </span>
                    </div>
                  </div>
                )}

                {/* ── Type mode (always show on edit, or when inputMode=type) ── */}
                {(inputMode==="type" || editId) && (
                <>
                <div className="form-group" style={{position:"relative"}}>
                  <label className="form-label">Medication name *</label>
                  <input className="form-input" placeholder="Start typing — e.g. Lisinopril" value={form.name} autoFocus autoComplete="off"
                    onChange={e=>handleNameChange(e.target.value)}
                    onBlur={()=>setTimeout(()=>setShowSuggestions(false),180)}
                    onFocus={()=>nameSuggestions.length>0&&setShowSuggestions(true)}
                  />
                  {showSuggestions&&nameSuggestions.length>0&&(
                    <div style={{position:"absolute",top:"100%",left:0,right:0,background:"var(--surface)",border:"1px solid var(--teal)",borderRadius:"var(--radius-sm)",zIndex:500,overflow:"hidden",marginTop:2}}>
                      {nameSuggestions.map(s=>(
                        <div key={s} style={{padding:"9px 13px",cursor:"pointer",fontSize:13,borderBottom:"1px solid var(--border)"}}
                          onMouseDown={()=>selectSuggestion(s)}
                          onMouseEnter={e=>e.currentTarget.style.background="var(--teal-dim)"}
                          onMouseLeave={e=>e.currentTarget.style.background=""}
                        >{s}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Dose strength * {loadingDoses&&<span style={{marginLeft:6,fontSize:10,color:"var(--teal)"}}>loading…</span>}</label>
                    {doseOptions.length>0 && !manualDose
                      ? <>
                          <select className="form-input" value={form.dose} onChange={e=>{
                            if (e.target.value === "__manual__") {
                              setManualDose(true);
                              setForm(f=>({...f, dose:""}));
                            } else {
                              setForm(f=>({...f, dose:e.target.value}));
                            }
                          }}>
                            <option value="" disabled>Select a dose…</option>
                            {doseOptions.map(d=><option key={d} value={d}>{d}</option>)}
                            <option value="__manual__">Enter manually…</option>
                          </select>
                        </>
                      : <div style={{display:"flex",gap:6}}>
                          <input className="form-input" placeholder="Amount" type="number" min="0.1" step="0.1"
                            value={form.dose ? form.dose.replace(/[^0-9.]/g,"") : ""}
                            onChange={e => {
                              const num = e.target.value;
                              const unit = form.dose?.match(/[a-zA-Z%]+/)?.[0] || "mg";
                              setForm(f=>({...f, dose: num ? `${num} ${unit}` : ""}));
                            }}
                            autoFocus={manualDose}
                            style={{flex:1}}
                          />
                          <select
                            className="form-input"
                            style={{width:90,flexShrink:0}}
                            value={form.dose?.match(/[a-zA-Z%]+/)?.[0] || "mg"}
                            onChange={e => {
                              const num = form.dose?.replace(/[^0-9.]/g,"") || "";
                              setForm(f=>({...f, dose: num ? `${num} ${e.target.value}` : ""}));
                            }}
                          >
                            <option value="mg">mg</option>
                            <option value="mcg">mcg</option>
                            <option value="ml">ml</option>
                            <option value="g">g</option>
                            <option value="IU">IU</option>
                            <option value="%">%</option>
                          </select>
                          {doseOptions.length>0 && manualDose && (
                            <button className="btn btn-secondary btn-sm" style={{flexShrink:0}} onClick={()=>{setManualDose(false);setForm(f=>({...f,dose:doseOptions[0]}));}}>
                              List
                            </button>
                          )}
                        </div>
                    }
                  </div>
                  <div className="form-group">
                    <label className="form-label">Pills in bottle</label>
                    <input className="form-input" type="number" min="1" value={form.refillCount}
                      onChange={e=>{
                        const v=e.target.value;
                        if(v===""){ setForm(f=>({...f,refillCount:"",refillTotal:""})); return; }
                        const n=parseInt(v);
                        if(!isNaN(n)&&n>0) setForm(f=>({...f,refillCount:n,refillTotal:n}));
                      }}
                      onBlur={()=>{ if(!form.refillCount||form.refillCount<1) setForm(f=>({...f,refillCount:30,refillTotal:30})); }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Color label</label>
                  <div className="color-swatches">{COLORS.map(c=><div key={c} className={`swatch ${form.color===c?"selected":""}`} style={{background:c}} onClick={()=>setForm(f=>({...f,color:c}))}/>)}</div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Pharmacy <span style={{fontWeight:400,color:"var(--text-dim)"}}>optional</span></label>
                    <select className="form-input" value={form.pharmacy||""} onChange={e=>setForm(f=>({...f,pharmacy:e.target.value}))}>
                      <option value="">None selected</option>
                      {["CVS","Walgreens","Walmart","Costco","Amazon","Other"].map(p=><option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rx Number <span style={{fontWeight:400,color:"var(--text-dim)"}}>optional</span></label>
                    <input className="form-input" placeholder="e.g. 7654321" value={form.rxNumber||""} onChange={e=>setForm(f=>({...f,rxNumber:e.target.value}))}/>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                  <button className="btn btn-primary" disabled={!form.name||!form.dose||form.dose==="__manual__"} onClick={()=>setStep(2)}>Next →</button>
                </div>
                </>
                )}
              </>
            )}

            {step===2&&(
              <>
                <div className="modal-title">How do you take it?</div>
                <div className="stype-grid">
                  {SIMPLE_SCHEDS.map(s=>(
                    <div key={s.id} className={`stype-card ${form.schedType===s.id?"selected":""}`} onClick={()=>setForm(f=>({...f,schedType:s.id}))}>
                      <div className="stype-icon">{s.icon}</div><div className="stype-title">{s.title}</div><div className="stype-desc">{s.desc}</div>
                    </div>
                  ))}
                </div>
                <div className="advanced-toggle" role="button" tabIndex={0} onClick={()=>setShowAdvanced(v=>!v)}>
                  <span style={{flex:1}}>Advanced schedules</span>
                  <span style={{fontSize:11,color:"var(--text-dim)"}}>Interval · Cyclic</span>
                  <span className={`advanced-chevron ${showAdvanced?"open":""}`}>▼</span>
                </div>
                {showAdvanced&&(
                  <div className="stype-grid" style={{marginTop:8}}>
                    {ADVANCED_SCHEDS.map(s=>(
                      <div key={s.id} className={`stype-card ${form.schedType===s.id?"selected":""}`} onClick={()=>setForm(f=>({...f,schedType:s.id}))}>
                        <div className="stype-icon">{s.icon}</div><div className="stype-title">{s.title}</div><div className="stype-desc">{s.desc}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{marginTop:14}}>
                  {form.schedType==="fixed"&&(
                    <div className="form-group">
                      <label className="form-label">Dose times</label>
                      {(form.fixedTimes||["08:00"]).map((time,i)=>(
                        <div key={i} style={{display:"flex",gap:8,marginBottom:6}}>
                          <input className="form-input" type="time" value={time} onChange={e=>{const a=[...(form.fixedTimes||["08:00"])];a[i]=e.target.value;setForm(f=>({...f,fixedTimes:a}));}}/>
                          {i>0&&<button className="btn btn-danger btn-sm" onClick={()=>setForm(f=>({...f,fixedTimes:f.fixedTimes.filter((_,j)=>j!==i)}))}>×</button>}
                        </div>
                      ))}
                      {(form.fixedTimes||[]).length<4&&<button className="btn btn-secondary btn-sm" onClick={()=>setForm(f=>({...f,fixedTimes:[...(f.fixedTimes||["08:00"]),"12:00"]}))}>+ Add time</button>}
                    </div>
                  )}
                  {form.schedType==="times"&&(
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Times per day</label>
                        <select className="form-input" value={form.timesPerDay||1} onChange={e=>setForm(f=>({...f,timesPerDay:parseInt(e.target.value)}))}>
                          {[1,2,3,4,5,6].map(n=><option key={n} value={n}>{n}x per day</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">First dose hour</label>
                        <select className="form-input" value={form.timesStartHour||8} onChange={e=>setForm(f=>({...f,timesStartHour:parseInt(e.target.value)}))}>
                          {Array.from({length:16},(_,i)=>i+6).map(h=><option key={h} value={h}>{String(h).padStart(2,"0")}:00</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                  {form.schedType==="prn"&&(
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Max doses per day</label>
                        <select className="form-input" value={form.prnMaxPerDay||4} onChange={e=>setForm(f=>({...f,prnMaxPerDay:parseInt(e.target.value)}))}>
                          {[1,2,3,4,5,6,8].map(n=><option key={n} value={n}>{n} per day</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Min hours between doses</label>
                        <select className="form-input" value={form.prnMinGapHours||4} onChange={e=>setForm(f=>({...f,prnMinGapHours:parseInt(e.target.value)}))}>
                          {[1,2,3,4,6,8].map(h=><option key={h} value={h}>{h}h minimum</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                  {form.schedType==="interval"&&(
                    <div>
                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Interval (hours)</label>
                          <select className="form-input" value={form.intervalHours||8} onChange={e=>setForm(f=>({...f,intervalHours:parseInt(e.target.value)}))}>
                            {[2,3,4,6,8,12,24].map(h=><option key={h} value={h}>Every {h}h</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label className="form-label">First dose time</label>
                          <input className="form-input" type="time" value={form.intervalFirstTime||"08:00"} onChange={e=>setForm(f=>({...f,intervalFirstTime:e.target.value}))}/>
                        </div>
                      </div>
                      <div className="info-box">⏱️ Taking a dose late automatically pushes subsequent doses to maintain the {form.intervalHours||8}h gap.</div>
                    </div>
                  )}
                  {form.schedType==="cyclic"&&(
                    <div>
                      <div className="form-row">
                        <div className="form-group"><label className="form-label">Days ON</label><input className="form-input" type="number" min={1} max={90} value={form.cyclicOnDays||21} onChange={e=>setForm(f=>({...f,cyclicOnDays:parseInt(e.target.value)||21}))}/></div>
                        <div className="form-group"><label className="form-label">Days OFF</label><input className="form-input" type="number" min={1} max={90} value={form.cyclicOffDays||7} onChange={e=>setForm(f=>({...f,cyclicOffDays:parseInt(e.target.value)||7}))}/></div>
                      </div>
                      <div className="form-row">
                        <div className="form-group"><label className="form-label">Dose time</label><input className="form-input" type="time" value={form.cyclicTime||"08:00"} onChange={e=>setForm(f=>({...f,cyclicTime:e.target.value}))}/></div>
                        <div className="form-group"><label className="form-label">Cycle start</label><input className="form-input" type="date" value={form.cyclicStart||todayStr} onChange={e=>setForm(f=>({...f,cyclicStart:e.target.value}))}/></div>
                      </div>
                    </div>
                  )}
                  {form.schedType!=="interval"&&form.schedType!=="prn"&&form.schedType!=="cyclic"&&(
                    <div>
                      <div className="modal-section">Missed dose rule</div>
                      <select className="form-input" value={form.missedRule||"take_if_window"} onChange={e=>setForm(f=>({...f,missedRule:e.target.value}))}>
                        <option value="take_if_window">Take ASAP if within 4 hours, otherwise skip</option>
                        <option value="skip">Always skip — resume next scheduled dose</option>
                        <option value="always_take">Always take when remembered</option>
                      </select>
                      <div className="form-hint">Auto-filled from FDA label where available.</div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={()=>setStep(1)}>← Back</button>
                  <button className="btn btn-primary" onClick={()=>setStep(3)}>Review →</button>
                </div>
              </>
            )}

            {step===3&&(
              <>
                <div className="modal-title">{editId?"Confirm changes":"Confirm medication"}</div>
                <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"16px 18px",marginBottom:16}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                    <div style={{width:16,height:16,borderRadius:"50%",background:form.color,flexShrink:0}}/>
                    <span style={{fontWeight:700,fontSize:17}}>{form.name}</span>
                    <span style={{color:"var(--text-muted)",fontSize:14}}>{form.dose}</span>
                  </div>
                  {[
                    ["Schedule", schedLabel(form)],
                    ["Refill count", `${form.refillCount} pills`],
                    ["Missed dose rule", form.missedRule==="skip"?"Always skip":form.missedRule==="take_if_window"?"Take if within 4 hours":"Always take when remembered"],
                  ].filter(Boolean).map(([k,v])=>(
                    <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderTop:"1px solid var(--border)",fontSize:13}}>
                      <span style={{color:"var(--text-muted)"}}>{k}</span><span style={{fontWeight:500}}>{v}</span>
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={()=>setStep(2)}>← Back</button>
                  <button className="btn btn-primary" onClick={save}>{editId?"✓ Save Changes":"✓ Add Medication"}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ── INTERACTIONS ──────────────────────────────────────────────────────────────
function Interactions({ meds, interactions, interactionsLoading }) {
  return (
    <div>
      <div className="page-header"><h1 className="page-title">Drug Interactions</h1><div className="page-desc">Live check via NLM RxNorm · {interactionsLoading ? "Checking…" : `${interactions.length} interaction${interactions.length!==1?"s":""} found`}</div></div>
      {interactionsLoading && <div style={{textAlign:"center",padding:"32px",color:"var(--text-muted)",fontSize:13}}><div className="spinner" style={{margin:"0 auto 10px"}}/> Checking interactions…</div>}
      {!interactionsLoading && interactions.length===0
        ? <div className="card">
            <div className="empty-state" style={{padding:"40px 24px"}}>
              <div className="empty-icon">🟢</div>
              <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:"var(--text)",marginBottom:8}}>
                {meds.length < 2 ? "Add more medications to check interactions" : "No interactions detected"}
              </div>
              <div className="empty-text" style={{maxWidth:300,margin:"0 auto",lineHeight:1.6}}>
                {meds.length < 2
                  ? "Interaction checking requires at least two medications. Add your full medication list for a complete safety check."
                  : "Your current medications have no known interactions. This is checked automatically whenever you update your list."}
              </div>
            </div>
          </div>
        : !interactionsLoading && (<>
          {interactions.map((a,i)=>(
            <div key={i} className="interaction-alert" style={{marginBottom:12}}>
              <div className="interaction-icon">{a.severity==="high"?"🔴":a.severity==="moderate"?"🟡":"🟠"}</div>
              <div className="interaction-text">
                <div className="interaction-drugs">{a.drugs}</div>
                <div style={{color:"var(--text-muted)",marginBottom:6}}>{a.note}</div>
                <span className={`tag ${a.severity==="high"?"tag-coral":a.severity==="moderate"?"tag-amber":"tag-teal"}`}>{a.severity} severity</span>
              </div>
            </div>
          ))}
          <div style={{fontSize:12,color:"var(--text-dim)",marginTop:4,padding:"10px 13px",background:"rgba(255,255,255,0.04)",borderRadius:8,lineHeight:1.6,borderLeft:"3px solid var(--amber)"}}>
            <strong>⚕️ Medical Disclaimer:</strong> These alerts are for informational purposes only. Always consult your doctor or pharmacist before making any medication changes. Do not stop, start, or adjust doses based solely on this information.
          </div>
        </>)
      }
    </div>
  );
}

// ── SYMPTOMS ──────────────────────────────────────────────────────────────────
function Symptoms({ symptoms, setSymptoms, meds, showToast, user }) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({text:"",severity:2,med:""});
  const save = () => {
    if (!form.text) return;
    const newSymptom = {...form, id:Date.now(), date:getTodayStr()};
    setSymptoms(p=>[newSymptom,...p]);
    // Persist to Supabase
    if (user) {
      const symInsertDate = getTodayStr();
      supabase.from("symptoms").insert({
        user_id: user.id,
        text: form.text,
        severity: form.severity,
        date: symInsertDate,
        notes: form.med || null,
      }).select().then(({ data, error }) => {
        if (error) { console.error("Side effect save error:", error.message); return; }
        if (data?.[0]?.id) {
          setSymptoms(p => p.map(s => s.id === newSymptom.id ? {...s, id: data[0].id} : s));
        }
      });
    }
    setShowModal(false); setForm({text:"",severity:2,med:""}); showToast("📝 Side effect logged");
  };
  const remove = (s) => {
    setSymptoms(p=>p.filter(x=>x.id!==s.id));
    if (user && typeof s.id === "string" && s.id.includes("-")) {
      supabase.from("symptoms").delete().eq("id", s.id).then(({ error }) => {
        if (error) console.error("Symptom delete error:", error.message);
      });
    }
  };
  return (
    <div>
      <div className="page-header" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div><h1 className="page-title">Side Effects</h1><div className="page-desc">Track medication side effects and reactions</div></div>
        <button className="btn btn-primary" onClick={()=>setShowModal(true)}>+ Log Side Effect</button>
      </div>
      {symptoms.length===0?<div className="empty-state" style={{padding:"48px 24px"}}>
          <div className="empty-icon">📋</div>
          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:"var(--text)",marginBottom:8}}>Track how your medications make you feel</div>
          <div className="empty-text" style={{maxWidth:300,margin:"0 auto 20px",lineHeight:1.6}}>Log side effects or reactions after taking a dose. Over time, Veriva™ can help you spot patterns between your medications and your symptoms.</div>
          <button className="btn btn-primary" onClick={()=>setShowModal(true)}>+ Log a side effect</button>
          <div style={{fontSize:11,color:"var(--text-dim)",marginTop:16}}>Symptoms are only visible to you and caregivers you invite</div>
        </div>
        :symptoms.map(s=><div key={s.id} className="symptom-entry">
          <div><div style={{fontSize:14}}>{s.text}</div><div style={{fontSize:11,color:"var(--text-muted)",marginTop:3}}>{s.date}{s.med&&` · ${s.med}`}</div><SeverityDots level={s.severity}/></div>
          <button className="btn btn-danger btn-sm" onClick={()=>remove(s)}>Remove</button>
        </div>)
      }
      {showModal&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal">
            <div className="modal-title">Log a Side Effect</div>
            <div className="form-group"><label className="form-label">Describe the side effect or reaction</label><input className="form-input" placeholder="e.g. Nausea after morning dose" value={form.text} onChange={e=>setForm(f=>({...f,text:e.target.value}))}/></div>
            <div className="form-group"><label className="form-label">Severity (1–5)</label><input className="form-input" type="range" min={1} max={5} value={form.severity} onChange={e=>setForm(f=>({...f,severity:parseInt(e.target.value)}))} style={{padding:"6px 0"}}/><div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--text-muted)"}}><span>Mild</span><span>Level {form.severity}</span><span>Severe</span></div></div>
            <div className="form-group"><label className="form-label">Related medication</label><select className="form-input" value={form.med} onChange={e=>setForm(f=>({...f,med:e.target.value}))}><option value="">Not sure / General</option>{meds.map(m=><option key={m.id} value={m.name}>{m.name}</option>)}</select></div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save}>Log Side Effect</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── REFILLS ───────────────────────────────────────────────────────────────────
function Refills({ meds, setMeds, showToast }) {
  const low = meds.filter(m => (m.refillCount / m.refillTotal) <= 0.3);

  const logRefill = id => {
    setMeds(p => p.map(m => m.id === id ? {...m, refillCount: m.refillTotal} : m));
    showToast("✅ Refill logged — pill count reset");
  };

  const requestRefill = (med) => {
    const pharmacy = med.pharmacy || "";
    const rxNum = med.rxNumber || "";

    const pharmacyUrls = {
      "CVS":      "https://www.cvs.com/account/login?referer=%2Fpharmacy%2Frefill%2Fapp",
      "Walgreens":"https://www.walgreens.com/login.jsp?ru=%2Ftopic%2Fpharmacy%2Frx-refill.jsp",
      "Walmart":  "https://www.walmart.com/pharmacy",
      "Costco":   "https://costco.web.medrefill.com/",
      "Amazon":   "https://pharmacy.amazon.com/",
    };
    const url = pharmacyUrls[pharmacy];
    if (url) {
      window.open(url, "_blank");
      showToast(`📱 Opening ${pharmacy} — log in to complete your refill`);
    } else {
      showToast("💡 Add your pharmacy in the medication settings to enable one-tap refill");
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Refill Tracker</h1>
        <div className="page-desc">Pill counts update automatically as you log doses</div>
      </div>

      {low.length > 0 && (
        <div className="card" style={{borderColor:"rgba(251,113,133,.35)",marginBottom:16}}>
          <div className="card-title" style={{color:"var(--coral)",marginBottom:10}}>⚠️ Running Low</div>
          {low.map(m => (
            <div key={m.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid var(--border)"}}>
              <div>
                <div style={{fontSize:14,fontWeight:600}}>{m.name} <span style={{fontWeight:400,color:"var(--text-muted)",fontSize:12}}>{m.dose}</span></div>
                <div style={{fontSize:12,color:"var(--coral)",marginTop:2}}>{m.refillCount} pills remaining</div>
              </div>
              <div style={{display:"flex",gap:8}}>
                {m.pharmacy && (
                  <button className="btn btn-secondary btn-sm" onClick={()=>requestRefill(m)}>
                    🌐 Refill Website
                  </button>
                )}
                <button className="btn btn-primary btn-sm" onClick={()=>logRefill(m.id)}>Log Refill</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {meds.length === 0 && (
        <div className="empty-state" style={{padding:"48px 24px"}}>
          <div className="empty-icon">🔄</div>
          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:"var(--text)",marginBottom:8}}>Never run out of a medication</div>
          <div className="empty-text" style={{maxWidth:300,margin:"0 auto",lineHeight:1.6}}>Once you add your medications, Veriva™ tracks your pill count automatically as you log doses — and alerts you when it's time to refill.</div>
        </div>
      )}

      {meds.map(m => (
        <div key={m.id} className="card" style={{marginBottom:10}}>
          <RefillBar med={m}/>
          <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap"}}>
            {m.pharmacy ? (
              <button className="btn btn-secondary btn-sm" onClick={()=>requestRefill(m)}>
                🌐 {m.pharmacy} Website{m.rxNumber ? ` · Rx #${m.rxNumber}` : ""}
              </button>
            ) : (
              <span style={{fontSize:11,color:"var(--text-dim)"}}>Add pharmacy in medication settings for one-tap refill</span>
            )}
            <button className="btn btn-primary btn-sm" onClick={()=>logRefill(m.id)}>✅ Log Refill</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── CAREGIVERS ────────────────────────────────────────────────────────────────
function Caregivers({ caregivers, setCaregivers, showToast, user }) {
  const [show, setShow] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({name:"",role:"",email:"",access:"Read-only"});

  // ── Earned reveal: first caregiver is free, second requires premium ───────
  const FREE_LIMIT = 1;
  const atLimit = caregivers.length >= FREE_LIMIT;

  const handleInviteClick = () => {
    if (atLimit) { setShowUpgrade(true); return; }
    setShow(true);
  };

  const save = async () => {
    if (!form.name) return;
    if (!form.email) { showToast("⚠️ Email is required to send an invite"); return; }

    setSending(true);
    const initials = form.name.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2);

    // Insert to Supabase first to get the invite_token
    let inviteToken = null;
    let caregiverId = null;
    if (user) {
      const { data, error } = await supabase.from("caregivers").insert({
        user_id: user.id,
        name: form.name,
        role: form.role || null,
        email: form.email,
        access_level: form.access === "Full" ? "full" : "read-only",
        status: "pending",
      }).select().single();
      if (error) {
        console.error("Caregiver save error:", error.message);
        showToast("❌ Could not save caregiver — please try again");
        setSending(false);
        return;
      }
      inviteToken = data.invite_token;
      caregiverId = data.id;
    }

    // Add to local state
    const newCaregiver = {
      ...form, id: caregiverId || Date.now(),
      initials, color: COLORS[caregivers.length % COLORS.length], status: "Pending"
    };
    setCaregivers(p => [...p, newCaregiver]);

    // Send invite email via Supabase Edge Function
    const fromName = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "Someone";
    try {
      const res = await supabase.functions.invoke("send-caregiver-invite", {
        body: {
          to_email: form.email,
          to_name: form.name,
          from_name: fromName,
          invite_token: inviteToken,
          access_level: form.access === "Full" ? "full" : "read-only",
        },
      });
      if (res.error) throw res.error;
      showToast(`📨 Invite sent to ${form.email}`);
    } catch (err) {
      console.error("Email send error:", err);
      showToast("⚠️ Caregiver saved but email delivery failed — check their address");
    }

    setSending(false);
    setShow(false);
    setForm({name:"",role:"",email:"",access:"Read-only"});
  };

  const remove = (c) => {
    setCaregivers(p=>p.filter(x=>x.id!==c.id));
    if (user && typeof c.id === "string" && c.id.includes("-")) {
      supabase.from("caregivers").delete().eq("id", c.id).then(({ error }) => {
        if (error) console.error("Caregiver delete error:", error.message);
      });
    }
  };

  return (
    <div>
      <div className="page-header" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <h1 className="page-title">Caregiver Access</h1>
          <div className="page-desc">Share your care with people you trust</div>
        </div>
        <button className="btn btn-primary" onClick={handleInviteClick}>+ Invite</button>
      </div>

      {/* Empty state */}
      {caregivers.length===0&&(
        <div className="empty-state" style={{padding:"48px 24px"}}>
          <div className="empty-icon">👥</div>
          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,color:"var(--text)",marginBottom:8}}>Share your care with someone you trust</div>
          <div className="empty-text" style={{maxWidth:300,margin:"0 auto 20px",lineHeight:1.6}}>Invite a family member, partner, or caregiver to view your medication schedule. They'll get an email with instructions and can see when you've taken doses.</div>
          <button className="btn btn-primary" onClick={handleInviteClick}>+ Invite a caregiver</button>
          <div style={{fontSize:11,color:"var(--text-dim)",marginTop:16}}>First caregiver is free · You control what they can see</div>
        </div>
      )}

      {/* Caregiver list */}
      {caregivers.map(c=>(
        <div key={c.id} className="caregiver-card">
          <div style={{width:40,height:40,borderRadius:"50%",background:`${c.color}22`,color:c.color,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:14,flexShrink:0}}>{c.initials}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontWeight:700,fontSize:14}}>{c.name}</div>
            <div style={{fontSize:12,color:"var(--text-muted)",marginTop:2}}>
              {c.role&&<span>{c.role} · </span>}
              {c.access} access
              {c.email&&<span> · {c.email}</span>}
            </div>
          </div>
          <span className={`tag ${c.status==="Active"?"tag-green":"tag-amber"}`}>{c.status}</span>
          <button className="btn btn-danger btn-sm" onClick={()=>remove(c)}>Remove</button>
        </div>
      ))}

      {/* Premium upsell — shows below existing caregiver once limit hit */}
      {caregivers.length >= FREE_LIMIT && (
        <div style={{marginTop:12,padding:"16px 18px",background:"rgba(192,132,252,.07)",border:"1px solid rgba(192,132,252,.2)",borderRadius:"var(--radius)",display:"flex",alignItems:"center",gap:14}}>
          <span style={{fontSize:28}}>👥</span>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:600,color:"var(--purple)",marginBottom:3}}>Add more caregivers with Premium</div>
            <div style={{fontSize:12,color:"var(--text-muted)",lineHeight:1.5}}>Premium unlocks multiple caregivers, role-based access, real-time missed-dose alerts, and remote refill management.</div>
          </div>
          <button className="btn btn-sm" style={{background:"rgba(192,132,252,.15)",color:"var(--purple)",border:"1px solid rgba(192,132,252,.3)",flexShrink:0}} onClick={()=>setShowUpgrade(true)}>
            ⭐ Upgrade
          </button>
        </div>
      )}

      {/* Invite modal */}
      {show&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShow(false)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-title">Invite a Caregiver</div>
            <div style={{fontSize:13,color:"var(--text-muted)",marginBottom:18,lineHeight:1.6}}>
              They'll receive an email with a link to accept your invitation. They don't need a Veriva™ account yet.
            </div>
            <div className="form-group">
              <label className="form-label">Their full name</label>
              <input className="form-input" placeholder="e.g. Sarah Johnson" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">Their email address</label>
              <input className="form-input" type="email" placeholder="their@email.com" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">Relationship (optional)</label>
              <input className="form-input" placeholder="e.g. Spouse, Parent, Doctor" value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">What can they do?</label>
              <select className="form-input" value={form.access} onChange={e=>setForm(f=>({...f,access:e.target.value}))}>
                <option value="Read-only">View only — see your schedule and dose history</option>
                <option value="Full">Full access — can also help manage medications and refills</option>
              </select>
            </div>
            <div style={{fontSize:11,color:"var(--text-dim)",marginBottom:18,lineHeight:1.6}}>
              🔒 They will only see what you allow. You can remove their access at any time.
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShow(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={sending}>
                {sending ? "Sending…" : "Send invitation"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium upgrade modal */}
      {showUpgrade&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowUpgrade(false)}>
          <div className="modal" style={{maxWidth:400,textAlign:"center"}}>
            <div style={{fontSize:44,marginBottom:12}}>👥</div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:20,marginBottom:8}}>Add more caregivers</div>
            <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.65,marginBottom:20}}>
              You've used your free caregiver slot. Upgrade to Premium to invite your whole care team — family members, partners, and healthcare providers.
            </div>
            <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"14px 16px",marginBottom:20,textAlign:"left"}}>
              <div style={{fontSize:11,fontWeight:600,color:"var(--text-muted)",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.8px"}}>Premium caregiver features</div>
              {[
                "👥 Unlimited caregivers",
                "🔑 Role-based access control",
                "🚨 Real-time missed-dose alerts",
                "🔄 Remote refill management",
                "📋 Caregiver activity audit log",
              ].map(f=>(
                <div key={f} style={{fontSize:13,color:"var(--text-muted)",padding:"4px 0",display:"flex",alignItems:"center",gap:8}}>{f}</div>
              ))}
              <div style={{marginTop:12,fontSize:13,fontWeight:600,color:"var(--teal)"}}>$6.99/mo · $59.99/yr</div>
            </div>
            <div style={{display:"flex",gap:8,marginBottom:8}}>
              <button className="btn btn-primary" style={{flex:1,fontSize:13}} onClick={async()=>{try{const r=await fetch("/.netlify/functions/create-checkout",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({priceId:"price_1TQ8SURsJWowELMNEtZX4da0",userId:user?.id||"",email:user?.email||""})}); const d=await r.json();if(d.url)window.location.href=d.url;else alert("Something went wrong.");}catch(e){alert("Something went wrong.");}}}>⭐ $6.99 / month</button>
              <button className="btn btn-primary" style={{flex:1,fontSize:13,background:"rgba(192,132,252,.2)",border:"1px solid rgba(192,132,252,.4)",color:"var(--purple)"}} onClick={async()=>{try{const r=await fetch("/.netlify/functions/create-checkout",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({priceId:"price_1TQ8SxRsJWowELMNhWt2pfug",userId:user?.id||"",email:user?.email||""})}); const d=await r.json();if(d.url)window.location.href=d.url;else alert("Something went wrong.");}catch(e){alert("Something went wrong.");}}}>⭐ $59.99 / year</button>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <button className="btn btn-secondary" onClick={()=>setShowUpgrade(false)}>Maybe later</button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

// ── AI CHECK-IN ───────────────────────────────────────────────────────────────
function renderMarkdown(text) {
  // Simple markdown renderer — bold, italic, line breaks
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\n/g, "<br/>");
}

function AICheckin({ meds, doseHistory, symptoms, interactions, user }) {
  const now = new Date();

  // Build rich context: medications + today's dose history
  const medCtx = meds.map(m => {
    const todayEntries = (doseHistory[m.id]||[]).filter(e =>
      e.takenAt?.startsWith(todayStr) || e.date === todayStr
    );
    const takenCount = todayEntries.filter(e => e.takenAt && !e.skipped).length;
    const skippedCount = todayEntries.filter(e => e.skipped).length;
    const doses = generateTodayDoses(m, doseHistory, now);
    const scheduled = doses.filter(d => d.plannedTime && d.type !== "prn").length;
    let status = "";
    if (scheduled > 0) status = ` — ${takenCount}/${scheduled} taken today${skippedCount > 0 ? `, ${skippedCount} skipped` : ""}`;
    // Refill info
    const daily = m.schedType==="fixed"?(m.fixedTimes||["08:00"]).length:m.schedType==="times"?(m.timesPerDay||1):m.schedType==="interval"?(m.intervalMaxPerDay||3):1;
    const daysLeft = daily > 0 ? Math.floor((m.refillCount||0) / daily) : null;
    const refillStr = daysLeft !== null ? `, ${m.refillCount} pills left (~${daysLeft} days supply)` : "";
    return `${m.name} ${m.dose} (${schedLabel(m)})${status}${refillStr}`;
  }).join("; ");

  // Side effects context (last 10)
  const sideEffectCtx = symptoms && symptoms.length > 0
    ? "\n\nRecent side effects logged: " + symptoms.slice(0,10).map(s =>
        `${s.text} (severity ${s.severity}/5${s.med ? `, linked to ${s.med}` : ""}, ${s.date})`
      ).join("; ")
    : "\n\nNo side effects logged yet.";

  // Drug interactions
  const interactionCtx = interactions && interactions.length > 0
    ? "\n\nDrug interactions flagged: " + interactions.map(i =>
        `${i.drugs} (${i.severity})`
      ).join("; ")
    : "";

  // 7-day adherence
  const allEntries = Object.values(doseHistory).flat();
  const last7 = Array.from({length:7},(_,i)=>{
    const d = new Date(); d.setDate(d.getDate()-i);
    return d.toISOString().split("T")[0];
  });
  const takenLast7 = allEntries.filter(e => e.takenAt && last7.includes(e.date)).length;
  const adherenceCtx = `\n\n7-day adherence: ${takenLast7} doses logged in the last 7 days.`;

  const firstName = user?.user_metadata?.full_name?.split(" ")[0] || "there";

  const [messages, setMessages] = useState([{
    role: "assistant",
    content: `Hi ${firstName}! I'm your Veriva™ companion. I can help with missed doses, scheduling questions, side effects, or just check in on how you're feeling. What's on your mind?`
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  useEffect(()=>bottomRef.current?.scrollIntoView({behavior:"smooth"}),[messages]);

  const send = async () => {
    const text = input.trim(); if (!text||loading) return;
    const newMsgs = [...messages, {role:"user", content:text}];
    setMessages(newMsgs); setInput(""); setLoading(true);
    const fullCtx = medCtx + sideEffectCtx + interactionCtx + adherenceCtx;
    console.log("AI context being sent:", fullCtx.slice(0, 300));
    try {
      const r = await askClaude(newMsgs, fullCtx);
      setMessages([...newMsgs, {role:"assistant", content:r}]);
    } catch {
      setMessages([...newMsgs, {role:"assistant", content:"Connection issue — please try again."}]);
    }
    setLoading(false);
  };

  const suggestions = meds.length > 0
    ? [
        `How is my adherence looking today?`,
        `Any interactions I should know about with my medications?`,
        `What side effects should I watch for?`,
        `I missed a dose — what should I do?`,
      ]
    : ["What can Veriva™ help me with?", "How do I add my medications?"];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">AI Health Check-in</h1>
        <div className="page-desc">Powered by Claude · Always consult your doctor for medical decisions</div>
      </div>
      <div className="card">
        <div className="ai-chat">
          {messages.map((m,i) => (
            <div key={i} className={`ai-bubble ${m.role}`}
              dangerouslySetInnerHTML={{__html: m.role==="assistant" ? renderMarkdown(m.content) : m.content}}
            />
          ))}
          {loading && <div className="ai-bubble assistant loading">Thinking…</div>}
          <div ref={bottomRef}/>
        </div>
        {messages.length <= 2 && (
          <div style={{marginTop:12}}>
            <div style={{fontSize:11,fontWeight:600,color:"var(--text-dim)",textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>Suggested questions</div>
            <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
              {suggestions.map(s=>(
                <button key={s} className="btn btn-secondary btn-sm" onClick={()=>setInput(s)}>{s}</button>
              ))}
            </div>
            {meds.length===0&&(
              <div style={{marginTop:16,padding:"12px 14px",background:"var(--teal-dim)",border:"1px solid rgba(45,212,191,.2)",borderRadius:"var(--radius-sm)",fontSize:12,color:"var(--text-muted)",lineHeight:1.6}}>
                💡 Add your medications first so the AI can give you personalized answers about your specific drugs, doses, and schedule.
              </div>
            )}
          </div>
        )}
        <div className="ai-input-row">
          <input className="ai-input" placeholder="Ask anything about your medications…"
            value={input} onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&send()}/>
          <button className="btn btn-primary" onClick={send} disabled={loading}>Send</button>
        </div>
      </div>
    </div>
  );
}


// ── PREMIUM MODAL ─────────────────────────────────────────────────────────────
function PremiumModal({ feature, onClose, user }) {
  const MONTHLY_PRICE_ID = "price_1TQ8SURsJWowELMNEtZX4da0";
  const ANNUAL_PRICE_ID  = "price_1TQ8SxRsJWowELMNhWt2pfug";
  const [loading, setLoading] = useState(null); // "monthly" | "annual" | null

  async function handleCheckout(plan) {
    setLoading(plan);
    try {
      const priceId = plan === "monthly" ? MONTHLY_PRICE_ID : ANNUAL_PRICE_ID;
      const res = await fetch("/.netlify/functions/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          priceId,
          userId: user?.id || "",
          email: user?.email || "",
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("Something went wrong. Please try again.");
      }
    } catch (e) {
      alert("Something went wrong. Please try again.");
    }
    setLoading(null);
  }


  const featureDetails = {
    notifications: {
      icon: "🔔",
      title: "Alarm-Style Notifications",
      desc: "Persistent lock-screen alarms that don't go away until you mark a dose taken. Available in the Veriva™ mobile app.",
      eta: "Coming with iOS & Android app",
      isPremium: false,
    },
    caregiver: {
      icon: "👥",
      title: "Caregiver Coordination",
      desc: "Invite family members or caregivers to view your schedule, get missed-dose alerts, and manage refills remotely.",
      eta: "Premium feature",
      isPremium: true,
    },
    ai_insights: {
      icon: "🤖",
      title: "AI Health Insights",
      desc: "Symptom-to-medication correlation analysis, predictive adherence nudges, and doctor visit summaries generated by AI.",
      eta: "Premium feature",
      isPremium: true,
    },
    export: {
      icon: "📤",
      title: "Data Export",
      desc: "Export your complete medication history, adherence data, and side effect logs as a PDF or CSV.",
      eta: "Coming soon",
      isPremium: false,
    },
    upgrade: {
      icon: "⭐",
      title: "Veriva™ Premium",
      desc: "Unlock caregiver coordination, AI health insights, advanced analytics, doctor visit summaries, and more.",
      eta: "Premium",
      isPremium: true,
    },
  };

  const details = featureDetails[feature] || featureDetails.upgrade;

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:400,textAlign:"center"}}>
        <div style={{fontSize:48,marginBottom:12}}>{details.icon}</div>
        <div style={{fontFamily:"'DM Serif Display',serif",fontSize:20,marginBottom:8}}>{details.title}</div>
        <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.6,marginBottom:20}}>{details.desc}</div>
        <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:20,
          background: details.isPremium ? "rgba(192,132,252,.12)" : "var(--teal-dim)",
          border: details.isPremium ? "1px solid rgba(192,132,252,.3)" : "1px solid rgba(45,212,191,.2)",
          fontSize:12,fontWeight:600,
          color: details.isPremium ? "var(--purple)" : "var(--teal)",
          marginBottom:24}}>
          {details.isPremium ? "⭐ " : "🔜 "}{details.eta}
        </div>
        {details.isPremium && (
          <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"14px 16px",marginBottom:20,textAlign:"left"}}>
            <div style={{fontSize:12,fontWeight:600,color:"var(--text-muted)",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.8px"}}>Premium includes</div>
            {["👥 Caregiver coordination","🤖 AI symptom correlation","📊 Advanced analytics","📋 Doctor visit summaries","🔔 Alarm-style notifications","💡 Predictive adherence nudges"].map(f=>(
              <div key={f} style={{fontSize:12,color:"var(--text-muted)",padding:"3px 0"}}>{f}</div>
            ))}
            <div style={{marginTop:12,fontSize:13,fontWeight:600,color:"var(--teal)"}}>$6.99/mo · $59.99/yr</div>
          </div>
        )}
        {details.isPremium && (
          <div style={{display:"flex",gap:8,marginBottom:12}}>
            <button
              className="btn btn-primary"
              style={{flex:1,fontSize:13}}
              onClick={()=>handleCheckout("monthly")}
              disabled={!!loading}
            >
              {loading==="monthly" ? "Loading…" : "⭐ $6.99 / month"}
            </button>
            <button
              className="btn btn-primary"
              style={{flex:1,fontSize:13,background:"rgba(192,132,252,.2)",border:"1px solid rgba(192,132,252,.4)",color:"var(--purple)"}}
              onClick={()=>handleCheckout("annual")}
              disabled={!!loading}
            >
              {loading==="annual" ? "Loading…" : "⭐ $59.99 / year"}
            </button>
          </div>
        )}
        <div style={{display:"flex",gap:10,justifyContent:"center"}}>
          <button className="btn btn-secondary" onClick={onClose}>Maybe later</button>
        </div>
      </div>
    </div>
  );
}

// ── AUTH SCREEN ───────────────────────────────────────────────────────────────

// ── ACCEPT INVITE SCREEN ─────────────────────────────────────────────────────
function AcceptInviteScreen({ token, onDone }) {
  const [status, setStatus] = useState("loading"); // loading | found | invalid | accepted | declined
  const [invite, setInvite] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    supabase.from("caregivers")
      .select("id, name, email, role, access_level, status, user_id")
      .eq("invite_token", token)
      .single()
      .then(({ data, error }) => {
        if (error || !data) { setStatus("invalid"); return; }
        if (data.status === "active") { setStatus("accepted"); setInvite(data); return; }
        setInvite(data);
        setStatus("found");
      });
  }, [token]);

  const handle = async (action) => {
    setWorking(true);
    if (action === "accept") {
      const { error } = await supabase.from("caregivers")
        .update({ status: "active", accepted_at: new Date().toISOString() })
        .eq("invite_token", token);
      if (error) { setWorking(false); return; }
      setStatus("accepted");
    } else {
      const { error } = await supabase.from("caregivers")
        .update({ status: "revoked" })
        .eq("invite_token", token);
      if (error) { setWorking(false); return; }
      setStatus("declined");
    }
    setWorking(false);
  };

  const accessLabel = invite?.access_level === "full"
    ? "View schedule, log doses, and help manage medications"
    : "View medication schedule and dose history";

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"24px"}}>
      <style>{styles}</style>
      <div style={{fontFamily:"'DM Serif Display',serif",fontSize:28,color:"var(--teal)",marginBottom:32}}>Veriva™</div>

      <div className="card" style={{maxWidth:420,width:"100%",textAlign:"center",padding:"32px 28px"}}>

        {status === "loading" && (
          <>
            <div className="spinner" style={{margin:"0 auto 16px"}}/>
            <div style={{fontSize:14,color:"var(--text-muted)"}}>Looking up your invitation…</div>
          </>
        )}

        {status === "invalid" && (
          <>
            <div style={{fontSize:40,marginBottom:16}}>⚠️</div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:20,marginBottom:10}}>Invitation not found</div>
            <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.6,marginBottom:24}}>
              This link may have expired or already been used. Ask the person who invited you to send a new invitation.
            </div>
            <button className="btn btn-secondary" onClick={onDone}>Go to Veriva</button>
          </>
        )}

        {status === "found" && invite && (
          <>
            <div style={{fontSize:40,marginBottom:16}}>👥</div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:20,marginBottom:8}}>You've been invited</div>
            <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.7,marginBottom:20}}>
              Someone has invited <strong>{invite.name || invite.email}</strong> to be their caregiver on Veriva™.
            </div>
            <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"14px 16px",marginBottom:24,textAlign:"left"}}>
              <div style={{fontSize:11,fontWeight:600,color:"var(--text-muted)",marginBottom:8,textTransform:"uppercase",letterSpacing:"0.7px"}}>What you'll be able to do</div>
              <div style={{fontSize:13,color:"var(--text)",lineHeight:1.6}}>{accessLabel}</div>
              {invite.role && <div style={{fontSize:12,color:"var(--text-muted)",marginTop:8}}>Your role: {invite.role}</div>}
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <button className="btn btn-secondary" disabled={working} onClick={()=>handle("decline")}>
                Decline
              </button>
              <button className="btn btn-primary" disabled={working} onClick={()=>handle("accept")}>
                {working ? "Please wait…" : "Accept invitation"}
              </button>
            </div>
            <div style={{fontSize:11,color:"var(--text-dim)",marginTop:16,lineHeight:1.5}}>
              🔒 You control your own privacy. You can be removed at any time.
            </div>
          </>
        )}

        {status === "accepted" && (
          <>
            <div style={{fontSize:40,marginBottom:16}}>✅</div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:20,marginBottom:8}}>You're connected</div>
            <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.6,marginBottom:24}}>
              You now have caregiver access. Create a Veriva™ account or sign in to view the medication schedule.
            </div>
            <button className="btn btn-primary" onClick={onDone}>Create account or sign in</button>
          </>
        )}

        {status === "declined" && (
          <>
            <div style={{fontSize:40,marginBottom:16}}>👋</div>
            <div style={{fontFamily:"'DM Serif Display',serif",fontSize:20,marginBottom:8}}>Invitation declined</div>
            <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.6,marginBottom:24}}>
              No problem — the invitation has been declined. You can close this tab.
            </div>
            <button className="btn btn-secondary" onClick={onDone}>Go to Veriva</button>
          </>
        )}

      </div>
    </div>
  );
}

function AuthScreen({ onAuth }) {
  const [tab, setTab] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleSubmit = async () => {
    setError(""); setLoading(true);
    try {
      if (tab === "signup") {
        const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: name } } });
        if (data?.user) {
          await supabase.from("users").update({ terms_accepted_at: new Date().toISOString() }).eq("auth_id", data.user.id);
        }
        if (error) throw error;
        if (data.user) onAuth(data.user);
        else setError("Check your email to confirm your account.");
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onAuth(data.user);
      }
    } catch (e) { setError(e.message || "Authentication failed."); }
    setLoading(false);
  };

  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--bg)",padding:24}}>
      <style>{styles}</style>
      <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"36px",width:"100%",maxWidth:400}}>
        <div style={{fontFamily:"DM Serif Display,serif",fontSize:28,color:"var(--teal)",textAlign:"center",marginBottom:6}}>Veriva™</div>
        <div style={{fontSize:13,color:"var(--text-muted)",textAlign:"center",marginBottom:28}}>Personal Health & Medication Management</div>
        <div style={{display:"flex",marginBottom:24,border:"1px solid var(--border)",borderRadius:"var(--radius-sm)",overflow:"hidden"}}>
          {["signin","signup"].map(t=>(
            <button key={t} style={{flex:1,padding:9,textAlign:"center",fontSize:13,fontWeight:500,cursor:"pointer",color:tab===t?"#0d1117":"var(--text-muted)",background:tab===t?"var(--teal)":"transparent",border:"none",fontFamily:"DM Sans,sans-serif",transition:"all .14s"}} onClick={()=>{setTab(t);setError("");}}>
              {t==="signin"?"Sign In":"Create Account"}
            </button>
          ))}
        </div>
        {error&&<div style={{background:"rgba(251,113,133,.1)",border:"1px solid rgba(251,113,133,.3)",borderRadius:"var(--radius-sm)",padding:"10px 13px",fontSize:13,color:"var(--coral)",marginBottom:12}}>{error}</div>}
        {tab==="signup"&&(
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input className="form-input" placeholder="Your name" value={name} onChange={e=>setName(e.target.value)}/>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Email</label>
          <input className="form-input" type="email" placeholder="you@email.com" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSubmit()}/>
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <input className="form-input" type="password" placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSubmit()}/>
        </div>
        {tab==="signup"&&(
          <div style={{display:"flex",alignItems:"flex-start",gap:10,margin:"16px 0 4px",padding:"12px 14px",background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:"var(--radius-sm)"}}>
            <input
              type="checkbox"
              id="terms-checkbox"
              checked={termsAccepted}
              onChange={e=>setTermsAccepted(e.target.checked)}
              style={{marginTop:2,accentColor:"var(--teal)",width:15,height:15,flexShrink:0,cursor:"pointer"}}
              aria-label="Accept Terms of Service and Privacy Policy"
            />
            <label htmlFor="terms-checkbox" style={{fontSize:12,color:"var(--text-muted)",lineHeight:1.55,cursor:"pointer"}}>
              I agree to Veriva's{" "}
              <a href="https://veriva.health/terms.html" target="_blank" rel="noopener noreferrer" style={{color:"var(--teal)"}}>Terms of Service</a>
              {" "}and{" "}
              <a href="https://veriva.health/privacy.html" target="_blank" rel="noopener noreferrer" style={{color:"var(--teal)"}}>Privacy Policy</a>.
              Your health data is encrypted and protected.
            </label>
          </div>
        )}
        <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",marginTop:4}} onClick={handleSubmit} disabled={loading||(tab==="signup"&&!termsAccepted)}>
          {loading?"Please wait…":tab==="signup"?"Create Account":"Sign In"}
        </button>
        <div style={{fontSize:12,color:"var(--text-dim)",textAlign:"center",marginTop:16}}>{tab==="signin"&&"Your health data is encrypted and protected."}</div>
      </div>
    </div>
  );
}


// ── SETTINGS SCREEN ───────────────────────────────────────────────────────────
function SettingsScreen({ user, showToast, handleSignOut, onPremium, meds, setMeds }) {
  const [notifEnabled, setNotifEnabled] = useState(true);
  const [reminderLead, setReminderLead] = useState("0");
  const [missedAlert, setMissedAlert] = useState("15");
  const [refillDays, setRefillDays] = useState("7");
  const [notifLoading, setNotifLoading] = useState(false);
  const [medNotifExpanded, setMedNotifExpanded] = useState(false);
  // Per-medication reminder overrides: { [medId]: { enabled: bool } }
  const [medNotifOverrides, setMedNotifOverrides] = useState({});

  // Load notification settings from Supabase on mount
  useEffect(() => {
    const uid = user && user.id;
    if (!uid) return;
    supabase.from("notification_settings").select("*").eq("user_id", uid)
      .then(({ data, error }) => {
        if (error || !data || data.length === 0) return;
        const row = data[0];
        setNotifEnabled(row.reminders_enabled ?? true);
        setReminderLead(String(row.reminder_lead_minutes ?? 0));
        setMissedAlert(String(row.missed_dose_alert_minutes ?? 15));
        setRefillDays(String(row.refill_alert_days ?? 7));
      });
  }, [user]);

  const saveNotifSettings = async (overrides = {}) => {
    const uid = user && user.id;
    if (!uid) return;
    setNotifLoading(true);
    const patch = {
      user_id: uid,
      reminders_enabled: overrides.notifEnabled ?? notifEnabled,
      reminder_lead_minutes: parseInt(overrides.reminderLead ?? reminderLead),
      missed_dose_alert_minutes: parseInt(overrides.missedAlert ?? missedAlert),
      refill_alert_days: parseInt(overrides.refillDays ?? refillDays),
      updated_at: new Date().toISOString(),
    };
    const { error } = await supabase.from("notification_settings").upsert(patch, { onConflict: "user_id" });
    setNotifLoading(false);
    if (error) showToast("❌ Could not save — please try again");
    else showToast("✅ Notification settings saved");
  };

  const toggleMedNotif = (medId, currentEnabled) => {
    const next = !currentEnabled;
    setMedNotifOverrides(prev => ({ ...prev, [medId]: { enabled: next } }));
    // Persist override on the medication notes field isn't ideal — store in localStorage for now
    try {
      const stored = JSON.parse(localStorage.getItem("veriva_med_notif") || "{}");
      stored[medId] = { enabled: next };
      localStorage.setItem("veriva_med_notif", JSON.stringify(stored));
    } catch {}
    showToast(next ? "🔔 Reminders on for this medication" : "🔕 Reminders off for this medication");
  };

  // Load per-med overrides from localStorage on mount
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("veriva_med_notif") || "{}");
      setMedNotifOverrides(stored);
    } catch {}
  }, []);

  const Row = ({icon, label, children}) => (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"13px 0",borderBottom:"1px solid var(--border)"}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:17}}>{icon}</span>
        <span style={{fontSize:13.5,fontWeight:500}}>{label}</span>
      </div>
      <div>{children}</div>
    </div>
  );

  const Toggle = ({on, onChange, label}) => (
    <div role="switch" aria-checked={on} aria-label={label} tabIndex={0} onClick={()=>onChange(!on)} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&onChange(!on)} style={{width:44,height:24,borderRadius:12,background:on?"var(--teal)":"var(--border)",cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
      <div style={{position:"absolute",top:3,left:on?23:3,width:18,height:18,borderRadius:9,background:"white",transition:"left .2s",boxShadow:"0 1px 4px rgba(0,0,0,.3)"}}/>
    </div>
  );

  return (
    <div style={{maxWidth:600}}>
      <h2 style={{fontFamily:"DM Serif Display,serif",fontSize:22,marginBottom:6}}>Settings</h2>
      <p style={{fontSize:13,color:"var(--text-muted)",marginBottom:24}}>Manage your account and notification preferences.</p>

      {/* Account */}
      <div className="card" style={{marginBottom:14}}>
        <div className="card-title" style={{marginBottom:16}}>Account</div>
        <Row icon="👤" label="Name">
          <span style={{fontSize:13,color:"var(--text-muted)"}}>{user?.user_metadata?.full_name || "—"}</span>
        </Row>
        <Row icon="✉️" label="Email">
          <span style={{fontSize:13,color:"var(--text-muted)"}}>{user?.email}</span>
        </Row>
        <Row icon="🔐" label="Password">
          <button className="btn btn-secondary btn-sm" onClick={()=>showToast("📧 Password reset email sent")}>Reset password</button>
        </Row>
        <Row icon="📅" label="Member since">
          <span style={{fontSize:13,color:"var(--text-muted)"}}>
            {user?.created_at ? new Date(user.created_at).toLocaleDateString("en-US",{month:"long",year:"numeric"}) : "—"}
          </span>
        </Row>
        <Row icon="🌍" label="Time zone">
          <select className="form-input" style={{width:200,padding:"5px 8px",fontSize:12}}
            defaultValue={Intl.DateTimeFormat().resolvedOptions().timeZone}
            onChange={e=>{ try{localStorage.setItem("veriva_tz",e.target.value);}catch{} showToast("✅ Timezone saved"); }}>
            {["America/New_York","America/Chicago","America/Denver","America/Los_Angeles","America/Phoenix","America/Anchorage","Pacific/Honolulu","America/Puerto_Rico","Europe/London","Europe/Paris","Europe/Berlin","Asia/Tokyo","Asia/Shanghai","Asia/Kolkata","Australia/Sydney"].map(tz=>(
              <option key={tz} value={tz}>{tz.replace(/_/g," ")}</option>
            ))}
          </select>
        </Row>
      </div>

      {/* Notifications */}
      <div className="card" style={{marginBottom:14}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
          <div className="card-title">Notification Preferences</div>
          <Toggle label="Enable all reminders" on={notifEnabled} onChange={v=>{setNotifEnabled(v);saveNotifSettings({notifEnabled:v});}}/>
        </div>
        <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:16}}>Controls when Veriva™ reminds you to take each dose.</div>

        <div style={{opacity:notifEnabled?1:0.45,pointerEvents:notifEnabled?"auto":"none",transition:"opacity .2s"}}>
          <Row icon="⏱️" label="Remind me before dose">
            <select className="form-input" aria-label="Reminder lead time" style={{width:130,padding:"5px 8px",fontSize:12}} value={reminderLead} onChange={e=>{setReminderLead(e.target.value);}}>
              <option value="0">At dose time</option>
              <option value="5">5 min before</option>
              <option value="10">10 min before</option>
              <option value="15">15 min before</option>
              <option value="30">30 min before</option>
            </select>
          </Row>
          <Row icon="⚠️" label="Missed dose alert after">
            <select className="form-input" aria-label="Missed dose alert delay" style={{width:130,padding:"5px 8px",fontSize:12}} value={missedAlert} onChange={e=>{setMissedAlert(e.target.value);}}>
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
              <option value="120">2 hours</option>
            </select>
          </Row>
          <Row icon="💊" label="Refill reminder">
            <select className="form-input" aria-label="Refill reminder days" style={{width:130,padding:"5px 8px",fontSize:12}} value={refillDays} onChange={e=>{setRefillDays(e.target.value);}}>
              <option value="3">3 days out</option>
              <option value="7">7 days out</option>
              <option value="14">14 days out</option>
              <option value="30">30 days out</option>
            </select>
          </Row>

          <button className="btn btn-primary btn-sm" style={{marginTop:14}} disabled={notifLoading} onClick={()=>saveNotifSettings()}>
            {notifLoading?"Saving…":"💾 Save preferences"}
          </button>

          {/* Per-medication overrides */}
          {meds && meds.length > 0 && (
            <div style={{marginTop:16,borderTop:"1px solid var(--border)",paddingTop:14}}>
              <div
                className="advanced-toggle" role="button" tabIndex={0}
                onClick={()=>setMedNotifExpanded(p=>!p)}
                onKeyDown={e=>(e.key===" "||e.key==="Enter")&&setMedNotifExpanded(p=>!p)}
                aria-expanded={medNotifExpanded}
                style={{marginTop:0}}
              >
                <span style={{flex:1,fontSize:13,fontWeight:500}}>Per-medication reminders</span>
                <span style={{fontSize:11,color:"var(--text-dim)"}}>{medNotifExpanded?"▲ Hide":"▼ Show"}</span>
              </div>
              {medNotifExpanded && (
                <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:8}}>
                  <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:4}}>
                    Override global reminders for individual medications. Off = silent for that med only.
                  </div>
                  {meds.map(m => {
                    const override = medNotifOverrides[m.id];
                    const isOn = override ? override.enabled : true;
                    return (
                      <div key={m.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 12px",background:"var(--surface2)",borderRadius:"var(--radius-sm)",border:"1px solid var(--border)"}}>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:10,height:10,borderRadius:"50%",background:m.color,flexShrink:0}}/>
                          <div>
                            <div style={{fontSize:13,fontWeight:500}}>{m.name}</div>
                            <div style={{fontSize:11,color:"var(--text-muted)"}}>{m.dose} · {m.schedType==="prn"?"As needed":m.schedType==="fixed"?`${(m.fixedTimes||[]).length}x fixed`:m.schedType==="interval"?`Every ${m.intervalHours}h`:m.schedType==="cyclic"?"Cyclic":`${m.timesPerDay}x daily`}</div>
                          </div>
                        </div>
                        <Toggle
                          label={`Reminders for ${m.name}`}
                          on={isOn}
                          onChange={()=>toggleMedNotif(m.id, isOn)}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Plan */}
      <div className="card" style={{marginBottom:14}}>
        <div className="card-title" style={{marginBottom:16}}>Your Plan</div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:14,fontWeight:600,color:"var(--teal)"}}>Free Plan</div>
            <div style={{fontSize:12,color:"var(--text-muted)",marginTop:3}}>Unlimited medications · Basic reminders · Drug interaction checks</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={()=>onPremium?.("upgrade")}>⭐ Upgrade to Premium</button>
        </div>
        <div style={{marginTop:14,padding:"12px 14px",background:"var(--teal-dim)",border:"1px solid rgba(45,212,191,.2)",borderRadius:"var(--radius-sm)"}}>
          <div style={{fontSize:12,fontWeight:600,color:"var(--teal)",marginBottom:6}}>Premium includes:</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"4px 16px"}}>
            {["👥 Caregiver coordination","🤖 AI health companion","📊 Advanced analytics","📋 Doctor visit summaries","🔔 Alarm-style notifications","💡 Symptom correlation"].map(f=>(
              <div key={f} style={{fontSize:12,color:"var(--text-muted)"}}>{f}</div>
            ))}
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <div className="card">
        <div className="card-title" style={{marginBottom:16,color:"var(--coral)"}}>Account Actions</div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <button className="btn btn-secondary btn-sm" onClick={()=>onPremium?.("export")}>📤 Export my data</button>
          <button className="btn btn-danger btn-sm" onClick={handleSignOut}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

// ── ONBOARDING SCREEN ────────────────────────────────────────────────────────
function OnboardingScreen({ onComplete, onAddMed }) {
  const [step, setStep] = useState(0); // 0=welcome, 1=add med, 2=notifications

  const steps = [
    {
      icon: "💊",
      title: "Your medications, clearly organized",
      desc: "Managing medications is harder than it should be. Veriva™ keeps everything in one place — your schedule, your history, and your peace of mind.",
      features: [
        { icon: "📋", label: "Every medication, every schedule", sub: "Fixed times, intervals, as-needed, cyclic — all supported" },
        { icon: "⚠️", label: "Drug interaction checks", sub: "Automatic checks as you add medications" },
        { icon: "👥", label: "Share with a caregiver", sub: "Loop in family or your care team when you need to" },
      ],
      primary: "Let's get started",
      skip: null,
      trust: null,
    },
    {
      icon: "➕",
      title: "Add your first medication",
      desc: "Just type the name — Veriva™ looks up your medication and fills in dose options automatically. Takes about 30 seconds.",
      features: [
        { icon: "🔍", label: "Automatic dose lookup", sub: "Pulls from a verified drug database" },
        { icon: "📅", label: "Flexible scheduling", sub: "Set it up the way your prescription actually works" },
        { icon: "🔒", label: "Private by default", sub: "Your medication data is encrypted and only visible to you" },
      ],
      primary: "Add my first medication",
      skip: "I'll do this later",
      trust: "🔒 Your health data is private and encrypted. Veriva™ does not sell or share your information.",
    },
    {
      icon: "🔔",
      title: "Stay on schedule",
      desc: "Veriva™ can remind you when it's time to take a dose. You're in control — turn reminders on or off for each medication individually.",
      features: [
        { icon: "⏰", label: "Dose reminders", sub: "Get notified before or at your scheduled time" },
        { icon: "⚠️", label: "Missed dose alerts", sub: "Veriva™ flags late doses so nothing slips through" },
        { icon: "🎛️", label: "Per-medication control", sub: "Customize or silence reminders for each drug" },
      ],
      primary: "Turn on reminders",
      skip: "Not right now",
      trust: null,
    },
  ];

  const s = steps[step];

  const handlePrimary = () => {
    if (step === 0) { setStep(1); return; }
    if (step === 1) {
      onAddMed();
      return;
    }
    if (step === 2) {
      if ("Notification" in window) {
        Notification.requestPermission().then(() => onComplete());
      } else {
        onComplete();
      }
    }
  };

  const handleSkip = () => {
    if (step === 1) { setStep(2); return; }
    if (step === 2) { onComplete(); return; }
    setStep(s => s + 1);
  };

  return (
    <div className="onboard">
      <style>{styles}</style>
      <div className="onboard-logo">Veriva™</div>
      <div className="onboard-step-dots">
        {steps.map((_, i) => <div key={i} className={`onboard-dot ${i === step ? "active" : ""}`}/>)}
      </div>
      <div className="onboard-icon">{s.icon}</div>
      <div className="onboard-title">{s.title}</div>
      <div className="onboard-desc">{s.desc}</div>
      <div className="onboard-features">
        {s.features.map(f => (
          <div key={f.label} className="onboard-feature">
            <div className="onboard-feature-icon">{f.icon}</div>
            <div className="onboard-feature-text">
              <div className="onboard-feature-label">{f.label}</div>
              <div className="onboard-feature-sub">{f.sub}</div>
            </div>
          </div>
        ))}
      </div>
      {s.trust && (
        <div style={{
          fontSize:11.5, color:"var(--text-dim)", lineHeight:1.6,
          maxWidth:320, margin:"-16px auto 0", textAlign:"center", padding:"0 8px"
        }}>
          {s.trust}
        </div>
      )}
      <div className="onboard-actions">
        <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"13px"}} onClick={handlePrimary}>
          {s.primary}
        </button>
        {s.skip && <div className="onboard-skip" role="button" tabIndex={0} onClick={handleSkip}>{s.skip}</div>}
      </div>
    </div>
  );
}

// ── ROOT ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [view, setView] = useState("dashboard");
  // Caregiver accept-invite flow — read token from URL on load
  const [inviteToken] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("token") || null;
    } catch { return null; }
  });
  // Onboarding: show for new signups (account created within last 5 minutes)
  // Falls back to localStorage so existing users are never re-shown onboarding
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(() => {
    try { return localStorage.getItem("veriva_onboarded") === "1"; } catch { return false; }
  });
  const completeOnboarding = () => {
    try { localStorage.setItem("veriva_onboarded", "1"); } catch {}
    setHasSeenOnboarding(true);
  };
  // Check if this is a brand-new signup — show onboarding if account < 5 min old
  useEffect(() => {
    if (!user) return;
    const created = user.created_at ? new Date(user.created_at) : null;
    const isNewUser = created && (Date.now() - created.getTime()) < 5 * 60 * 1000;
    if (isNewUser) {
      try { localStorage.removeItem("veriva_onboarded"); } catch {}
      setHasSeenOnboarding(false);
    }
  }, [user?.id]);
  const [meds, setMedsRaw] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [interactionsLoading, setInteractionsLoading] = useState(false);
  const [doseHistoryRaw, setDoseHistoryRaw] = useState({});

  // Derive doseHistory as a readable alias
  const doseHistory = doseHistoryRaw;
  const [toast, setToast] = useState(null);
  const [epicSession, setEpicSession] = useState(null);
  const [dbLoading, setDbLoading] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [premiumModal, setPremiumModal] = useState(null); // null | feature key
  const showToast = useCallback(msg=>{ setToast(msg); setTimeout(()=>setToast(null),3000); },[]);

  // ── Stripe checkout return handler ──────────────────────────────────────────
  useEffect(()=>{
    const params = new URLSearchParams(window.location.search);
    if(params.get("checkout")==="success"){
      showToast("🎉 Welcome to Veriva Premium!");
      window.history.replaceState({},"",window.location.pathname);
    } else if(params.get("checkout")==="cancel"){
      showToast("Checkout cancelled.");
      window.history.replaceState({},"",window.location.pathname);
    }
  },[]);

  // ── Auth session ────────────────────────────────────────────────────────────
  useEffect(() => {
    // getSession gives us the current session immediately
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });
    // Only update user state on meaningful auth events — never on token refresh
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
        if (session?.user) setUser(session.user);
      } else if (event === "SIGNED_OUT") {
        setUser(null);
      }
      // Ignore: TOKEN_REFRESHED, USER_UPDATED, PASSWORD_RECOVERY — these should
      // never clear state or trigger a full data reload
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load all data when user logs in / logs out ────────────────────────────────
  // user.id IS the auth UUID — used directly as user_id in all tables (no users table)
  const uidRef = useRef(null);

  useEffect(() => {
    if (!user) {
      // Only clear if we were previously logged in
      if (uidRef.current !== null) {
        uidRef.current = null;
        setMedsRaw([]);
        setDoseHistoryRaw({});
        setSymptoms([]);
        setCaregivers([]);
        setDbLoading(false);
      }
      return;
    }

    // Same user already loaded — skip redundant reload
    if (uidRef.current === user.id) return;

    uidRef.current = user.id;
    console.log("✅ Auth user.id:", user.id);

    const loadAll = async () => {
      setDbLoading(true);

      // Load medications — explicit user_id filter + RLS as backup
      const { data: medsData, error: medsErr } = await supabase
        .from("medications").select("*")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .order("created_at", { ascending: true });
      if (medsErr) {
        console.error("Meds load error:", medsErr.message);
      } else {
        setMedsRaw(medsData.map(dbToMed));
      }
      setDbLoading(false);

      // Load dose history (last 60 days — needed for streak + 28-day adherence grid)
      const sixtyAgo = new Date(Date.now() - 60*86400000).toISOString().split("T")[0];
      const { data: doseData, error: doseErr } = await supabase
        .from("dose_history").select("*").eq("user_id", user.id).gte("date", sixtyAgo);
      if (doseErr) {
        console.error("Dose history load error:", doseErr.message);
      } else {
        const history = {};
        (doseData || []).forEach(row => {
          if (!history[row.medication_id]) history[row.medication_id] = [];
          history[row.medication_id].push({
            doseKey: row.dose_key, takenAt: row.taken_at,
            skipped: row.skipped, date: row.date,
          });
        });
        setDoseHistoryRaw(history);
      }

      // Load symptoms
      const { data: sympData, error: sympErr } = await supabase
        .from("symptoms").select("*").eq("user_id", user.id).order("created_at", { ascending: false });
      if (sympErr) console.error("Symptoms load error:", sympErr.message);
      else if (sympData) setSymptoms(sympData.map(r => ({
        id: r.id, text: r.text, severity: r.severity, date: r.date, med: r.med_name || r.notes || "",
      })));

      // Load caregivers
      const { data: cgData, error: cgErr } = await supabase
        .from("caregivers").select("*").eq("user_id", user.id).order("created_at", { ascending: true });
      if (cgErr) console.error("Caregivers load error:", cgErr.message);
      else if (cgData) setCaregivers(cgData.map(r => ({
        id: r.id, name: r.name, role: r.role || "",
        initials: r.name.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2),
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        status: r.status === "active" ? "Active" : "Pending",
        access: r.access_level === "full" ? "Full" : "Read-only",
        email: r.email || "",
      })));
    };

    loadAll().catch(e => console.error("loadAll error:", e));
  }, [user]);

  // ── setMeds: update local state + persist to Supabase ────────────────────────
  const pendingWriteRef = useRef(null);

  // ── medsRef: kept in sync so setDoseHistory can resolve temp IDs to UUIDs ──
  // Declared here (before setDoseHistory) so the closure always has access.
  const medsRef = useRef([]);
  useEffect(() => { medsRef.current = meds; }, [meds]);

  const setMeds = useCallback((updater) => {
    setMedsRaw(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const uid = uidRef.current;
      if (!uid) { console.warn("setMeds: no uid — user not ready"); return next; }

      const prevIds = new Set(prev.map(m => m.id));
      const nextIds = new Set(next.map(m => m.id));

      // Soft-delete removed meds (must have a real UUID)
      prev
        .filter(m => !nextIds.has(m.id) && typeof m.id === "string" && m.id.length > 10)
        .forEach(m => {
          console.log("Deleting med:", m.id);
          supabase.from("medications").update({ is_active: false }).eq("id", m.id)
            .then(({ error }) => { if (error) console.error("Med delete error:", error.message); });
        });

      // Split into: brand new meds (no UUID) vs existing meds (have UUID)
      const newMeds = next.filter(m => typeof m.id !== "string" || m.id.length < 10);
      const existingChanged = next.filter(m =>
        typeof m.id === "string" && m.id.length > 10 &&
        prevIds.has(m.id) &&
        JSON.stringify(prev.find(p => p.id === m.id)) !== JSON.stringify(m)
      );

      // INSERT new meds (no id — let DB generate UUID)
      if (newMeds.length > 0) {
        const insertRows = newMeds.map(m => {
          const row = medToDb(m, uid);
          delete row.id; // must not send id for new rows
          return row;
        });
        console.log("Inserting", insertRows.length, "new med(s):", JSON.stringify(insertRows[0]));
        pendingWriteRef.current = supabase.from("medications")
          .insert(insertRows).select()
          .then(({ data, error }) => {
            if (error) {
              console.error("Med INSERT error:", error.message, error.code, error.details, error.hint);
              return;
            }
            console.log("✅ Med inserted:", data?.length, "rows", JSON.stringify(data?.[0]));
            // Replace temp numeric IDs with real DB UUIDs
            if (data?.length > 0) {
              setMedsRaw(cur => {
                const dbMap = new Map(data.map(r => [r.name + r.dose, r.id]));
                const updated = cur.map(m => {
                  if (typeof m.id !== "string" || m.id.length < 10) {
                    const dbId = dbMap.get(m.name + m.dose);
                    if (dbId) return { ...m, id: dbId };
                  }
                  return m;
                });
                // Sync medsRef immediately so pending dose saves can resolve new UUIDs
                medsRef.current = updated;
                return updated;
              });
            }
          });
      }

      // UPDATE existing changed meds (have UUID)
      existingChanged.forEach(m => {
        const row = medToDb(m, uid);
        console.log("Updating med:", m.id);
        supabase.from("medications").update(row).eq("id", m.id)
          .then(({ error }) => {
            if (error) console.error("Med UPDATE error:", error.message, error.code);
            else console.log("✅ Med updated:", m.id);
          });
      });

      return next;
    });
  }, []);

  // ── setDoseHistory: update local state + persist to Supabase ─────────────────
  const setDoseHistory = useCallback((updater) => {
    setDoseHistoryRaw(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const uid = uidRef.current;
      if (!uid) return next;

      // Dedup against today's entries only — doseKeys include date so cross-day
      // collisions are impossible, but React may call updater multiple times
      const todayForDedup = getTodayStr();
      const prevKeys = new Set(
        Object.values(prev).flat()
          .filter(e => e.date === todayForDedup || (e.takenAt && e.takenAt.startsWith(todayForDedup)))
          .map(e => e.doseKey)
      );

      Object.entries(next).forEach(([medId, entries]) => {
        entries.forEach(entry => {
          if (prevKeys.has(entry.doseKey)) return;

          const isUUID = typeof medId === "string" && medId.includes("-") && medId.length > 30;
          const resolvedMedId = isUUID
            ? medId
            : medsRef.current?.find(m => String(m.id) === String(medId))?.id;

          if (!resolvedMedId) return;

          supabase.from("dose_history").insert({
            user_id: uid,
            medication_id: resolvedMedId,
            dose_key: entry.doseKey,
            taken_at: entry.takenAt || null,
            skipped: entry.skipped || false,
            date: entry.date || getTodayStr(),
          }).then(({ error }) => {
            if (error && error.code !== "23505") {
              console.error("Dose save error:", error.message);
            }
          });
        });
      });
      return next;
    });
  }, []);

  // medsRef moved above setDoseHistory — see below

  // ── Sign out ──────────────────────────────────────────────────────────────────
  const handleSignOut = async () => {
    if (pendingWriteRef.current) await pendingWriteRef.current;
    uidRef.current = null;
    await supabase.auth.signOut();
    setUser(null);
    setMedsRaw([]); setDoseHistoryRaw({}); setSymptoms([]); setCaregivers([]);
    showToast("👋 Signed out");
  };

    // ── Fetch live drug interactions whenever meds list changes ──────────────────
  useEffect(() => {
    if (meds.length < 2) { setInteractions([]); return; }
    setInteractionsLoading(true);
    fetchLiveInteractions(meds).then(results => {
      setInteractions(results);
      setInteractionsLoading(false);
    });
  }, [meds]);


  // ── setMeds: updates local state AND persists to Supabase ───────────────────
  const lowRefill = meds.filter(m=>(m.refillCount/m.refillTotal)<=0.3);
  const [showMore, setShowMore] = useState(false);

  // Primary tabs (bottom nav — 5 slots)
  const primaryTabs = [
    {id:"dashboard",  icon:"🏠", label:"Home"},
    {id:"medications",icon:"💊", label:"Meds"},
    {id:"side-effects",   icon:"🩺", label:"Side FX", badge:symptoms.length||null},
    {id:"ai",         icon:"🤖", label:"AI"},
    {id:"__more__",   icon:"☰",  label:"More"},
  ];

  // Overflow items shown in "More" sheet
  const moreTabs = [
    {id:"interactions",icon:"⚠️", label:"Interactions", badge:interactions.length||null},
    {id:"refills",     icon:"🔄", label:"Refills",      badge:lowRefill.length||null},
    {id:"caregivers",  icon:"👥", label:"Caregivers"},
  ];
  // Sidebar nav items (desktop)
  const navItems = [
    {id:"dashboard",  icon:"🏠", label:"Dashboard"},
    {id:"medications",icon:"💊", label:"Medications"},
    {id:"interactions",icon:"⚠️",label:"Interactions", badge:interactions.length},
    {id:"side-effects",   icon:"🩺", label:"Side Effects",     badge:symptoms.length},
    {id:"refills",    icon:"🔄", label:"Refills",      badge:lowRefill.length||null},
    {id:"caregivers", icon:"👥", label:"Caregivers"},
    {id:"ai",         icon:"🤖", label:"AI Check-in"},
    {id:"settings",   icon:"⚙️", label:"Settings"},
  ];

  const handleTabClick = (id) => {
    if (id === "__more__") { setShowMore(v => !v); return; }
    setView(id); setShowMore(false);
  };

  // ── Auth gate ────────────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"var(--bg)"}}>
        <style>{styles}</style>
        <div style={{fontFamily:"DM Serif Display,serif",fontSize:28,color:"var(--teal)"}}>Veriva™</div>
      </div>
    );
  }

  if (inviteToken && !user) {
    return <AcceptInviteScreen token={inviteToken} onDone={() => {
      // Clear token from URL then go to auth
      try { window.history.replaceState({}, "", window.location.pathname); } catch {}
    }} />;
  }

  if (!user) {
    return <AuthScreen onAuth={u => setUser(u)} />;
  }

  if (!hasSeenOnboarding) {
    return <OnboardingScreen onComplete={completeOnboarding} onAddMed={() => { completeOnboarding(); setView("medications"); }} />;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <nav className="topnav">
          <div className="logo">Veriva™<span className="logo-sub">Personal Health</span></div>
          <div className="nav-actions">
            {epicSession && <span style={{fontSize:11,color:"var(--purple)",background:"rgba(192,132,252,.1)",border:"1px solid rgba(192,132,252,.25)",borderRadius:20,padding:"3px 10px"}}>⚕️ Epic Connected</span>}
            {dbLoading && <span style={{fontSize:11,color:"var(--text-muted)"}}>saving…</span>}
            <div style={{position:"relative"}}>
              <div className="avatar" title="Account & Settings" role="button" tabIndex={0} aria-label="Account and settings" aria-expanded={showProfileMenu} onClick={()=>setShowProfileMenu(v=>!v)} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&setShowProfileMenu(v=>!v)} style={{cursor:"pointer"}}>
                {user?.email?.[0]?.toUpperCase()||"A"}
              </div>
              {showProfileMenu && (
                <>
                  <div style={{position:"fixed",inset:0,zIndex:299}} onClick={()=>setShowProfileMenu(false)}/>
                  <div style={{position:"absolute",right:0,top:"calc(100% + 8px)",background:"var(--surface)",border:"1px solid var(--border)",borderRadius:"var(--radius)",padding:"8px 0",minWidth:220,zIndex:300,boxShadow:"0 8px 32px rgba(0,0,0,.4)"}}>
                    <div style={{padding:"10px 16px 8px",borderBottom:"1px solid var(--border)",marginBottom:4}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--text)"}}>{user?.user_metadata?.full_name || "My Account"}</div>
                      <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>{user?.email}</div>
                    </div>
                    <div style={{padding:"2px 0"}}>
                      {[
                        {icon:"⚙️", label:"Settings", action:()=>{setView("settings");setShowProfileMenu(false);}},
                        {icon:"🔔", label:"Notification preferences", action:()=>{setView("settings");setShowProfileMenu(false);}},
                        {icon:"👥", label:"Caregivers", action:()=>{setView("caregivers");setShowProfileMenu(false);}},
                        {icon:"⚕️", label:"Connect Epic / FHIR", action:()=>{setView("medications");setShowProfileMenu(false);}},
                      ].map(item=>(
                        <div key={item.label} onClick={item.action} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 16px",cursor:"pointer",fontSize:13,color:"var(--text-muted)",transition:"all .12s"}}
                          onMouseEnter={e=>e.currentTarget.style.background="var(--surface2)"}
                          onMouseLeave={e=>e.currentTarget.style.background=""}>
                          <span style={{fontSize:15}}>{item.icon}</span>{item.label}
                        </div>
                      ))}
                      <div style={{borderTop:"1px solid var(--border)",marginTop:4,paddingTop:4}}>
                        <div onClick={()=>{setShowProfileMenu(false);handleSignOut();}} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 16px",cursor:"pointer",fontSize:13,color:"var(--coral)",transition:"all .12s"}}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(251,113,133,.06)"}
                          onMouseLeave={e=>e.currentTarget.style.background=""}>
                          <span style={{fontSize:15}}>🚪</span>Sign out
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </nav>

        <div className="main-layout">
          {/* Desktop sidebar */}
          <aside className="sidebar" aria-label="Sidebar navigation">
            <div className="nav-section">Menu</div>
            {navItems.map(n=>(
              <div key={n.id} className={`nav-item ${view===n.id?"active":""}`} role="button" tabIndex={0} aria-current={view===n.id?"page":undefined} onClick={()=>setView(n.id)} onKeyDown={e=>(e.key===" "||e.key==="Enter")&&setView(n.id)}>
                <span className="nav-icon">{n.icon}</span>
                <span style={{flex:1}}>{n.label}</span>
                {n.badge>0&&<span className="badge">{n.badge}</span>}
              </div>
            ))}
          </aside>

          <main className="content" aria-label="Main content">
            {view==="dashboard"    && <Dashboard meds={meds} setMeds={setMeds} doseHistory={doseHistory} setDoseHistory={setDoseHistory} showToast={showToast} setView={setView} interactions={interactions} interactionsLoading={interactionsLoading} user={user}/>}
            {view==="medications"  && <Medications meds={meds} setMeds={setMeds} showToast={showToast} epicSession={epicSession} setEpicSession={setEpicSession}/>}
            {view==="interactions" && <Interactions meds={meds} interactions={interactions} interactionsLoading={interactionsLoading}/>}
            {view==="side-effects"     && <Symptoms symptoms={symptoms} setSymptoms={setSymptoms} meds={meds} showToast={showToast} user={user}/>}
            {view==="refills"      && <Refills meds={meds} setMeds={setMeds} showToast={showToast}/>}
            {view==="caregivers"   && <Caregivers caregivers={caregivers} setCaregivers={setCaregivers} showToast={showToast} user={user}/>}
            {view==="ai"           && <AICheckin meds={meds} doseHistory={doseHistory} symptoms={symptoms} interactions={interactions} user={user}/>}
            {view==="settings"     && <SettingsScreen user={user} showToast={showToast} handleSignOut={handleSignOut} onPremium={setPremiumModal} meds={meds} setMeds={setMeds}/>}
          </main>
        </div>

        {/* Mobile bottom tab bar */}
        <nav className="bottom-nav" aria-label="Main navigation">
          {showMore && (
            <>
              <div className="tab-more-backdrop" onClick={()=>setShowMore(false)}/>
              <div className="tab-more-menu">
                {moreTabs.map(t=>(
                  <div key={t.id} className={`tab-more-item ${view===t.id?"active":""}`} onClick={()=>handleTabClick(t.id)}>
                    <span style={{fontSize:18}}>{t.icon}</span>
                    <span style={{flex:1}}>{t.label}</span>
                    {t.badge>0&&<span className="badge">{t.badge}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
          <div className="bottom-nav-inner">
            {primaryTabs.map(t=>(
              <button key={t.id} className={`tab-item ${view===t.id||(t.id==="__more__"&&showMore)?"active":""}`} onClick={()=>handleTabClick(t.id)} aria-label={t.label} aria-pressed={view===t.id}>
                {t.badge>0&&<span className="tab-badge">{t.badge}</span>}
                <span className="tab-item-icon">{t.icon}</span>
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </nav>

        {premiumModal&&<PremiumModal feature={premiumModal} onClose={()=>setPremiumModal(null)} user={user}/>}
        {toast&&<div className="toast">✦ {toast}</div>}
      </div>
    </>
  );
}
